import React, { useState, useEffect, useRef, useCallback } from "react";
import Menubar from "../Menubar/Menubar";
import { Outlet } from "react-router-dom";
import "../Dashboard/Dashboard.css";
import { Tooltip } from "antd";
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdFileDownloadDone,
  MdDelete,
  MdCancel,
} from "react-icons/md";
import { IoLogoFacebook, IoLogoWhatsapp } from "react-icons/io";
import { RiInstagramFill, RiWhatsappFill } from "react-icons/ri";
import { TbDeviceAnalytics, TbRefresh } from "react-icons/tb";
import { AiFillInstagram } from "react-icons/ai";
import {
  FaFacebookSquare,
  FaPhone,
  FaCalendarAlt,
  FaEdit,
  FaEye,
  FaInstagram,
} from "react-icons/fa";
import { IoMail, IoNotificationsOutline } from "react-icons/io5";
import { IoIosTime, IoIosMail } from "react-icons/io";
import { RxOpenInNewWindow } from "react-icons/rx";
import Loader from "../Loader/Loader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { FiDownload } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InstagramDemoChat from "./InstagramDemoChat";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://dash.zorbastays.com"}/web-backend`;

/* ═════════════════════════════════════════════════════════════════════════
   UI TOKENS + SHARED PRESENTATIONAL COMPONENTS
   ═════════════════════════════════════════════════════════════════════════ */
const ui = {
  card: "bg-white rounded-xl border border-gray-200 shadow-sm",
  th: "px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap",
  td: "px-4 py-3 align-middle",
  input:
    "w-full h-10 px-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#1D2826] focus:ring-1 focus:ring-[#1D2826]/20 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed",
  textarea:
    "w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg resize-none focus:outline-none focus:border-[#1D2826] focus:ring-1 focus:ring-[#1D2826]/20 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed",
  btn: "h-9 px-4 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5 whitespace-nowrap",
  btnPrimary: "bg-[#1D2826] text-white hover:bg-[#2d3d3a]",
  btnGhost: "bg-gray-100 text-gray-600 hover:bg-gray-200",
  btnDanger: "bg-red-500 text-white hover:bg-red-600",
  btnWhatsApp: "bg-[#25d366] text-white hover:bg-green-600",
  sectionTitle:
    "sm:col-span-2 text-xs font-bold uppercase tracking-wide text-gray-400",
};

/* Replaces the "relative top-2 ml-2 bg-white" floating-label hack */
function Field({ label, className = "", children }) {
  return (
    <div className={`flex min-w-0 flex-col gap-1.5 ${className}`}>
      <span className="text-xs font-medium text-gray-500">{label}</span>
      {children}
    </div>
  );
}

/* One modal shell for every dialog → same padding, radius, mobile width */
function ModalShell({ title, children, footer, maxWidth = "max-w-md" }) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div
        className={`flex max-h-[90vh] w-full ${maxWidth} flex-col rounded-2xl bg-white shadow-2xl`}
      >
        <div className="px-6 pt-5 pb-3 text-base font-bold text-gray-800">
          {title}
        </div>
        <div className="overflow-y-auto px-6 pb-5 text-sm text-gray-600">
          {children}
        </div>
        {footer && (
          <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div
      className={`${ui.card} flex items-center justify-between gap-4 px-4 py-3`}
    >
      <div>
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
      </div>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#3C4C4E] text-xl text-white">
        {icon}
      </div>
    </div>
  );
}

const ICON_TONES = {
  sky: "border-sky-300 hover:text-sky-500",
  yellow: "border-yellow-300 hover:text-yellow-600",
  purple: "border-purple-300 hover:text-purple-500",
  red: "border-red-300 hover:text-red-500",
  green: "border-green-300 hover:text-green-600",
};
function IconButton({ tip, tone = "sky", disabled, onClick, children }) {
  return (
    <Tooltip title={tip} placement="top">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-gray-400 transition-all hover:scale-110 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100 ${
          disabled ? "border-gray-200" : ICON_TONES[tone]
        }`}
      >
        {children}
      </button>
    </Tooltip>
  );
}

function Pagination({
  total,
  from,
  to,
  currentPage,
  setCurrentPage,
  totalPages,
  pages,
}) {
  const nav =
    "flex h-8 min-w-8 items-center justify-center px-2 text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent";
  return (
    <nav className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-4 py-2 md:flex-row">
      <span className="text-xs text-gray-500">
        Showing{" "}
        <b className="text-gray-700">
          {total ? from : 0}–{to}
        </b>{" "}
        of <b className="text-gray-700">{total}</b>
      </span>
      <ul className="flex items-center gap-1">
        <li>
          <button
            className={`${nav} rounded`}
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          >
            <MdKeyboardDoubleArrowLeft />
          </button>
        </li>
        {pages.map((p, i) =>
          p === "..." ? (
            <li key={i} className="px-1 text-gray-400">
              …
            </li>
          ) : (
            <li key={i}>
              <button
                onClick={() => setCurrentPage(p)}
                className={`${nav} rounded ${
                  currentPage === p
                    ? "bg-[#1D2826] text-white hover:bg-[#1D2826]"
                    : ""
                }`}
              >
                {p}
              </button>
            </li>
          ),
        )}
        <li>
          <button
            className={`${nav} rounded`}
            disabled={currentPage === totalPages || !totalPages}
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          >
            <MdKeyboardDoubleArrowRight />
          </button>
        </li>
      </ul>
    </nav>
  );
}

function PipelineTab({ stage, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`relative h-9 min-w-[96px] flex-1 shrink-0 whitespace-nowrap px-4 text-[11px] leading-tight transition-all ${stage.color} ${
        isActive ? "font-bold" : "font-medium opacity-70 hover:opacity-100"
      }`}
      style={{
        clipPath:
          "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)",
        marginRight: "-6px",
      }}
    >
      {stage.name}
      {isActive && (
        <span className="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-current" />
      )}
    </button>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   DOMAIN HELPERS (unchanged logic)
   ═════════════════════════════════════════════════════════════════════════ */
const calcNights = (checkin, checkout) => {
  if (!checkin || !checkout) return 0;
  const ci = new Date(checkin);
  const co = new Date(checkout);
  if (isNaN(ci) || isNaN(co)) return 0;
  return Math.max(0, Math.round((co - ci) / (1000 * 60 * 60 * 24)));
};

const emptyPropertyDetail = () => ({
  destination: "",
  guests: "",
  bedrooms: "",
  inclusions: "",
  checkin: "",
  checkout: "",
  checkin_time: "12:00 PM",
  checkout_time: "11:00 AM",
  checkinHour: "12",
  checkinMinute: "00",
  checkinAmPm: "PM",
  checkoutHour: "11",
  checkoutMinute: "00",
  checkoutAmPm: "AM",
  proprice: "",
  secdep: "",
  advpay: "",
  balpay: "",
  totpay: "",
});

const recalcPropertyPricing = (detail) => {
  const nights = calcNights(detail.checkin, detail.checkout);
  const proprice = parseFloat(detail.proprice) || 0;
  return { ...detail, totpay: (nights * proprice).toFixed(2) };
};

const getSelectedProperties = (formData) =>
  (formData.propertyname || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

// Whitespace/case-insensitive key so " Villa  Coco " and "Villa Coco" are the same property
const normKey = (s) =>
  String(s || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// How many meaningful fields a property-detail entry has (used to pick the "real" duplicate)
const countFilled = (d) =>
  [
    "destination",
    "checkin",
    "checkout",
    "proprice",
    "secdep",
    "advpay",
    "balpay",
    "totpay",
    "inclusions",
  ].filter((f) => d?.[f] !== "" && d?.[f] != null).length;

// Rebuilds propertydetails so it only contains the properties listed in `names`,
// one entry each (stale / whitespace-variant duplicates are dropped).
const reconcilePropertyDetails = (details, names) => {
  if (!names.length) return details;
  const out = {};
  names.forEach((w) => {
    const matches = Object.entries(details)
      .filter(([k]) => normKey(k) === normKey(w))
      .map(([, v]) => v);
    if (matches.length)
      out[w] = matches.reduce((best, cur) =>
        countFilled(cur) > countFilled(best) ? cur : best,
      );
  });
  return Object.keys(out).length ? out : details;
};

const STATUSES = [
  "New Enquiry",
  "Call Not Picked",
  "Contacted",
  "Qualified",
  "Plan & Quote Sent",
  // "In Pipeline",
  "Negotiating",
  "Awaiting Payment",
  "Lost & Closed",
];
const STATUS_COLORS = {
  "New Enquiry": "#6366f1",
  "Call Not Picked": "#f59e0b",
  Contacted: "#3b82f6",
  Qualified: "#8b5cf6",
  "Plan & Quote Sent": "#06b6d4",
  // "In Pipeline": "#10b981",
  Negotiating: "#f97316",
  "Awaiting Payment": "#ec4899",
  Booked: "#22c55e",
  "Lost & Closed": "#ef4444",
};
const PIPELINE_STAGES = [
  {
    name: "Incoming",
    color: "bg-emerald-100 text-emerald-600",
    status: "Incoming Leads",
  },
  {
    name: "Processed",
    color: "bg-yellow-100 text-yellow-600",
    status: "All Processed Leads",
  },
  {
    name: "New Enquiry",
    color: "bg-purple-100 text-purple-500",
    status: "New Enquiry",
  },
  {
    name: "Call Not Picked",
    color: "bg-sky-100 text-sky-600",
    status: "Call Not Picked",
  },
  {
    name: "Contacted",
    color: "bg-pink-100 text-pink-600",
    status: "Contacted",
  },
  {
    name: "Qualified",
    color: "bg-teal-100 text-teal-700",
    status: "Qualified",
  },
  {
    name: "Plan/Quote Sent",
    color: "bg-red-100 text-red-500",
    status: "Plan & Quote Sent",
  },
  // {
  //   name: "In Pipeline",
  //   color: "bg-blue-100 text-blue-600",
  //   status: "In Pipeline",
  // },
  {
    name: "Negotiating",
    color: "bg-lime-100 text-lime-700",
    status: "Negotiating",
  },
  {
    name: "Awaiting Payment",
    color: "bg-green-100 text-green-700",
    status: "Awaiting Payment",
  },
  {
    name: "Lost & Closed",
    color: "bg-slate-200 text-slate-700",
    status: "Lost & Closed",
  },
];
const WEBSITE_STATUSES = [
  "New Lead",
  "Contacted",
  "Qualified",
  "Plan & Quote Sent",
  // "In Pipeline",
  "Negotiating",
  "Awaiting Payment",
  "Lost & Closed",
];
const WEBSITE_STATUS_COLORS = {
  "New Lead": "#6366f1",
  Contacted: "#3b82f6",
  Qualified: "#8b5cf6",
  "Plan & Quote Sent": "#06b6d4",
  // "In Pipeline": "#10b981",
  Negotiating: "#f97316",
  "Awaiting Payment": "#ec4899",
  "Lost & Closed": "#ef4444",
};
const WEBSITE_PIPELINE_STAGES = [
  { name: "All", color: "bg-orange-100 text-orange-600", status: "All Leads" },
  {
    name: "Incoming",
    color: "bg-emerald-100 text-emerald-600",
    status: "Incoming Leads",
  },
  {
    name: "Processed",
    color: "bg-yellow-100 text-yellow-600",
    status: "All Processed Leads",
  },
  {
    name: "New Lead",
    color: "bg-purple-100 text-purple-500",
    status: "New Lead",
  },
  {
    name: "Contacted",
    color: "bg-pink-100 text-pink-600",
    status: "Contacted",
  },
  {
    name: "Qualified",
    color: "bg-teal-100 text-teal-700",
    status: "Qualified",
  },
  {
    name: "Plan/Quote Sent",
    color: "bg-red-100 text-red-500",
    status: "Plan & Quote Sent",
  },
  // {
  //   name: "In Pipeline",
  //   color: "bg-blue-100 text-blue-600",
  //   status: "In Pipeline",
  // },
  {
    name: "Negotiating",
    color: "bg-lime-100 text-lime-700",
    status: "Negotiating",
  },
  {
    name: "Awaiting Payment",
    color: "bg-green-100 text-green-700",
    status: "Awaiting Payment",
  },
  {
    name: "Lost & Closed",
    color: "bg-slate-200 text-slate-700",
    status: "Lost & Closed",
  },
];
const CHANNELS = {
  "Instagram DM": {
    color: "#e1306c",
    bg: "#fce4ec",
    icon: <AiFillInstagram />,
  },
  "Facebook Lead": {
    color: "#1877f2",
    bg: "#e3f2fd",
    icon: <FaFacebookSquare />,
  },
  WhatsApp: { color: "#25d366", bg: "#e8f5e9", icon: <RiWhatsappFill /> },
};
const TAG_COLORS = { Hot: "#ef4444", Warm: "#f97316", Cold: "#64748b" };

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
function channelToSource(channel) {
  return (
    {
      "Facebook Lead": "Facebook",
      "Instagram DM": "Instagram",
      WhatsApp: "WhatsApp",
    }[channel] ||
    channel ||
    "Lead"
  );
}

// ─── WhatsApp follow-up helpers ───────────────────────────────────────────────
// Digits only; strips leading 0s / 00; 10-digit numbers are assumed Indian (+91)
const normalizePhone = (raw) => {
  let d = String(raw || "")
    .replace(/\D/g, "")
    .replace(/^0+/, "");
  if (d.length === 10) d = "91" + d;
  return d;
};

const isValidPhone = (raw) => {
  const d = normalizePhone(raw);
  // Indian: 91 + 10 digits, national number must start with 6-9
  if (d.startsWith("91")) return d.length === 12 && /^[6-9]/.test(d[2]);
  // Other countries: E.164 allows up to 15 digits
  return d.length >= 11 && d.length <= 15;
};
const fmtWaDate = (val) => {
  if (!val) return "";
  let d;
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [y, m, day] = val.split("-").map(Number);
    d = new Date(y, m - 1, day);
  } else d = new Date(val);
  return isNaN(d) ? "" : format(d, "dd MMM yyyy");
};

const fmtINR = (v) => `₹${(parseFloat(v) || 0).toLocaleString("en-IN")}`;

const buildWhatsAppMessage = (
  customerName,
  details,
  propertiesList,
  brochureMap,
) => {
  const names = Object.keys(details);
  const first =
    String(customerName || "")
      .trim()
      .split(/\s+/)[0] || "there";

  const optionBlocks = names.map((name, i) => {
    const d = details[name] || {};
    const meta = (propertiesList || []).find(
      (p) => p.propertyname?.trim().normalize() === name.trim().normalize(),
    );

    if (!meta) {
      console.warn(
        `[WhatsApp] No property match found in properties list for "${name}"`,
      );
    }

    const nights = calcNights(d.checkin, d.checkout);
    const ci = fmtWaDate(d.checkin) || "—";
    const co = fmtWaDate(d.checkout) || "—";
    const maxGuests = meta?.maxadult ?? d.guests ?? "—";
    const pdfLink = brochureMap?.[name] || "";

    const lines = [
      `*OPTION ${i + 1} — ${name}*`,
      `📍 ${d.destination || "—"} · ${d.bedrooms || "—"} Bedrooms · Up to ${maxGuests} Guests`,
      ``,
      `*${ci} – ${co}* · ${nights} Night${nights === 1 ? "" : "s"}`,
      `*${fmtINR(d.proprice)} / night*`,
      `*Total Stay: ${fmtINR(d.totpay)}*`,
      ``,
      `Advance: ${fmtINR(d.advpay)}`,
      `Balance: ${fmtINR(d.balpay)}`,
      `Security Deposit: ${fmtINR(d.secdep)}`,
      ``,
      `✨ *Inclusions:* ${d.inclusions || "—"}`,
    ];

    if (pdfLink) {
      lines.push(``, `📄 *View Property Brochure →*`, pdfLink);
    } else {
      console.warn(`[WhatsApp] No brochure URL resolved for "${name}"`);
    }

    return lines.join("\n");
  });

  return [
    `Hello ${first}, 👋`,
    ``,
    `Greetings from *Zorba Stays*!`,
    ``,
    `Based on your requirements, we've shortlisted these stays for you:`,
    ``,
    optionBlocks.join("\n\n──────────────\n\n"),
    ``,
    `Please have a look at the options and brochures, and let us know which stay you'd like to explore further. We're happy to assist with availability, booking and any questions you may have. 😊`,
    ``,
    `Warm regards,`,
    `*Team Zorba Stays*`,
  ].join("\n");
};

// Fetches propertybrochure for each property from /admin/properties/:id,
// since the trimmed /properties list doesn't carry that field.
const fetchPropertyBrochures = async (names, propertiesList) => {
  const brochureMap = {};
  await Promise.all(
    names.map(async (name) => {
      const meta = (propertiesList || []).find(
        (p) => p.propertyname?.trim().normalize() === name.trim().normalize(),
      );
      if (!meta) return;

      if (meta.propertybrochure) {
        brochureMap[name] = meta.propertybrochure;
        return;
      }
      if (!meta.id) return;

      try {
        const res = await fetch(`${API}/admin/properties/${meta.id}`);
        if (!res.ok) return;
        const full = await res.json();
        if (full.propertybrochure) brochureMap[name] = full.propertybrochure;
      } catch (e) {
        console.warn(
          `[WhatsApp] Failed to fetch brochure for "${name}":`,
          e.message,
        );
      }
    }),
  );
  return brochureMap;
};

/* ═════════════════════════════════════════════════════════════════════════
   MODALS
   ═════════════════════════════════════════════════════════════════════════ */
function ConfirmProcessModal({ onConfirm, onCancel }) {
  return (
    <ModalShell
      title="Process lead?"
      footer={
        <>
          <button onClick={onCancel} className={`${ui.btn} ${ui.btnGhost}`}>
            Cancel
          </button>
          <button onClick={onConfirm} className={`${ui.btn} ${ui.btnPrimary}`}>
            Yes, process
          </button>
        </>
      }
    >
      This will save the lead to the database. Do you want to continue?
    </ModalShell>
  );
}

function SendLeadMailModal({ lead, onConfirm, onCancel, sending }) {
  const leadName =
    `${lead.firstname || lead.name || ""} ${lead.lastname || ""}`.trim() ||
    lead.customer_name ||
    "this lead";
  return (
    <ModalShell
      title="Send pre-booking mail?"
      footer={
        <>
          <button
            onClick={onCancel}
            disabled={sending}
            className={`${ui.btn} ${ui.btnGhost}`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={sending}
            className={`${ui.btn} ${ui.btnPrimary}`}
          >
            {sending ? (
              <>
                <svg
                  className="h-4 w-4 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                Sending…
              </>
            ) : (
              "Yes, send"
            )}
          </button>
        </>
      }
    >
      Do you want to send the "Pre Booking" mail to{" "}
      <b className="text-gray-800">{leadName}</b>?
    </ModalShell>
  );
}

function DeleteLeadModal({ leadName, onConfirm, onCancel, deleting }) {
  return (
    <ModalShell
      title="Delete lead?"
      footer={
        <>
          <button
            onClick={onCancel}
            disabled={deleting}
            className={`${ui.btn} ${ui.btnGhost}`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className={`${ui.btn} ${ui.btnDanger}`}
          >
            {deleting ? "Deleting…" : "Yes, delete"}
          </button>
        </>
      }
    >
      <p>
        Are you sure you want to delete{" "}
        <b className="text-gray-800">{leadName}</b>?
      </p>
      <p className="mt-1 text-xs text-red-500">This action cannot be undone.</p>
    </ModalShell>
  );
}

function WhatsAppPhoneModal({ leadName, onSubmit, onCancel }) {
  const [phone, setPhone] = useState("");
  const [err, setErr] = useState("");

  const submit = () => {
    if (!isValidPhone(phone)) {
      setErr("Enter a valid phone number (with country code if not Indian)");
      return;
    }
    onSubmit(phone.trim());
  };

  return (
    <ModalShell
      title="Phone number missing"
      footer={
        <>
          <button onClick={onCancel} className={`${ui.btn} ${ui.btnGhost}`}>
            Cancel
          </button>
          <button onClick={submit} className={`${ui.btn} ${ui.btnWhatsApp}`}>
            Save & send
          </button>
        </>
      }
    >
      <p className="mb-3">
        Enter the number for <b className="text-gray-800">{leadName}</b> to send
        the property details on WhatsApp.
      </p>
      <input
        autoFocus
        type="tel"
        value={phone}
        onChange={(e) => {
          setPhone(e.target.value);
          setErr("");
        }}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder="e.g. 9876543210"
        className={ui.input}
      />
      {err && <p className="mt-1.5 text-xs text-red-500">{err}</p>}
    </ModalShell>
  );
}

// ─── Process buttons ──────────────────────────────────────────────────────────
const processBtnCls = (processing) =>
  `h-8 px-3 rounded-md text-white text-xs font-bold whitespace-nowrap transition-all ${
    processing
      ? "bg-gray-400 cursor-not-allowed"
      : "bg-[#aeb3a4] hover:bg-[#797d72] cursor-pointer"
  }`;

function ProcessMetaLeadButton({ lead, onProcessed }) {
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleProcess = async () => {
    setProcessing(true);
    try {
      const meta_lead_id = lead.id.startsWith("meta_")
        ? lead.id.replace("meta_", "")
        : lead.id;
      const res = await fetch(`${API}/leads/process-meta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          meta_lead_id,
          name: lead.name,
          phone: lead.phone || "",
          email: lead.email || "",
          channel: lead.channel || "Facebook Lead",
          enquiry: lead.enquiry || "",
          form_id: lead.form_id || "",
          page_id: lead.page_id || "",
          field_data: lead.field_data || [],
          raw_payload: lead,
        }),
      });
      if (!res.ok) throw new Error("Failed " + res.status);
      const { lead: savedLead, already_existed } = await res.json();
      setDone(true);
      onProcessed(savedLead, already_existed);
    } catch (e) {
      alert("Error processing lead: " + e.message);
    } finally {
      setProcessing(false);
    }
  };

  if (done)
    return (
      <span className="text-xs font-bold text-green-600">✓ Saved to DB</span>
    );

  return (
    <>
      {showConfirm && (
        <ConfirmProcessModal
          onConfirm={() => {
            setShowConfirm(false);
            handleProcess();
          }}
          onCancel={() => setShowConfirm(false)}
        />
      )}
      <button
        onClick={() => setShowConfirm(true)}
        disabled={processing}
        className={processBtnCls(processing)}
      >
        {processing ? "Saving…" : "Process lead"}
      </button>
    </>
  );
}

function ProcessWebsiteLeadButton({ lead, onProcessed }) {
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleProcess = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API}/website-leads/process`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ leadid: lead.leadid }),
      });
      if (!res.ok) throw new Error("Failed " + res.status);
      const { lead: savedLead, already_existed } = await res.json();
      setDone(true);
      onProcessed(savedLead, already_existed);
    } catch (e) {
      alert("Error processing lead: " + e.message);
    } finally {
      setProcessing(false);
    }
  };

  if (done)
    return (
      <span className="text-xs font-bold text-green-600">✓ Saved to DB</span>
    );

  return (
    <>
      {showConfirm && (
        <ConfirmProcessModal
          onConfirm={() => {
            setShowConfirm(false);
            handleProcess();
          }}
          onCancel={() => setShowConfirm(false)}
        />
      )}
      <button
        onClick={() => setShowConfirm(true)}
        disabled={processing}
        className={processBtnCls(processing)}
      >
        {processing ? "Saving…" : "Process lead"}
      </button>
    </>
  );
}

// ─── Follow Up Modal ──────────────────────────────────────────────────────────
function FollowUpModal({ lead, onClose }) {
  const [type, setType] = useState("whatsapp");
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const send = () => {
    setSent(true);
    setTimeout(onClose, 1600);
  };
  if (sent)
    return (
      <ModalShell title="">
        <div className="py-4 text-center">
          <div className="text-5xl">✅</div>
          <div className="mt-3 text-base font-bold text-gray-800">
            Follow-up sent!
          </div>
        </div>
      </ModalShell>
    );
  return (
    <ModalShell
      title="Send follow-up"
      footer={
        <>
          <button onClick={onClose} className={`${ui.btn} ${ui.btnGhost}`}>
            Cancel
          </button>
          <button
            onClick={send}
            disabled={!msg}
            className={`${ui.btn} ${ui.btnWhatsApp}`}
          >
            Send
          </button>
        </>
      }
    >
      <p className="mb-4 text-xs text-gray-400">
        To: <b className="text-gray-700">{lead.name || lead.customername}</b>
        {lead.phone ? ` · ${lead.phone}` : ""}
      </p>
      <div className="mb-4 grid grid-cols-2 gap-2">
        {["whatsapp", "email"].map((t) => (
          <button
            key={t}
            onClick={() => setType(t)}
            className={`h-10 rounded-lg border-2 text-sm font-semibold transition-all ${
              type === t
                ? "border-indigo-500 bg-indigo-50 text-indigo-600"
                : "border-gray-200 bg-white text-gray-500"
            }`}
          >
            {t === "whatsapp" ? "📱 WhatsApp" : "✉️ Email"}
          </button>
        ))}
      </div>
      <textarea
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        rows={4}
        placeholder={`Write your ${type} message…`}
        className={ui.textarea}
      />
    </ModalShell>
  );
}

// ─── Create Lead Modal ────────────────────────────────────────────────────────
function CreateLeadModal({ onClose, onCreated, teamMembers = [] }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    channel: "Instagram DM",
    enquiry: "",
    tag: "Warm",
    assignee: "Unassigned",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setErr("Name is required");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Server error " + res.status);
      onCreated(await res.json());
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <ModalShell
      title="➕ Add new lead"
      maxWidth="max-w-lg"
      footer={
        <>
          <button onClick={onClose} className={`${ui.btn} ${ui.btnGhost}`}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className={`${ui.btn} ${ui.btnPrimary}`}
          >
            {saving ? "Saving…" : "Create lead"}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Full name *">
          <input
            className={ui.input}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Phone number">
            <input
              className={ui.input}
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
            />
          </Field>
          <Field label="Email">
            <input
              className={ui.input}
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </Field>
        </div>
        <Field label="Channel">
          <select
            className={ui.input}
            value={form.channel}
            onChange={(e) => set("channel", e.target.value)}
          >
            {Object.keys(CHANNELS).map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Enquiry / message">
          <textarea
            className={ui.textarea}
            rows={3}
            value={form.enquiry}
            onChange={(e) => set("enquiry", e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Tag">
            <select
              className={ui.input}
              value={form.tag}
              onChange={(e) => set("tag", e.target.value)}
            >
              {["Hot", "Warm", "Cold"].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </Field>
          <Field label="Assigned to">
            <select
              className={ui.input}
              value={form.assignee}
              onChange={(e) => set("assignee", e.target.value)}
            >
              {(teamMembers.length ? teamMembers : ["Unassigned"]).map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </Field>
        </div>
        {err && <div className="text-xs text-red-500">⚠ {err}</div>}
      </div>
    </ModalShell>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   LEAD SIDE PANEL
   ═════════════════════════════════════════════════════════════════════════ */
function TimeSelects({ prefix, detail, onChange, disabled }) {
  const cls = `${ui.input} px-2`;
  return (
    <div className="grid grid-cols-3 gap-2">
      <select
        name={`${prefix}Hour`}
        value={detail[`${prefix}Hour`]}
        onChange={onChange}
        disabled={disabled}
        className={cls}
      >
        {Array.from({ length: 12 }, (_, i) =>
          String(i + 1).padStart(2, "0"),
        ).map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <select
        name={`${prefix}Minute`}
        value={detail[`${prefix}Minute`]}
        onChange={onChange}
        disabled={disabled}
        className={cls}
      >
        {["00", "15", "30", "45"].map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <select
        name={`${prefix}AmPm`}
        value={detail[`${prefix}AmPm`]}
        onChange={onChange}
        disabled={disabled}
        className={cls}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}

function LeadSidePanel({
  isOpen,
  mode,
  formData,
  setFormData,
  onClose,
  onSave,
  onSwitchToEdit,
  properties,
  editingLead,
  teamMembers = [],
}) {
  const isViewOnly = mode === "view";
  const [showInstaChat, setShowInstaChat] = useState(false);
  // Simple text/select fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "country") {
        updated.state = "";
        updated.city = "";
      } else if (name === "state") {
        updated.city = "";
      }
      return updated;
    });
  };

  // ── Property multi-select state ────────────────────────────────────
  const [isPropOpen, setIsPropOpen] = useState(false);
  const [propSearch, setPropSearch] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [manualProperty, setManualProperty] = useState("");
  const [worldData, setWorldData] = useState([]);
  const dropdownRef = useRef(null);

  // { [propertyName]: { db: [...], airbnb: [...] } }
  const [blockedDatesMap, setBlockedDatesMap] = useState({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsPropOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetch("/world.json")
      .then((res) => res.json())
      .then((data) => setWorldData(data))
      .catch((err) => console.error("Error loading world.json:", err));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setIsPropOpen(false);
      setManualMode(false);
      setPropSearch("");
      setManualProperty("");
    }
  }, [isOpen]);

  // Find selected country/state/city objects
  const selectedCountry = worldData.find((c) => c.name === formData.country);
  const states = selectedCountry?.states || [];
  const selectedState = states.find((s) => s.name === formData.state);
  const cities = selectedState?.cities || [];

  const selectedProperties = getSelectedProperties(formData);

  // Fetch blocked dates for any newly-selected property we haven't fetched yet
  useEffect(() => {
    selectedProperties.forEach((prop) => {
      if (blockedDatesMap[prop] !== undefined) return;
      fetch(`${API}/blocked-dates/${encodeURIComponent(prop)}`)
        .then((res) => res.json())
        .then((data) => {
          setBlockedDatesMap((prev) => ({
            ...prev,
            [prop]: {
              db: data.success ? data.dbblockeddates || [] : [],
              airbnb: data.success ? data.airbnbblockeddates || [] : [],
            },
          }));
        })
        .catch(() => {
          setBlockedDatesMap((prev) => ({
            ...prev,
            [prop]: { db: [], airbnb: [] },
          }));
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.propertyname]);

  const sortedProperties = [...(properties || [])].sort((a, b) =>
    a.propertyname.localeCompare(b.propertyname),
  );
  const filteredProperties = sortedProperties.filter((p) =>
    p.propertyname.toLowerCase().includes(propSearch.toLowerCase()),
  );

  const toggleProperty = (propertyname, matched) => {
    setFormData((prev) => {
      const current = getSelectedProperties(prev);
      const details = { ...(prev.propertyDetails || {}) };
      let next;

      if (current.includes(propertyname)) {
        next = current.filter((p) => p !== propertyname);
        delete details[propertyname];
      } else {
        next = [...current, propertyname];
        details[propertyname] = {
          ...emptyPropertyDetail(),
          bedrooms: matched?.maxbed ?? "",
          guests: matched?.maxadult ?? "",
        };
      }

      return {
        ...prev,
        propertyname: next.join(", "),
        propertyDetails: details,
      };
    });
  };

  const removeProperty = (propertyname) => toggleProperty(propertyname, null);

  const handleManualAdd = () => {
    if (!manualProperty.trim()) return;
    toggleProperty(manualProperty.trim(), null);
    setManualMode(false);
    setManualProperty("");
  };

  // ── Per-property field updates ─────────────────────────────────────
  const updatePropertyDetail = (propName, patch) => {
    setFormData((prev) => ({
      ...prev,
      propertyDetails: {
        ...prev.propertyDetails,
        [propName]: recalcPropertyPricing({
          ...prev.propertyDetails[propName],
          ...patch,
        }),
      },
    }));
  };

  const handlePropertyText = (propName, field) => (e) =>
    updatePropertyDetail(propName, { [field]: e.target.value });

  const handlePropertyNumeric = (propName, field) => (e) => {
    const v = e.target.value;
    if (v === "" || /^[0-9]+$/.test(v))
      updatePropertyDetail(propName, { [field]: v });
  };

  const handlePropertyPrice = (propName, field) => (e) => {
    const v = e.target.value;
    if (/^\d*\.?\d*$/.test(v)) updatePropertyDetail(propName, { [field]: v });
  };

  const handlePropertyDate = (propName, which) => (date) => {
    const val = date ? format(date, "yyyy-MM-dd") : "";
    if (which === "checkin") {
      updatePropertyDetail(propName, { checkin: val, checkout: "" });
    } else {
      updatePropertyDetail(propName, { checkout: val });
    }
  };

  const handlePropertyTime = (propName, isCheckin) => (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const detail = { ...prev.propertyDetails[propName], [name]: value };
      const hourKey = isCheckin ? "checkinHour" : "checkoutHour";
      const minuteKey = isCheckin ? "checkinMinute" : "checkoutMinute";
      const ampmKey = isCheckin ? "checkinAmPm" : "checkoutAmPm";
      const targetKey = isCheckin ? "checkin_time" : "checkout_time";
      detail[targetKey] =
        `${detail[hourKey]}:${detail[minuteKey]} ${detail[ampmKey]}`;
      return {
        ...prev,
        propertyDetails: { ...prev.propertyDetails, [propName]: detail },
      };
    });
  };

  const parseDateValue = (val) => {
    if (!val) return null;
    if (val instanceof Date) return val;
    if (typeof val === "string" && val.includes("T")) return new Date(val);
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [y, m, d] = val.split("-").map(Number);
      return new Date(y, m - 1, d);
    }
    return new Date(val);
  };

  const isDateBlockedFor = (propName) => (date) => {
    const str = format(date, "yyyy-MM-dd");
    const b = blockedDatesMap[propName];
    return !!b && (b.db.includes(str) || b.airbnb.includes(str));
  };
  const getDayClassFor = (propName) => (date) => {
    const str = format(date, "yyyy-MM-dd");
    const b = blockedDatesMap[propName];
    if (!b) return undefined;
    if (b.airbnb.includes(str)) return "airbnb-blocked-day";
    if (b.db.includes(str)) return "zorba-blocked-day";
    return undefined;
  };
  const hasBlockedInRangeFor = (propName, checkin) => (checkoutDate) => {
    if (!checkin) return false;
    const b = blockedDatesMap[propName];
    if (!b) return false;
    const start = new Date(checkin);
    start.setDate(start.getDate() + 1);
    let current = new Date(start);
    const end = new Date(checkoutDate);
    while (current < end) {
      const str = format(current, "yyyy-MM-dd");
      if (b.db.includes(str) || b.airbnb.includes(str)) return true;
      current.setDate(current.getDate() + 1);
    }
    return false;
  };

  const text = (name, extra = {}) => ({
    name,
    value: formData[name] || "",
    onChange: handleChange,
    readOnly: isViewOnly,
    disabled: isViewOnly,
    className: ui.input,
    ...extra,
  });

  return (
    <>
      {/* Raises the datepicker popper above the panel */}
      <style>{`.react-datepicker-popper{z-index:80 !important}`}</style>

      {isOpen && (
        <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      )}

      <aside
        className={`fixed right-0 top-0 z-50 flex h-full w-full flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out sm:w-[560px] md:w-[680px] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <header className="flex shrink-0 items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-[#1D2826]">
            {mode === "edit" ? "Edit lead" : "View lead"}
          </h2>
          <div className="flex items-center gap-3">
            {mode === "view" && (
              <button
                onClick={onSwitchToEdit}
                className={`${ui.btn} h-8 bg-green-900 px-3 text-xs text-white hover:opacity-90`}
              >
                <FaEdit /> Edit lead
              </button>
            )}
            {mode === "view" && (
              <button
                onClick={() => setShowInstaChat(true)}
                className={`${ui.btn} h-8 bg-gradient-to-r from-[#3897f0] to-[#833ab4] px-3 text-xs text-white hover:opacity-90`}
              >
                <FaInstagram /> Instagram
              </button>
            )}
            <button
              onClick={onClose}
              className="text-xl text-gray-500 hover:text-black"
              aria-label="Close"
            >
              <MdCancel />
            </button>
          </div>
        </header>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* ── Customer ─────────────────────────────── */}
            <h3 className={ui.sectionTitle}>Customer</h3>
            <Field label="First name">
              <input {...text("firstname")} />
            </Field>
            <Field label="Last name">
              <input {...text("lastname")} />
            </Field>
            <Field label="Phone">
              <input {...text("phone")} />
            </Field>
            <Field label="Email">
              <input {...text("email", { type: "email" })} />
            </Field>
            <Field label="Country">
              <select
                name="country"
                value={formData.country || ""}
                onChange={handleChange}
                disabled={isViewOnly}
                className={ui.input}
              >
                <option value="">Select your country</option>
                {worldData.map((country) => (
                  <option key={country.id} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="State">
              <select
                name="state"
                value={formData.state || ""}
                onChange={handleChange}
                disabled={isViewOnly || !formData.country}
                className={ui.input}
              >
                <option value="">Select your state</option>
                {states.map((state) => (
                  <option key={state.id} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="City">
              <select
                name="city"
                value={formData.city || ""}
                onChange={handleChange}
                disabled={isViewOnly || !formData.state}
                className={ui.input}
              >
                <option value="">Select your city</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
            </Field>

            {/* ── Lead ─────────────────────────────────── */}
            <h3 className={`${ui.sectionTitle} mt-2`}>Lead</h3>
            <Field label="Channel">
              <select
                name="channel"
                value={formData.channel || ""}
                onChange={handleChange}
                disabled={isViewOnly}
                className={ui.input}
              >
                {["Instagram DM", "Facebook Lead", "WhatsApp"].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                name="status"
                value={formData.status || ""}
                onChange={handleChange}
                disabled={isViewOnly}
                className={ui.input}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Assigned to">
              <select
                name="assignee"
                value={formData.assignee || "Unassigned"}
                onChange={handleChange}
                disabled={isViewOnly}
                className={ui.input}
              >
                {(teamMembers.length ? teamMembers : ["Unassigned"]).map(
                  (t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ),
                )}
              </select>
            </Field>
            <Field label="Tag">
              <select
                name="tag"
                value={formData.tag || "Warm"}
                onChange={handleChange}
                disabled={isViewOnly}
                className={ui.input}
              >
                {["Hot", "Warm", "Cold"].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>

            {/* ── Properties ───────────────────────────── */}
            <h3 className={`${ui.sectionTitle} mt-2`}>Properties</h3>
            <div
              className="relative flex flex-col gap-1.5 sm:col-span-2"
              ref={dropdownRef}
            >
              <span className="text-xs font-medium text-gray-500">
                Selected properties
              </span>

              <div
                onClick={() => !isViewOnly && setIsPropOpen((p) => !p)}
                className={`flex min-h-[40px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-gray-300 p-1.5 text-sm ${
                  isViewOnly
                    ? "cursor-not-allowed bg-gray-50"
                    : "cursor-pointer bg-white"
                }`}
              >
                {selectedProperties.length === 0 && (
                  <span className="px-1.5 text-gray-400">
                    Select one or more properties
                  </span>
                )}
                {selectedProperties.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#DAE0CE] px-2.5 py-1 text-xs font-semibold text-[#1D2826]"
                  >
                    {p}
                    {!isViewOnly && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          removeProperty(p);
                        }}
                        className="cursor-pointer text-[#1D2826]/60 hover:text-red-500"
                      >
                        ✕
                      </span>
                    )}
                  </span>
                ))}
              </div>

              {isPropOpen && !isViewOnly && (
                <div className="absolute left-0 top-full z-20 mt-1 max-h-80 w-full overflow-y-auto rounded-lg border bg-white shadow-lg">
                  <div className="flex items-center justify-between border-b bg-gray-50 px-3 py-2">
                    <span className="text-sm font-semibold text-gray-600">
                      Select properties
                    </span>
                    <button
                      type="button"
                      onClick={() => setManualMode((p) => !p)}
                      className="text-xs font-medium text-blue-600 hover:underline"
                    >
                      {manualMode ? "← Back to list" : "+ Add manually"}
                    </button>
                  </div>

                  <div className="border-b p-2">
                    {manualMode ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Enter property name"
                          className={`${ui.input} h-9 flex-1`}
                          value={manualProperty}
                          onChange={(e) => setManualProperty(e.target.value)}
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleManualAdd()
                          }
                        />
                        <button
                          type="button"
                          disabled={!manualProperty.trim()}
                          onClick={handleManualAdd}
                          className={`${ui.btn} h-9 ${ui.btnPrimary}`}
                        >
                          + Add
                        </button>
                      </div>
                    ) : (
                      <input
                        type="text"
                        placeholder="Search a property"
                        className={`${ui.input} h-9`}
                        value={propSearch}
                        onChange={(e) => setPropSearch(e.target.value)}
                      />
                    )}
                  </div>

                  {!manualMode && (
                    <ul className="max-h-56 overflow-y-auto">
                      {filteredProperties.length > 0 ? (
                        filteredProperties.map((p) => {
                          const checked = selectedProperties.includes(
                            p.propertyname,
                          );
                          return (
                            <li
                              key={p.id}
                              onClick={() => toggleProperty(p.propertyname, p)}
                              className="flex cursor-pointer items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50"
                            >
                              <input
                                type="checkbox"
                                readOnly
                                checked={checked}
                                className="pointer-events-none"
                              />
                              {p.propertyname}
                            </li>
                          );
                        })
                      ) : (
                        <li className="px-4 py-3 text-sm text-gray-400">
                          No results found
                        </li>
                      )}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* ── One card per selected property ───────── */}
            {selectedProperties.map((propName) => {
              const detail =
                formData.propertyDetails?.[propName] || emptyPropertyDetail();
              const nights = calcNights(detail.checkin, detail.checkout);

              return (
                <div
                  key={propName}
                  className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 sm:col-span-2"
                >
                  <h4 className="mb-4 text-sm font-bold text-[#1D2826]">
                    🏡 {propName}
                  </h4>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Field label="Destination">
                      <input
                        value={detail.destination}
                        onChange={handlePropertyText(propName, "destination")}
                        readOnly={isViewOnly}
                        disabled={isViewOnly}
                        className={ui.input}
                      />
                    </Field>
                    <Field label="Number of guests">
                      <input
                        inputMode="numeric"
                        value={detail.guests}
                        onChange={handlePropertyNumeric(propName, "guests")}
                        readOnly={isViewOnly}
                        disabled={isViewOnly}
                        className={ui.input}
                      />
                    </Field>

                    <Field label="Check-in date">
                      <DatePicker
                        wrapperClassName="w-full"
                        selected={parseDateValue(detail.checkin)}
                        onChange={handlePropertyDate(propName, "checkin")}
                        dateFormat="dd-MM-yyyy"
                        minDate={new Date()}
                        filterDate={(d) => !isDateBlockedFor(propName)(d)}
                        dayClassName={getDayClassFor(propName)}
                        disabled={isViewOnly}
                        className={ui.input}
                      />
                      <TimeSelects
                        prefix="checkin"
                        detail={detail}
                        onChange={handlePropertyTime(propName, true)}
                        disabled={isViewOnly}
                      />
                    </Field>

                    <Field label="Check-out date">
                      <DatePicker
                        wrapperClassName="w-full"
                        selected={parseDateValue(detail.checkout)}
                        onChange={handlePropertyDate(propName, "checkout")}
                        dateFormat="dd-MM-yyyy"
                        filterDate={(d) =>
                          !hasBlockedInRangeFor(propName, detail.checkin)(d)
                        }
                        disabled={!detail.checkin || isViewOnly}
                        minDate={
                          detail.checkin
                            ? (() => {
                                const d = parseDateValue(detail.checkin);
                                d.setDate(d.getDate() + 1);
                                return d;
                              })()
                            : null
                        }
                        placeholderText={
                          !detail.checkin
                            ? "Select check-in first"
                            : "dd-mm-yyyy"
                        }
                        className={ui.input}
                      />
                      <TimeSelects
                        prefix="checkout"
                        detail={detail}
                        onChange={handlePropertyTime(propName, false)}
                        disabled={isViewOnly}
                      />
                    </Field>

                    <Field label="Bedrooms">
                      <input
                        inputMode="numeric"
                        value={detail.bedrooms}
                        onChange={handlePropertyNumeric(propName, "bedrooms")}
                        readOnly={isViewOnly}
                        disabled={isViewOnly}
                        className={ui.input}
                      />
                    </Field>
                    <Field label="Property price (per night)">
                      <input
                        value={detail.proprice}
                        onChange={handlePropertyPrice(propName, "proprice")}
                        readOnly={isViewOnly}
                        disabled={isViewOnly}
                        className={ui.input}
                      />
                    </Field>
                    <Field label="Number of nights">
                      <input
                        value={nights}
                        readOnly
                        disabled
                        className={ui.input}
                      />
                    </Field>
                    <Field label="Security deposit">
                      <input
                        value={detail.secdep}
                        onChange={handlePropertyPrice(propName, "secdep")}
                        readOnly={isViewOnly}
                        disabled={isViewOnly}
                        className={ui.input}
                      />
                    </Field>
                    <Field label="Advance payment">
                      <input
                        value={detail.advpay}
                        onChange={handlePropertyPrice(propName, "advpay")}
                        readOnly={isViewOnly}
                        disabled={isViewOnly}
                        className={ui.input}
                      />
                    </Field>
                    <Field label="Balance payment">
                      <input
                        value={detail.balpay}
                        onChange={handlePropertyPrice(propName, "balpay")}
                        readOnly={isViewOnly}
                        disabled={isViewOnly}
                        className={ui.input}
                      />
                    </Field>
                    <Field label="Total price">
                      <input
                        value={detail.totpay || "0.00"}
                        readOnly
                        disabled
                        className={`${ui.input} font-semibold`}
                      />
                    </Field>
                    <Field label="Inclusions" className="sm:col-span-2">
                      <textarea
                        value={detail.inclusions}
                        onChange={handlePropertyText(propName, "inclusions")}
                        readOnly={isViewOnly}
                        disabled={isViewOnly}
                        rows={2}
                        className={ui.textarea}
                      />
                    </Field>
                  </div>
                </div>
              );
            })}

            {/* ── Notes ────────────────────────────────── */}
            <h3 className={`${ui.sectionTitle} mt-2`}>Notes</h3>
            <Field label="Enquiry" className="sm:col-span-2">
              <textarea
                name="enquiry"
                value={formData.enquiry || ""}
                onChange={handleChange}
                readOnly={isViewOnly}
                disabled={isViewOnly}
                rows={3}
                className={ui.textarea}
              />
            </Field>
            <Field label="Remark" className="sm:col-span-2">
              <textarea
                name="remark"
                value={formData.remark || ""}
                onChange={handleChange}
                readOnly={isViewOnly}
                disabled={isViewOnly}
                rows={2}
                className={ui.textarea}
              />
            </Field>
          </div>
        </div>

        {/* Fixed footer */}
        {mode === "edit" && (
          <footer className="grid shrink-0 grid-cols-2 gap-3 border-t bg-white px-6 py-4">
            <button onClick={onClose} className={`${ui.btn} ${ui.btnGhost}`}>
              Cancel
            </button>
            <button onClick={onSave} className={`${ui.btn} ${ui.btnPrimary}`}>
              Save
            </button>
          </footer>
        )}

        {showInstaChat && (
          <InstagramDemoChat
            lead={editingLead || formData}
            onClose={() => setShowInstaChat(false)}
          />
        )}
      </aside>
    </>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   TABLES
   ═════════════════════════════════════════════════════════════════════════ */
/* ── Small shared pieces ─────────────────────────────────────────────────── */
const Dash = () => <span className="text-xs text-gray-300">—</span>;

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "—");

function TableShell({ head, children, footer }) {
  return (
    <section className={`${ui.card} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-gray-600">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>{head}</tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
      {footer}
    </section>
  );
}

function EmptyRow({ colSpan, children, tone = "text-gray-400" }) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className={`px-4 py-14 text-center text-sm ${tone}`}
      >
        {children}
      </td>
    </tr>
  );
}

function ContactLines({ phone, email, time }) {
  return (
    <div className="flex flex-col gap-1 text-xs">
      {phone && (
        <span className="inline-flex items-center gap-1.5 text-gray-600">
          <FaPhone className="shrink-0 text-gray-400" /> {phone}
        </span>
      )}
      {email && (
        <span className="inline-flex items-center gap-1.5 break-all text-gray-500">
          <IoMail className="shrink-0 text-gray-400" /> {email}
        </span>
      )}
      {time && (
        <span className="inline-flex items-center gap-1.5 text-gray-400">
          <IoIosTime className="shrink-0" /> {time}
        </span>
      )}
    </div>
  );
}
const SCORE_TONES = {
  "Ready to book": "bg-red-100 text-red-600",
  Interested: "bg-orange-100 text-orange-600",
  "Just browsing": "bg-slate-100 text-slate-500",
  Booked: "bg-green-100 text-green-700",
  Lost: "bg-gray-200 text-gray-500",
};
function ScoreBadge({ s }) {
  if (!s) return null;
  const why = s.reasons
    .map((r) => `${r.points > 0 ? "+" : ""}${r.points} ${r.text}`)
    .join("\n");
  return (
    <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{why}</div>}>
      <Badge className={SCORE_TONES[s.label]}>
        {s.label} · {s.score}
      </Badge>
    </Tooltip>
  );
}
function Badge({ children, className = "", style }) {
  return (
    <span
      style={style}
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${className}`}
    >
      {children}
    </span>
  );
}

function StatusSelect({ value, options, colors, fallback = "", onChange }) {
  const c = colors[value] || "#94a3b8";
  return (
    <select
      value={value || fallback}
      onChange={(e) => onChange(e.target.value)}
      className="!h-8 !py-0 !leading-none min-w-[150px] cursor-pointer rounded-md border !px-2 text-[11px] font-bold outline-none"
      style={{ borderColor: c, background: c + "18", color: c }}
    >
      {options.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );
}

function AssigneeSelect({ value, teamMembers, teamLoading, onChange }) {
  return (
    <select
      value={value || "Unassigned"}
      onChange={(e) => onChange(e.target.value)}
      disabled={teamLoading}
      className="!h-8 !py-0 !leading-none min-w-[130px] cursor-pointer rounded-md border border-gray-300 bg-white !px-2 text-xs outline-none focus:border-[#1D2826] disabled:bg-gray-50"
    >
      {teamLoading ? (
        <option>Loading...</option>
      ) : (
        teamMembers.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))
      )}
    </select>
  );
}

function RemarkCell({ remark, onEdit }) {
  return remark ? (
    <button
      type="button"
      onClick={onEdit}
      title="Click to edit"
      className="block max-w-[180px] rounded-md bg-gray-50 px-2 py-1 text-left text-xs leading-snug text-gray-600 hover:bg-gray-100"
    >
      {remark}
    </button>
  ) : (
    <button
      type="button"
      onClick={onEdit}
      className="whitespace-nowrap rounded-md border border-dashed border-indigo-300 px-2 py-1 text-[11px] font-medium text-indigo-500 hover:bg-indigo-50"
    >
      + Add remark
    </button>
  );
}

function LeadActions({
  lead,
  source,
  name,
  sendingMailId,
  mailReady,
  onView,
  onEdit,
  onMail,
  onDelete,
}) {
  const sending = sendingMailId === lead.id;
  return (
    <div className="flex items-center gap-2">
      <IconButton tip="View lead" onClick={onView}>
        <FaEye size={14} />
      </IconButton>
      <IconButton tip="Edit lead" tone="yellow" onClick={onEdit}>
        <FaEdit size={14} />
      </IconButton>
      <IconButton
        tip={
          sending
            ? "Sending…"
            : mailReady
              ? "Send email"
              : "Add property, check-in & check-out first"
        }
        tone="purple"
        disabled={sending || !mailReady}
        onClick={onMail}
      >
        <IoIosMail size={16} className={sending ? "animate-pulse" : ""} />
      </IconButton>
      {lead.status === "Lost & Closed" && (
        <IconButton
          tip="Delete lead"
          tone="red"
          onClick={() => onDelete({ id: lead.id, name, type: source })}
        >
          <MdDelete size={14} />
        </IconButton>
      )}
    </div>
  );
}

function WhatsAppButton({ onClick }) {
  return (
    <IconButton tip="Send property details on WhatsApp" onClick={onClick}>
      <RiWhatsappFill size={16} className="text-[#25d366]" />
    </IconButton>
  );
}

function EnquiryText({ text }) {
  if (!text) return <Dash />;
  return (
    <div className="space-y-2 text-xs leading-relaxed text-gray-600">
      {text.split("\n").map((line, i) => {
        const clean = line.replace(/^•\s*/, "").trim();
        if (!clean) return null;
        const [label, ...rest] = clean.split(":");
        const value = rest.join(":").trim();
        return (
          <div key={i}>
            <div className="text-gray-500">
              {label}
              {value ? ":" : ""}
            </div>
            {value && (
              <div className="font-semibold text-gray-800">{value}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const rowCls = "border-b border-gray-100 transition-colors hover:bg-gray-50";

/* ── META TABLE ──────────────────────────────────────────────────────────── */
function MetaLeadsTable({
  leads, // currentLeads
  loading,
  isIncomingView,
  isAllLeadsView,
  selectedRows,
  onSelectAll,
  onRowCheck, // handleRowCheckboxChange
  updatingId,
  teamMembers,
  teamLoading,
  sendingMailId,
  isLeadMailReady,
  patchLead,
  handleAssigneeChange,
  addRemark,
  openLeadPanel,
  openMailConfirm,
  setDeleteTarget,
  handleWhatsAppClick,
  onMetaProcessed, // handleMetaLeadProcessed
  pagination, // props for <Pagination />
}) {
  const colSpan = isIncomingView ? 5 : 9;
  const allChecked = leads.length > 0 && selectedRows.length === leads.length;

  const head = (
    <>
      <th className={`${ui.th} w-10`}>
        <input type="checkbox" checked={allChecked} onChange={onSelectAll} />
      </th>
      <th className={ui.th}>Lead details</th>
      <th className={ui.th}>Enquiry</th>
      {isIncomingView ? (
        <>
          <th className={ui.th}>Meta fields</th>
          <th className={ui.th}>Action</th>
        </>
      ) : (
        <>
          <th className={ui.th}>Status</th>

          <th className={ui.th}>Assigned to</th>
          <th className={ui.th}>Remark</th>
          <th className={ui.th}>Date</th>
          <th className={ui.th}>Actions</th>
          <th className={`${ui.th} text-center`}>Follow up</th>
        </>
      )}
    </>
  );

  return (
    <TableShell head={head} footer={<Pagination {...pagination} />}>
      {loading ? (
        <tr>
          <td colSpan={colSpan}>
            <Loader label="Loading..." fullScreen={false} className="py-6" />
          </td>
        </tr>
      ) : leads.length === 0 ? (
        <EmptyRow colSpan={colSpan}>
          {isIncomingView
            ? "No unprocessed Meta leads. They'll appear here automatically via Meta webhooks."
            : "No leads found for this filter."}
        </EmptyRow>
      ) : (
        leads.map((lead) => {
          const ch = CHANNELS[lead.channel] || {
            color: "#888",
            bg: "#f1f5f9",
            icon: null,
          };
          const isMetaLead = String(lead.id).startsWith("meta_");
          const showProcessButton = isAllLeadsView && isMetaLead;

          return (
            <tr
              key={lead.id}
              className={rowCls}
              style={{ opacity: updatingId === lead.id ? 0.55 : 1 }}
            >
              <td className={ui.td}>
                <input
                  type="checkbox"
                  checked={selectedRows.includes(lead.id)}
                  onChange={() =>
                    onRowCheck(lead.id, isMetaLead && isIncomingView)
                  }
                />
              </td>

              {/* Lead details */}
              <td className={`${ui.td} min-w-[230px] align-top`}>
                <div className="mb-1.5 font-semibold text-gray-900">
                  {lead.name || "—"} <ScoreBadge s={lead.lead_score} />
                </div>
                <div className="mb-2 flex flex-wrap items-center gap-1.5">
                  <Badge style={{ background: ch.bg, color: ch.color }}>
                    {ch.icon} {lead.channel}
                  </Badge>
                  {lead.tag && (
                    <Badge
                      style={{
                        background: (TAG_COLORS[lead.tag] || "#999") + "18",
                        color: TAG_COLORS[lead.tag] || "#999",
                      }}
                    >
                      {lead.tag}
                    </Badge>
                  )}
                  {showProcessButton && (
                    <Badge className="bg-amber-100 text-amber-700">
                      Unprocessed
                    </Badge>
                  )}
                </div>

                <ContactLines
                  phone={lead.phone}
                  email={lead.email}
                  time={timeAgo(lead.created_at)}
                />
              </td>

              {/* Enquiry */}
              <td className={`${ui.td} min-w-[200px] max-w-[280px] align-top`}>
                <EnquiryText text={lead.enquiry} />
              </td>

              {isIncomingView ? (
                <>
                  <td
                    className={`${ui.td} min-w-[200px] max-w-[260px] align-top`}
                  >
                    {(lead.field_data || []).length > 0 ? (
                      <div className="flex flex-col gap-1">
                        {lead.field_data.map((f, i) => (
                          <div key={i} className="text-[11px] leading-snug">
                            <span className="text-gray-400">
                              {f.name.replace(/_/g, " ")}:{" "}
                            </span>
                            <span className="font-semibold text-gray-700">
                              {f.values?.[0] || "—"}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Dash />
                    )}
                  </td>
                  <td className={ui.td}>
                    <ProcessMetaLeadButton
                      lead={lead}
                      onProcessed={onMetaProcessed}
                    />
                  </td>
                </>
              ) : (
                <>
                  {/* Status */}
                  <td className={ui.td}>
                    {showProcessButton ? (
                      <ProcessMetaLeadButton
                        lead={lead}
                        onProcessed={onMetaProcessed}
                      />
                    ) : (
                      <StatusSelect
                        value={lead.status}
                        options={STATUSES}
                        colors={STATUS_COLORS}
                        onChange={(v) => patchLead(lead.id, "status", v)}
                      />
                    )}
                  </td>

                  {/* Assignee */}
                  <td className={ui.td}>
                    {showProcessButton ? (
                      <Dash />
                    ) : (
                      <AssigneeSelect
                        value={lead.assignee}
                        teamMembers={teamMembers}
                        teamLoading={teamLoading}
                        onChange={(v) => handleAssigneeChange(lead.id, v)}
                      />
                    )}
                  </td>

                  {/* Remark */}
                  <td className={ui.td}>
                    {showProcessButton ? (
                      <Dash />
                    ) : (
                      <RemarkCell
                        remark={lead.remark}
                        onEdit={() => addRemark(lead)}
                      />
                    )}
                  </td>

                  {/* Date */}
                  <td className={`${ui.td} whitespace-nowrap text-xs`}>
                    {showProcessButton ? (
                      <Dash />
                    ) : (
                      <Tooltip
                        title={
                          lead.created_at
                            ? new Date(lead.created_at).toLocaleString()
                            : "—"
                        }
                        placement="left"
                      >
                        <span>{fmtDate(lead.created_at)}</span>
                      </Tooltip>
                    )}
                  </td>

                  {/* Actions */}
                  <td className={ui.td}>
                    {!showProcessButton && (
                      <LeadActions
                        lead={lead}
                        source="meta"
                        name={lead.name}
                        sendingMailId={sendingMailId}
                        mailReady={isLeadMailReady(lead)}
                        onView={() => openLeadPanel(lead, "view")}
                        onEdit={() => openLeadPanel(lead, "edit")}
                        onMail={() => openMailConfirm(lead, "meta")}
                        onDelete={setDeleteTarget}
                      />
                    )}
                  </td>

                  {/* Follow up */}
                  <td className={`${ui.td} text-center`}>
                    {showProcessButton ? (
                      <Dash />
                    ) : (
                      <div className="flex justify-center">
                        <WhatsAppButton
                          onClick={() => handleWhatsAppClick(lead, "meta")}
                        />
                      </div>
                    )}
                  </td>
                </>
              )}
            </tr>
          );
        })
      )}
    </TableShell>
  );
}

/* ── WEBSITE TABLE ───────────────────────────────────────────────────────── */
function WebsiteLeadsTable({
  leads, // currentLeads
  loading, // isWebsiteIncomingView ? websiteLoading : processedWebsiteLoading
  error, // websiteError
  isWebsiteIncomingView,
  selectedRows,
  onSelectAll,
  onRowCheck,
  updatingId,
  teamMembers,
  teamLoading,
  sendingMailId,
  isLeadMailReady,
  patchWebsiteLead,
  addWebsiteRemark,
  openLeadPanel,
  openMailConfirm,
  setDeleteTarget,
  handleWhatsAppClick,
  onWebsiteProcessed, // handleWebsiteLeadProcessed
  pagination,
}) {
  const colSpan = isWebsiteIncomingView ? 7 : 11;
  const allChecked = leads.length > 0 && selectedRows.length === leads.length;

  const head = (
    <>
      <th className={`${ui.th} w-10`}>
        <input type="checkbox" checked={allChecked} onChange={onSelectAll} />
      </th>
      <th className={ui.th}>Lead ID</th>
      <th className={ui.th}>Customer</th>
      <th className={ui.th}>Property / destination</th>
      <th className={ui.th}>Check-in → Check-out</th>
      <th className={ui.th}>Location</th>
      {isWebsiteIncomingView ? (
        <th className={ui.th}>Action</th>
      ) : (
        <>
          <th className={ui.th}>Status</th>
          <th className={ui.th}>Assigned to</th>
          <th className={ui.th}>Remark</th>
          <th className={ui.th}>Actions</th>
          <th className={`${ui.th} text-center`}>Follow up</th>
        </>
      )}
    </>
  );

  return (
    <TableShell head={head} footer={<Pagination {...pagination} />}>
      {loading ? (
        <tr>
          <td colSpan={colSpan}>
            <Loader label="Loading..." fullScreen={false} className="py-6" />
          </td>
        </tr>
      ) : error && isWebsiteIncomingView ? (
        <EmptyRow colSpan={colSpan} tone="text-red-400">
          {error}
        </EmptyRow>
      ) : leads.length === 0 ? (
        <EmptyRow colSpan={colSpan}>
          {isWebsiteIncomingView
            ? "No new website leads. All incoming leads have been processed."
            : "No processed website leads found for this stage."}
        </EmptyRow>
      ) : (
        leads.map((lead) => {
          const raw = lead._isIncoming === true || isWebsiteIncomingView;
          const rowId = raw ? lead.leadid : lead.id;
          const name = raw
            ? lead.customername ||
              [lead.firstname, lead.lastname].filter(Boolean).join(" ") ||
              "—"
            : lead.customer_name || "—";
          const checkin = raw ? lead.checkin : lead.check_in;
          const checkout = raw ? lead.checkout : lead.check_out;
          const location = [lead.city, lead.state, lead.country]
            .filter(Boolean)
            .join(", ");

          return (
            <tr
              key={rowId}
              className={rowCls}
              style={{ opacity: updatingId === lead.id ? 0.55 : 1 }}
            >
              <td className={ui.td}>
                <input
                  type="checkbox"
                  checked={selectedRows.includes(rowId)}
                  onChange={() => onRowCheck(rowId, raw)}
                />
              </td>

              {/* Lead ID */}
              <td className={`${ui.td} align-top`}>
                <div className="flex flex-col items-start gap-1">
                  <span className="whitespace-nowrap font-mono text-xs text-indigo-600">
                    {raw ? lead.leadid : `WL-${lead.original_lead_id}`}
                  </span>
                  {raw && !isWebsiteIncomingView && (
                    <Badge className="bg-amber-100 text-amber-700">
                      Unprocessed
                    </Badge>
                  )}
                </div>
              </td>

              {/* Customer */}
              <td className={`${ui.td} min-w-[200px] align-top`}>
                <div className="mb-1.5 font-semibold text-gray-900">
                  {name} <ScoreBadge s={lead.lead_score} />
                </div>
                <ContactLines phone={lead.phone} email={lead.email} />
                {!raw && lead.converted === 1 && (
                  <Badge className="mt-2 bg-green-100 text-green-700">
                    ✓ Converted
                  </Badge>
                )}
              </td>

              {/* Property */}
              <td className={`${ui.td} min-w-[180px] align-top`}>
                <div className="text-xs font-semibold text-gray-700">
                  {lead.property || "—"}
                </div>
                <div className="mt-0.5 text-xs text-gray-400">
                  {lead.destination || "—"}
                </div>
              </td>

              {/* Dates */}
              <td
                className={`${ui.td} whitespace-nowrap text-xs text-gray-600`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <FaCalendarAlt className="text-gray-400" />
                  {fmtDate(checkin)} → {fmtDate(checkout)}
                </span>
              </td>

              {/* Location */}
              <td className={`${ui.td} min-w-[140px] text-xs text-gray-500`}>
                {location || "—"}
              </td>

              {isWebsiteIncomingView ? (
                <td className={ui.td}>
                  <ProcessWebsiteLeadButton
                    lead={lead}
                    onProcessed={onWebsiteProcessed}
                  />
                </td>
              ) : (
                <>
                  {/* Status */}
                  <td className={ui.td}>
                    {raw ? (
                      <ProcessWebsiteLeadButton
                        lead={lead}
                        onProcessed={onWebsiteProcessed}
                      />
                    ) : (
                      <StatusSelect
                        value={lead.status}
                        fallback="New Lead"
                        options={WEBSITE_STATUSES}
                        colors={WEBSITE_STATUS_COLORS}
                        onChange={(v) => patchWebsiteLead(lead.id, "status", v)}
                      />
                    )}
                  </td>

                  {/* Assignee */}
                  <td className={ui.td}>
                    {raw ? (
                      <Dash />
                    ) : (
                      <AssigneeSelect
                        value={lead.assignee}
                        teamMembers={teamMembers}
                        teamLoading={teamLoading}
                        onChange={(v) =>
                          patchWebsiteLead(lead.id, "assignee", v)
                        }
                      />
                    )}
                  </td>

                  {/* Remark */}
                  <td className={ui.td}>
                    {raw ? (
                      <Dash />
                    ) : (
                      <RemarkCell
                        remark={lead.remark}
                        onEdit={() => addWebsiteRemark(lead)}
                      />
                    )}
                  </td>

                  {/* Actions */}
                  <td className={ui.td}>
                    {raw ? (
                      <Dash />
                    ) : (
                      <LeadActions
                        lead={lead}
                        source="website"
                        name={lead.customer_name}
                        sendingMailId={sendingMailId}
                        mailReady={isLeadMailReady(lead)}
                        onView={() => openLeadPanel(lead, "view", "website")}
                        onEdit={() => openLeadPanel(lead, "edit", "website")}
                        onMail={() => openMailConfirm(lead, "website")}
                        onDelete={setDeleteTarget}
                      />
                    )}
                  </td>

                  {/* Follow up */}
                  <td className={`${ui.td} text-center`}>
                    {raw ? (
                      <Dash />
                    ) : (
                      <div className="flex justify-center">
                        <WhatsAppButton
                          onClick={() => handleWhatsAppClick(lead, "website")}
                        />
                      </div>
                    )}
                  </td>
                </>
              )}
            </tr>
          );
        })
      )}
    </TableShell>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT  (state + handlers: unchanged)
   ═════════════════════════════════════════════════════════════════════════ */
const InquireLeads = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const s = localStorage.getItem("menubarOpen");
    return s !== null ? JSON.parse(s) : window.innerWidth >= 1024;
  });
  const [storeTab, setStoreTab] = useState("meta");
  const [mailConfirmLead, setMailConfirmLead] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadSource, setLeadSource] = useState("meta");
  const [mailConfirmSource, setMailConfirmSource] = useState("meta");
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sendingMailId, setSendingMailId] = useState(null);
  const [processedMetaIds, setProcessedMetaIds] = useState(new Set());
  const [activeStatus, setActiveStatus] = useState("Incoming Leads");
  const [websiteLeads, setWebsiteLeads] = useState([]);
  const [websiteLoading, setWebsiteLoading] = useState(false);
  const [websiteError, setWebsiteError] = useState(null);
  const [processedWebsiteIds, setProcessedWebsiteIds] = useState(new Set());
  const [processedWebsiteLeads, setProcessedWebsiteLeads] = useState([]);
  const [processedWebsiteLoading, setProcessedWebsiteLoading] = useState(false);
  const [websiteActiveStatus, setWebsiteActiveStatus] = useState("All Leads");
  const [activeChannel, setActiveChannel] = useState("All");
  const [activeTag] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [followUpLead, setFollowUpLead] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertResult, setConvertResult] = useState(null);
  const [showBulkProcessModal, setShowBulkProcessModal] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProcessResult, setBulkProcessResult] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, type: "meta"|"website" }
  const [deleting, setDeleting] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = currentUser.id;
  const [properties, setProperties] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [notifBreakdown, setNotifBreakdown] = useState({ meta: 0, website: 0 });
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const rowsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [showPopup_filter, setShowPopup_filter] = useState(false);
  const filterRef = useRef(null);
  const [isLeadPanelOpen, setIsLeadPanelOpen] = useState(false);
  const [leadPanelMode, setLeadPanelMode] = useState("view"); // "view" | "edit"
  const [editingLead, setEditingLead] = useState(null);
  const [leadFormData, setLeadFormData] = useState({});
  const [websiteFilter, setWebsiteFilter] = useState("All"); // "All" | "Processed" | "Unprocessed"
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [waPhoneTarget, setWaPhoneTarget] = useState(null); // { lead, source }

  useEffect(() => {
    fetchTeamMembers();
  }, []);
  const fetchTeamMembers = async () => {
    try {
      setTeamLoading(true);
      const response = await fetch(
        "https://dash.zorbastays.com/web-backend/users",
      );
      const data = await response.json();

      // Extract names from the API response
      const names = data.map((user) => user.name);
      // Add "Unassigned" at the beginning
      setTeamMembers(["Unassigned", ...names]);
    } catch (error) {
      console.error("Error fetching team members:", error);
      // Fallback to static list if API fails
      setTeamMembers([
        "Unassigned",
        "Parth",
        "Altaf",
        "Riya",
        "Dev",
        "Sneha",
        "Maharshi",
      ]);
    } finally {
      setTeamLoading(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRows([]);
    setWebsiteFilter("All");
  }, [
    search,
    activeStatus,
    activeChannel,
    activeTag,
    websiteActiveStatus,
    storeTab,
  ]);
  useEffect(() => {
    function handleClickOutside(e) {
      if (filterRef.current && !filterRef.current.contains(e.target))
        setShowPopup_filter(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) setIsSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Just fetch all, filter client-side
      const res = await fetch(`${API}/leads?limit=200`);
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();
      const allLeads = data.leads || [];
      setProcessedMetaIds(
        new Set(
          allLeads
            .filter((l) => !String(l.id).startsWith("meta_") && l.meta_lead_id)
            .map((l) => l.meta_lead_id),
        ),
      );
      setLeads(allLeads);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []); // no longer depends on filter states

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const res = await fetch(`${API}/properties`);
        const data = await res.json();
        setProperties(data);
      } catch (err) {
        console.error("Error fetching properties:", err);
      }
    };
    fetchProperties();
  }, []);
  const fetchWebsiteLeads = useCallback(async () => {
    setWebsiteLoading(true);
    setWebsiteError(null);
    try {
      const res = await fetch(`${API}/website-leads`);
      if (!res.ok) throw new Error(`API error ${res.status}`);
      setWebsiteLeads((await res.json()).data || []);
    } catch (e) {
      setWebsiteError(e.message);
    } finally {
      setWebsiteLoading(false);
    }
  }, []);

  const fetchProcessedWebsiteLeads = useCallback(async () => {
    setProcessedWebsiteLoading(true);
    try {
      const url =
        websiteActiveStatus !== "Incoming Leads" &&
        websiteActiveStatus !== "All Processed Leads" &&
        websiteActiveStatus !== "All Leads"
          ? `${API}/processed-website-leads?status=${encodeURIComponent(websiteActiveStatus)}`
          : `${API}/processed-website-leads`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const rows = (await res.json()).data || [];
      setProcessedWebsiteLeads(rows);
      setProcessedWebsiteIds(
        new Set(rows.map((r) => String(r.original_lead_id))),
      );
    } catch (e) {
      console.error("fetchProcessedWebsiteLeads:", e.message);
    } finally {
      setProcessedWebsiteLoading(false);
    }
  }, [websiteActiveStatus]);

  useEffect(() => {
    if (storeTab === "website") {
      fetchWebsiteLeads();
      fetchProcessedWebsiteLeads();
    }
  }, [storeTab, fetchWebsiteLeads, fetchProcessedWebsiteLeads]);

  // ── Notifications ───────────────────────────────────────────────────────────
  const [, setCurrentCounts] = useState({ meta: 0, website: 0 });
  const hydrateTimes = (d) => {
    const ci = parseTimeParts(d.checkin_time, "12", "PM");
    const co = parseTimeParts(d.checkout_time, "11", "AM");
    return {
      ...d,
      checkinHour: ci.hour,
      checkinMinute: ci.minute,
      checkinAmPm: ci.ampm,
      checkoutHour: co.hour,
      checkoutMinute: co.minute,
      checkoutAmPm: co.ampm,
      checkin_time: `${ci.hour}:${ci.minute} ${ci.ampm}`,
      checkout_time: `${co.hour}:${co.minute} ${co.ampm}`,
    };
  };
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API}/lead-notifications?userId=${userId}`);
      const data = await res.json();
      setNotifCount(data.total || 0);
      setNotifBreakdown({ meta: data.meta || 0, website: data.website || 0 });
      setCurrentCounts({
        meta: data.currentMeta || 0,
        website: data.currentWebsite || 0,
      });
    } catch (e) {
      console.error("Notification fetch failed:", e.message);
    }
  }, [userId]);

  const markSeen = useCallback(async () => {
    if (!userId) return;
    try {
      await fetch(`${API}/lead-notifications/mark-seen`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId }), // just userId, nothing else
      });
      setNotifCount(0);
      setNotifBreakdown({ meta: 0, website: 0 });
      setShowNotifDropdown(false);
    } catch (e) {
      console.error("markSeen failed:", e.message);
    }
  }, [userId]);
  // Poll every 60 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const notifRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target))
        setShowNotifDropdown(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const matchesSearch = (lead, term) => {
    if (!term) return true;
    const t = term.toLowerCase();
    return (
      (lead.name || lead.customername || lead.customer_name || "")
        .toLowerCase()
        .includes(t) ||
      (lead.phone || "").toLowerCase().includes(t) ||
      (lead.email || "").toLowerCase().includes(t)
    );
  };

  const matchesDateRange = (dateStr) => {
    if (!dateFrom && !dateTo) return true;
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (dateFrom && d < new Date(dateFrom)) return false;
    if (dateTo && d > new Date(dateTo + "T23:59:59")) return false;
    return true;
  };
  const parseTimeParts = (timeStr, fallbackHour, fallbackAmPm) => {
    if (!timeStr)
      return { hour: fallbackHour, minute: "00", ampm: fallbackAmPm };
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return { hour: fallbackHour, minute: "00", ampm: fallbackAmPm };
    return {
      hour: match[1].padStart(2, "0"),
      minute: match[2],
      ampm: match[3].toUpperCase(),
    };
  };
  const buildInitialPropertyDetails = (lead, source) => {
    const existing = lead.propertydetails;

    if (existing && Object.keys(existing).length > 0) {
      // Clean any legacy comma-joined keys on read
      const cleaned = {};
      Object.entries(existing).forEach(([k, v]) => {
        if (k.includes(",")) {
          k.split(",").forEach((name) => {
            const trimmed = name.trim();
            if (trimmed) cleaned[trimmed] = hydrateTimes({ ...v });
          });
        } else {
          cleaned[k] = hydrateTimes(v);
        }
      });
      const wanted = String(
        (source === "website" ? lead.property : lead.propertyname) || "",
      )
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      return reconcilePropertyDetails(cleaned, wanted);
    }

    // Fallback: reconstruct from flat columns, splitting comma-joined propertyname
    const raw = source === "website" ? lead.property : lead.propertyname;
    if (!raw) return {};

    const names = String(raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const ci = parseTimeParts(lead.checkin_time, "12", "PM");
    const co = parseTimeParts(lead.checkout_time, "11", "AM");

    const details = {};
    names.forEach((name) => {
      details[name] = {
        destination: lead.destination || "",
        guests: lead.guests || "",
        bedrooms: lead.bedrooms || "",
        inclusions: lead.inclusions || "",
        checkin:
          source === "website" ? lead.check_in || "" : lead.checkin || "",
        checkout:
          source === "website" ? lead.check_out || "" : lead.checkout || "",
        checkin_time: lead.checkin_time || `${ci.hour}:${ci.minute} ${ci.ampm}`,
        checkout_time:
          lead.checkout_time || `${co.hour}:${co.minute} ${co.ampm}`,
        checkinHour: ci.hour,
        checkinMinute: ci.minute,
        checkinAmPm: ci.ampm,
        checkoutHour: co.hour,
        checkoutMinute: co.minute,
        checkoutAmPm: co.ampm,
        proprice: lead.proprice || "",
        secdep: lead.secdep || "",
        advpay: lead.advpay || "",
        balpay: lead.balpay || "",
        totpay: lead.totpay || "",
      };
    });
    return details;
  };

  // ── visibleLeads (Meta) ───────────────────────────────────────────────────────
  const visibleLeads = (() => {
    let base;
    if (activeStatus === "All Leads") base = leads;
    else if (activeStatus === "Incoming Leads")
      base = leads.filter(
        (l) =>
          String(l.id).startsWith("meta_") &&
          !processedMetaIds.has(String(l.id).replace("meta_", "")),
      );
    else if (activeStatus === "All Processed Leads")
      base = leads.filter((l) => !String(l.id).startsWith("meta_"));
    else
      base = leads.filter(
        (l) => !String(l.id).startsWith("meta_") && l.status === activeStatus,
      );

    return base
      .filter((l) => matchesSearch(l, search))
      .filter((l) => activeChannel === "All" || l.channel === activeChannel)
      .filter((l) => matchesDateRange(l.created_at));
  })();

  // ── visibleWebsiteLeads ───────────────────────────────────────────────────────
  const visibleWebsiteLeads = (() => {
    let base;
    if (websiteActiveStatus === "All Leads") {
      const incoming = websiteLeads
        .filter(
          (l) => !processedWebsiteIds.has(String(l.leadid).replace("WL-", "")),
        )
        .map((l) => ({ ...l, _isIncoming: true }));
      base = [...incoming, ...processedWebsiteLeads];
    } else if (websiteActiveStatus === "Incoming Leads") {
      base = websiteLeads
        .filter(
          (l) => !processedWebsiteIds.has(String(l.leadid).replace("WL-", "")),
        )
        .map((l) => ({ ...l, _isIncoming: true }));
    } else if (websiteActiveStatus === "All Processed Leads") {
      base = processedWebsiteLeads;
    } else {
      base = processedWebsiteLeads.filter(
        (l) => l.status === websiteActiveStatus,
      );
    }

    // Apply websiteFilter (Processed / Unprocessed) — only meaningful in All Leads
    if (websiteActiveStatus === "All Leads") {
      if (websiteFilter === "Unprocessed")
        base = base.filter((l) => l._isIncoming);
      else if (websiteFilter === "Processed")
        base = base.filter((l) => !l._isIncoming);
    }

    return base.filter((l) => {
      const nameMatch = matchesSearch(l, search);
      const dateField = l._isIncoming ? l.createdat : l.created_at;
      return nameMatch && matchesDateRange(dateField);
    });
  })();

  const patchLead = async (id, field, value) => {
    setUpdatingId(id);
    setLeads((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
    );
    try {
      const res = await fetch(`${API}/leads/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) throw new Error("patch failed");
    } catch {
      fetchLeads();
    } finally {
      setUpdatingId(null);
    }
  };
  const handleAssigneeChange = async (id, value) => {
    setUpdatingId(id);
    setLeads((prev) =>
      prev.map((l) => (l.id === id ? { ...l, assignee: value } : l)),
    );
    try {
      const res = await fetch(`${API}/leads/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assignee: value }),
      });
      if (!res.ok) throw new Error("patch failed");
    } catch {
      fetchLeads();
    } finally {
      setUpdatingId(null);
    }
  };
  const openLeadPanel = async (lead, mode, source = "meta") => {
    setLeadSource(source);
    setEditingLead(lead);
    setIsLeadPanelOpen(true);
    setLeadPanelMode(mode);

    // Show the panel immediately with whatever we have
    const ci = parseTimeParts(lead.checkin_time, "12", "PM");
    const co = parseTimeParts(lead.checkout_time, "11", "AM");
    setLeadFormData({
      ...lead,
      checkinHour: ci.hour,
      checkinMinute: ci.minute,
      checkinAmPm: ci.ampm,
      checkoutHour: co.hour,
      checkoutMinute: co.minute,
      checkoutAmPm: co.ampm,
      propertyname:
        source === "website" ? lead.property || "" : lead.propertyname || "",
      propertyDetails: buildInitialPropertyDetails(lead, source),
    });

    // ── Re-fetch the fresh row so propertydetails reflects the DB ──
    try {
      const url =
        source === "website"
          ? `${API}/processed-website-leads/${lead.id}`
          : `${API}/leads/${lead.id}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const fresh = await res.json();

      const fci = parseTimeParts(fresh.checkin_time, "12", "PM");
      const fco = parseTimeParts(fresh.checkout_time, "11", "AM");

      const baseForm = {
        ...fresh,
        checkinHour: fci.hour,
        checkinMinute: fci.minute,
        checkinAmPm: fci.ampm,
        checkoutHour: fco.hour,
        checkoutMinute: fco.minute,
        checkoutAmPm: fco.ampm,
        propertyname:
          source === "website"
            ? fresh.property || ""
            : fresh.propertyname || "",
        propertyDetails: buildInitialPropertyDetails(fresh, source),
      };

      if (source === "website") {
        const [firstname = "", ...rest] = (fresh.customer_name || "")
          .trim()
          .split(/\s+/);
        baseForm.firstname = fresh.firstname || firstname;
        baseForm.lastname = fresh.lastname || rest.join(" ");
      } else {
        const [firstname = "", ...rest] = (fresh.name || "")
          .trim()
          .split(/\s+/);
        baseForm.firstname = fresh.firstname || firstname;
        baseForm.lastname = fresh.lastname || rest.join(" ");
      }

      setLeadFormData(baseForm);
      setEditingLead(fresh);
    } catch (err) {
      console.warn("Could not refresh lead, using list-row data:", err.message);
    }
  };
  const closeLeadPanel = () => {
    setIsLeadPanelOpen(false);
    setEditingLead(null);
    setLeadFormData({});
    setLeadSource("meta");
  };

  const handleSaveLead = async () => {
    if (!editingLead) return;
    setUpdatingId(editingLead.id);
    try {
      let endpoint, payload;

      const {
        checkinHour,
        checkinMinute,
        checkinAmPm,
        checkoutHour,
        checkoutMinute,
        checkoutAmPm,
        propertyDetails,
        ...cleanFormData
      } = leadFormData;

      // Strip scratch fields from each per-property detail
      const cleanedDetails = {};
      const selectedNames = getSelectedProperties(leadFormData);
      Object.entries(propertyDetails || {}).forEach(([name, d]) => {
        if (selectedNames.length && !selectedNames.includes(name)) return;

        const {
          checkinHour,
          checkinMinute,
          checkinAmPm,
          checkoutHour,
          checkoutMinute,
          checkoutAmPm,
          ...rest
        } = d;

        // 👇 derive the combined strings from the scratch fields (fall back to
        //    the empty-property defaults if a field is missing). This guarantees
        //    the DB always receives a valid "HH:MM AM/PM" string even when the
        //    user accepted the defaults and never touched the time pickers.
        const safeCheckinHour = checkinHour || "12";
        const safeCheckinMinute = checkinMinute || "00";
        const safeCheckinAmPm = checkinAmPm || "PM";
        const safeCheckoutHour = checkoutHour || "11";
        const safeCheckoutMinute = checkoutMinute || "00";
        const safeCheckoutAmPm = checkoutAmPm || "AM";

        cleanedDetails[name] = {
          ...rest,
          checkin_time: `${safeCheckinHour}:${safeCheckinMinute} ${safeCheckinAmPm}`,
          checkout_time: `${safeCheckoutHour}:${safeCheckoutMinute} ${safeCheckoutAmPm}`,
        };
      });

      if (leadSource === "website") {
        endpoint = `${API}/processed-website-leads/${editingLead.id}`;
        const { propertyname, checkin, checkout, ...rest } = cleanFormData;
        payload = {
          ...rest,
          property: propertyname,
          propertydetails: cleanedDetails,
        };
      } else {
        endpoint = `${API}/leads/${editingLead.id}`;
        payload = { ...cleanFormData, propertydetails: cleanedDetails };
      }

      const res = await fetch(endpoint, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Update failed");

      if (leadSource === "website") {
        setProcessedWebsiteLeads((prev) =>
          prev.map((l) => (l.id === editingLead.id ? { ...l, ...payload } : l)),
        );
      } else {
        setLeads((prev) =>
          prev.map((l) =>
            l.id === editingLead.id
              ? { ...l, ...leadFormData, propertydetails: cleanedDetails }
              : l,
          ),
        );
      }
      toast.success("Lead updated successfully");
      closeLeadPanel();
    } catch (e) {
      toast.error("Error: " + e.message);
    } finally {
      setUpdatingId(null);
    }
  };
  const patchWebsiteLead = async (id, field, value) => {
    setUpdatingId(id);
    setProcessedWebsiteLeads((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
    );
    try {
      const res = await fetch(`${API}/processed-website-leads/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) throw new Error("patch failed");
    } catch {
      fetchProcessedWebsiteLeads();
    } finally {
      setUpdatingId(null);
    }
  };
  const handleDeleteLead = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const url =
        deleteTarget.type === "meta"
          ? `${API}/leads/${deleteTarget.id}`
          : `${API}/processed-website-leads/${deleteTarget.id}`;
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed " + res.status);

      if (deleteTarget.type === "meta") {
        setLeads((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      } else {
        setProcessedWebsiteLeads((prev) =>
          prev.filter((l) => l.id !== deleteTarget.id),
        );
      }
      toast.success("Lead deleted successfully");
    } catch (e) {
      toast.error("Error: " + e.message);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const addRemark = (lead) => {
    const r = prompt("Remark:", lead.remark || "");
    if (r !== null) patchLead(lead.id, "remark", r);
  };
  const addWebsiteRemark = (lead) => {
    const r = prompt("Remark:", lead.remark || "");
    if (r !== null) patchWebsiteLead(lead.id, "remark", r);
  };
  const sendWhatsAppFollowUp = (lead, source) => {
    const details = buildInitialPropertyDetails(lead, source);
    if (Object.keys(details).length === 0) {
      toast.warning(
        "Add property details to this lead before sending on WhatsApp",
      );
      return;
    }

    // Open the tab NOW, synchronously, inside the click handler.
    // No "noopener" here — we need the reference back to navigate it below.
    const win = window.open("", "_blank");

    (async () => {
      try {
        const names = Object.keys(details);
        const brochureMap = await fetchPropertyBrochures(names, properties);

        const customerName =
          source === "website"
            ? lead.customer_name
            : lead.name || `${lead.firstname || ""} ${lead.lastname || ""}`;
        const text = buildWhatsAppMessage(
          customerName,
          details,
          properties,
          brochureMap,
        );
        const url = `https://api.whatsapp.com/send/?phone=${normalizePhone(lead.phone)}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;

        if (win && !win.closed) {
          win.location.href = url;
        } else {
          toast.info(
            "Pop-up blocked. Allow pop-ups for this site and try again.",
          );
        }
      } catch (e) {
        console.error("[WhatsApp] Failed to build/send message:", e);
        if (win && !win.closed) win.close(); // don't leave a dead blank tab behind
        toast.error("Couldn't prepare the WhatsApp message: " + e.message);
      }
    })();
  };

  const handleWhatsAppClick = (lead, source) => {
    if (!isValidPhone(lead.phone)) {
      setWaPhoneTarget({ lead, source }); // ask for the number first
      return;
    }
    sendWhatsAppFollowUp(lead, source);
  };

  const handleWaPhoneSubmit = (phone) => {
    const { lead, source } = waPhoneTarget;
    // Save the number so we never ask again (optimistic update + PATCH)
    if (source === "website") patchWebsiteLead(lead.id, "phone", phone);
    else patchLead(lead.id, "phone", phone);
    sendWhatsAppFollowUp({ ...lead, phone }, source);
    setWaPhoneTarget(null);
  };
  const handleCreated = (lead) => setLeads((prev) => [lead, ...prev]);
  const handleMetaLeadProcessed = (savedLead) => {
    setProcessedMetaIds((prev) => {
      const n = new Set(prev);
      n.add(savedLead.meta_lead_id);
      return n;
    });
    setLeads((prev) =>
      prev.some((l) => l.id === savedLead.id) ? prev : [savedLead, ...prev],
    );
  };
  const handleWebsiteLeadProcessed = (savedLead) => {
    setProcessedWebsiteIds((prev) => {
      const n = new Set(prev);
      n.add(String(savedLead.original_lead_id));
      return n;
    });
    setProcessedWebsiteLeads((prev) =>
      prev.some((l) => l.id === savedLead.id) ? prev : [savedLead, ...prev],
    );
  };

  const handleConvertToBooking = async () => {
    const leadsToConvert = leads.filter((l) => selectedRows.includes(l.id));
    if (!leadsToConvert.length) return;
    setConverting(true);
    setConvertResult(null);
    const results = { bookingids: [], errors: [] };
    for (const lead of leadsToConvert) {
      try {
        const nameParts = (lead.name || "").trim().split(/\s+/);
        const payload = {
          firstname: nameParts[0] || "Unknown",
          lastname: nameParts.slice(1).join(" ") || "",
          email: lead.email || "",
          phone: lead.phone || "",
          tripexecutive:
            lead.assignee && lead.assignee !== "Unassigned"
              ? lead.assignee
              : "",
          bookingstatus: "Pending",
          paymentstatus: "Pending",
          source: channelToSource(lead.channel),
        };
        const res = await fetch(`${API}/createBooking`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.message || `HTTP ${res.status}`);
        }
        const data = await res.json();
        results.bookingids.push(data.bookingid || "—");
        const delRes = await fetch(`${API}/leads/${lead.id}`, {
          method: "DELETE",
        });
        if (!delRes.ok)
          console.warn(
            `Booking created but lead delete failed for id ${lead.id}`,
          );
      } catch (e) {
        results.errors.push({ name: lead.name, error: e.message });
      }
    }
    const failedNames = new Set(results.errors.map((e) => e.name));
    setLeads((prev) =>
      prev.filter(
        (l) =>
          !leadsToConvert
            .filter((lc) => !failedNames.has(lc.name))
            .map((lc) => lc.id)
            .includes(l.id),
      ),
    );
    setSelectedRows([]);
    setConverting(false);
    setConvertResult(results);
  };

  const handleWebsiteConvertToBooking = async () => {
    if (!selectedRows.length) return;
    setConverting(true);
    setConvertResult(null);
    try {
      const res = await fetch(`${API}/website-leads/convert-booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selectedRows }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const results = {
        bookingids: data.bookingids || [],
        errors: data.errors || [],
      };
      const failedIds = new Set((data.errors || []).map((e) => e.id));
      setProcessedWebsiteLeads((prev) =>
        prev.filter(
          (l) =>
            !selectedRows.filter((id) => !failedIds.has(id)).includes(l.id),
        ),
      );
      setSelectedRows([]);
      setConvertResult(results);
    } catch (e) {
      setConvertResult({
        bookingids: [],
        errors: [{ id: "all", error: e.message }],
      });
    } finally {
      setConverting(false);
    }
  };

  const handleBulkProcessWebsiteLeads = async () => {
    const toProcess = currentLeads.filter(
      (l) => l._isIncoming && selectedRows.includes(l.leadid),
    );
    if (!toProcess.length) return;
    setBulkProcessing(true);
    setBulkProcessResult(null);
    const results = { processed: [], errors: [] };
    for (const lead of toProcess) {
      try {
        const res = await fetch(`${API}/website-leads/process`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ leadid: lead.leadid }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { lead: savedLead } = await res.json();
        results.processed.push(lead.customername || lead.leadid);
        handleWebsiteLeadProcessed(savedLead);
      } catch (e) {
        results.errors.push({
          name: lead.customername || lead.leadid,
          error: e.message,
        });
      }
    }
    setSelectedRows([]);
    setBulkProcessing(false);
    setBulkProcessResult(results);
  };

  // ── Smart checkbox handler with toast guard ────────────────────────────────
  // For website "All Leads" view: prevent mixing processed and unprocessed selections
  const handleRowCheckboxChange = (id, isUnprocessed = false) => {
    if (storeTab === "website" && websiteActiveStatus === "All Leads") {
      const hasProcessed = selectedRows.some(
        (sid) => !currentLeads.find((l) => l._isIncoming && l.leadid === sid),
      );
      const hasUnprocessed = selectedRows.some((sid) =>
        currentLeads.find((l) => l._isIncoming && l.leadid === sid),
      );

      if (isUnprocessed && hasProcessed) {
        toast.info("Cannot select processed and unprocessed leads at once");
        return;
      }
      if (!isUnprocessed && hasUnprocessed) {
        toast.info("Cannot select processed and unprocessed leads at once");
        return;
      }
    }
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (
      selectedRows.length === currentLeads.length &&
      currentLeads.length > 0
    ) {
      setSelectedRows([]);
      return;
    }
    // In All Leads website view, only select same type as first already-selected row (or all if none selected)
    if (
      storeTab === "website" &&
      websiteActiveStatus === "All Leads" &&
      selectedRows.length > 0
    ) {
      const hasProcessed = selectedRows.some(
        (sid) => !currentLeads.find((l) => l._isIncoming && l.leadid === sid),
      );
      const hasUnprocessed = selectedRows.some((sid) =>
        currentLeads.find((l) => l._isIncoming && l.leadid === sid),
      );
      if (hasUnprocessed && !hasProcessed) {
        setSelectedRows(
          currentLeads.filter((l) => l._isIncoming).map((l) => l.leadid),
        );
        return;
      }
      if (hasProcessed && !hasUnprocessed) {
        setSelectedRows(
          currentLeads.filter((l) => !l._isIncoming).map((l) => l.id),
        );
        return;
      }
    }
    setSelectedRows(currentLeads.map((l) => l.id || l.leadid));
  };

  // Counts for stat cards
  const newMetaLeadsCount = leads.filter(
    (l) =>
      String(l.id).startsWith("meta_") &&
      !processedMetaIds.has(String(l.id).replace("meta_", "")),
  ).length;
  const facebookCount = leads.filter(
    (l) => l.channel === "Facebook Lead",
  ).length;
  const instagramCount = leads.filter(
    (l) => l.channel === "Instagram DM",
  ).length;
  const whatsappCount = leads.filter((l) => l.channel === "WhatsApp").length;
  const incomingWebsiteCount = websiteLeads.filter(
    (l) => !processedWebsiteIds.has(String(l.leadid).replace("WL-", "")),
  ).length;

  const activeLeadList =
    storeTab === "meta" ? visibleLeads : visibleWebsiteLeads;
  const totalPages = Math.ceil(activeLeadList.length / rowsPerPage);
  const indexOfFirst = (currentPage - 1) * rowsPerPage;
  const indexOfLast = indexOfFirst + rowsPerPage;
  const currentLeads = activeLeadList.slice(indexOfFirst, indexOfLast);

  const generatePageNumbers = () => {
    const pages = [];
    if (totalPages <= 3) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage > 2) {
        pages.push(1);
        pages.push("...");
      }
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        if (i > 0 && i <= totalPages) pages.push(i);
      }
      if (currentPage < totalPages - 1) {
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const isIncomingView = activeStatus === "Incoming Leads";
  const isAllLeadsView = activeStatus === "All Leads";
  const isWebsiteIncomingView = websiteActiveStatus === "Incoming Leads";

  const selectedUnprocessedWebsiteCount = currentLeads.filter(
    (l) =>
      selectedRows.includes(l.leadid) &&
      (l._isIncoming || isWebsiteIncomingView),
  ).length;
  const selectedProcessedWebsiteCount = selectedRows.filter(
    (id) => !currentLeads.find((l) => l._isIncoming && l.leadid === id),
  ).length;

  const paginationProps = {
    total: activeLeadList.length,
    from: indexOfFirst + 1,
    to: Math.min(indexOfLast, activeLeadList.length),
    currentPage,
    setCurrentPage,
    totalPages,
    pages: generatePageNumbers(),
  };

  if (error && storeTab === "meta")
    return (
      <div className="flex">
        <Menubar
          isOpen={isSidebarOpen}
          setIsOpen={setIsSidebarOpen}
          isMobile={isMobile}
        />
        <div className="flex h-64 flex-1 items-center justify-center p-6">
          <div className="text-lg text-red-500">
            {error}
            <button
              onClick={fetchLeads}
              className="ml-3 text-sm text-indigo-500 underline"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );

  //META PreBooking Mail
  const openMailConfirm = (lead, source = "meta") => {
    if (!lead.email) {
      toast.warning("This lead has no email address on file");
      return;
    }

    const details = buildInitialPropertyDetails(lead, source);
    const names = Object.keys(details);

    const missing = [];

    if (names.length > 0) {
      // Multi-property lead — check each property has required fields
      if (names.length === 0) missing.push("Property");
      names.forEach((name) => {
        const pd = details[name] || {};
        if (!pd.checkin) missing.push(`${name}: Check-in`);
        if (!pd.checkout) missing.push(`${name}: Check-out`);
      });
    } else {
      // Legacy single-property lead
      if (!(lead.propertyname || lead.property)) missing.push("Property");
      if (!(lead.checkin || lead.check_in)) missing.push("Check-in date");
      if (!(lead.checkout || lead.check_out)) missing.push("Check-out date");
    }

    if (missing.length) {
      toast.warning(`Add ${missing.join(", ")} before sending this email`);
      return;
    }

    setMailConfirmSource(source);
    setMailConfirmLead(lead);
  };

  const handleSendLeadMail = async () => {
    const lead = mailConfirmLead;
    if (!lead) return;

    setSendingMailId(lead.id);
    try {
      const endpoint =
        mailConfirmSource === "website"
          ? `${API}/processed-website-leads/${lead.id}/send-mail`
          : `${API}/leads/${lead.id}/send-mail`;
      const res = await fetch(endpoint, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || `HTTP ${res.status}`);
      }
      toast.success("Email sent successfully");
      setMailConfirmLead(null);
    } catch (e) {
      toast.error("Error sending email: " + e.message);
      setMailConfirmLead(null);
    } finally {
      setSendingMailId(null);
    }
  };
  const handleLeadPDF = () => {
    const selectedLeads = leads.filter((l) => selectedRows.includes(l.id));
    if (selectedLeads.length === 0) return;

    if (selectedLeads.length > 1) {
      toast.info(
        "If your browser blocks some tabs, allow pop-ups for this site.",
      );
    }

    selectedLeads.forEach((l, i) => {
      const url = `/preleadpdf/${l.id}?autoprint=1`;
      setTimeout(() => {
        const win = window.open(url, "_blank");
        if (!win)
          toast.info(`Pop-up blocked for lead ${l.id}. Please allow pop-ups.`);
      }, i * 150);
    });
  };
  const handleWebsiteLeadPDF = () => {
    const selectedLeads = processedWebsiteLeads.filter((l) =>
      selectedRows.includes(l.id),
    );
    if (selectedLeads.length === 0) {
      toast.info("Select processed leads to download a Pre-Booking PDF");
      return;
    }

    if (selectedLeads.length > 1) {
      toast.info(
        "If your browser blocks some tabs, allow pop-ups for this site.",
      );
    }

    selectedLeads.forEach((l, i) => {
      const url = `/prewebsiteleadpdf/${l.id}?autoprint=1`;
      setTimeout(() => {
        const win = window.open(url, "_blank");
        if (!win)
          toast.info(`Pop-up blocked for lead ${l.id}. Please allow pop-ups.`);
      }, i * 150);
    });
  };
  const isLeadMailReady = (lead) => {
    if (!lead.email) return false;

    const details = buildInitialPropertyDetails(
      lead,
      lead.original_lead_id !== undefined || lead.customer_name !== undefined
        ? "website"
        : "meta",
    );
    const names = Object.keys(details);

    // Multi-property: every selected property must have check-in and check-out
    if (names.length > 0) {
      return names.every((name) => {
        const pd = details[name] || {};
        return !!pd.checkin && !!pd.checkout;
      });
    }

    // Legacy single-property: flat columns
    return (
      !!(lead.propertyname || lead.property) &&
      !!(lead.checkin || lead.check_in) &&
      !!(lead.checkout || lead.check_out)
    );
  };

  return (
    <div className="flex min-h-screen bg-[#F8F9FA]">
      <Menubar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        isMobile={isMobile}
      />

      <main
        className={`min-w-0 flex-1 p-4 transition-all duration-500 sm:p-6 ${
          isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] flex-col gap-5">
          <Outlet />

          {/* Header */}
          <header className="flex flex-wrap items-center justify-between gap-3 pl-12 lg:pl-0">
            <h1 className="text-xl font-semibold text-gray-800">
              Inquire Leads
            </h1>

            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="group mr-2 flex h-8 w-8 items-center overflow-hidden rounded-full p-2 duration-300 hover:w-[120px] hover:border hover:border-[#1D2826] hover:shadow-[2px_2px_20px_rgba(0,0,0,0.08)] sm:hover:w-[250px]">
                <div className="flex items-center justify-center fill-[#1D2826]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                  >
                    <path d="M18.9,16.776A10.539,10.539,0,1,0,16.776,18.9l5.1,5.1L24,21.88ZM10.5,18A7.5,7.5,0,1,1,18,10.5,7.507,7.507,0,0,1,10.5,18Z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search name, phone…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full border-none bg-transparent px-2 text-sm font-normal outline-none focus:ring-0"
                />
              </div>

              {/* Filter */}
              <div className="relative inline-block" ref={filterRef}>
                <div
                  onClick={() => setShowPopup_filter((p) => !p)}
                  className={`group flex cursor-pointer select-none scale-[0.75] flex-col items-center gap-4 rounded-full border-2 px-2 py-2.5 transition-all duration-300 ${
                    showPopup_filter
                      ? "border-[#1D2826]"
                      : "border-transparent hover:border-[#1D2826]"
                  }`}
                >
                  {[
                    ["100%", "0%"],
                    ["0%", "70%"],
                    ["100%", "0%"],
                  ].map(([a, b], i) => (
                    <div
                      key={i}
                      className={`relative h-0.5 w-6 rounded-full bg-[#1D2826] ${i === 1 ? "-my-2.5" : ""}`}
                    >
                      <div
                        className="absolute top-1/2 h-2 w-2 -translate-y-1/2 rounded-full border-2 border-black bg-white transition-all duration-300"
                        style={{
                          left: showPopup_filter ? a : b,
                          transform: `translate(${showPopup_filter ? (i === 1 ? "0" : "-100%") : i === 1 ? "0" : "-0%"},-50%)`,
                        }}
                      />
                    </div>
                  ))}
                </div>

                {showPopup_filter && (
                  <div className="absolute right-0 top-full z-30 mt-1 w-[260px] space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-xl">
                    {storeTab === "meta" && (
                      <div className="flex flex-col gap-2">
                        <p className="text-xs font-semibold uppercase text-gray-500">
                          Channel
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {[
                            "All",
                            "Instagram DM",
                            "Facebook Lead",
                            "WhatsApp",
                          ].map((ch) => (
                            <button
                              key={ch}
                              onClick={() => setActiveChannel(ch)}
                              className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                                activeChannel === ch
                                  ? "bg-[#1D2826] text-white"
                                  : "bg-gray-100 hover:bg-[#DAE0CE]"
                              }`}
                            >
                              {ch}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {storeTab === "website" && (
                      <div className="flex flex-col gap-2">
                        <p className="text-xs font-semibold uppercase text-gray-500">
                          Lead type
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {["All", "Processed", "Unprocessed"].map((f) => (
                            <button
                              key={f}
                              onClick={() => setWebsiteFilter(f)}
                              className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                                websiteFilter === f
                                  ? "bg-[#1D2826] text-white"
                                  : "bg-gray-100 hover:bg-[#DAE0CE]"
                              }`}
                            >
                              {f}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <p className="text-xs font-semibold uppercase text-gray-500">
                        Date range
                      </p>
                      <div className="flex justify-between gap-2">
                        {[
                          { label: "Today", days: 0 },
                          { label: "Week", days: 7 },
                          { label: "Month", days: 30 },
                          { label: "Year", days: 365 },
                        ].map(({ label, days }) => (
                          <button
                            key={label}
                            onClick={() => {
                              const from = new Date();
                              from.setDate(from.getDate() - days);
                              setDateFrom(
                                days === 0
                                  ? new Date().toISOString().split("T")[0]
                                  : from.toISOString().split("T")[0],
                              );
                              setDateTo(new Date().toISOString().split("T")[0]);
                            }}
                            className="flex-1 rounded bg-gray-100 px-2 py-1 text-xs font-medium hover:bg-[#DAE0CE] active:bg-[#DAE0CE]"
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={dateFrom}
                          onChange={(e) => setDateFrom(e.target.value)}
                          className="w-full rounded border px-2 py-1 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-green-600"
                        />
                        <span className="text-sm text-gray-500">-</span>
                        <input
                          type="date"
                          value={dateTo}
                          onChange={(e) => setDateTo(e.target.value)}
                          className="w-full rounded border px-2 py-1 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-green-600"
                        />
                      </div>
                      {(dateFrom || dateTo) && (
                        <button
                          onClick={() => {
                            setDateFrom("");
                            setDateTo("");
                          }}
                          className="text-left text-xs text-gray-400 underline hover:text-gray-700"
                        >
                          Clear dates
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Notification bell */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setShowNotifDropdown((p) => !p)}
                  title="New leads"
                  className="relative cursor-pointer rounded-lg border-none p-2 text-xl"
                >
                  <IoNotificationsOutline />
                  {notifCount > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white">
                      {notifCount > 99 ? "99+" : notifCount}
                    </span>
                  )}
                </button>

                {showNotifDropdown && (
                  <div className="absolute right-0 top-full z-30 mt-1 w-56 rounded-xl border border-gray-200 bg-white p-4 text-xs shadow-xl">
                    <p className="mb-2 text-sm font-semibold text-gray-700">
                      {notifCount > 0
                        ? `${notifCount} new lead${notifCount > 1 ? "s" : ""}`
                        : "All caught up!"}
                    </p>
                    {notifCount > 0 && (
                      <>
                        <div className="flex justify-between border-b border-gray-100 py-1 text-gray-500">
                          <span>Meta leads</span>
                          <span className="font-bold text-gray-800">
                            {notifBreakdown.meta}
                          </span>
                        </div>
                        <div className="mb-3 flex justify-between py-1 text-gray-500">
                          <span>Website leads</span>
                          <span className="font-bold text-gray-800">
                            {notifBreakdown.website}
                          </span>
                        </div>
                        <button
                          onClick={markSeen}
                          className="w-full cursor-pointer rounded-lg bg-[#1D2826] py-1.5 text-xs font-semibold text-white"
                        >
                          Mark all seen
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={
                  storeTab === "meta"
                    ? fetchLeads
                    : () => {
                        fetchWebsiteLeads();
                        fetchProcessedWebsiteLeads();
                      }
                }
                title="Refresh"
                className="cursor-pointer rounded-lg border-none p-2 text-xl"
              >
                <TbRefresh />
              </button>

              {storeTab === "meta" && (
                <button
                  onClick={() => setShowCreate(true)}
                  className={`${ui.btn} ${ui.btnPrimary} hover:scale-95`}
                >
                  + New lead
                </button>
              )}
            </div>
          </header>

          {/* Stats */}
          <section
            className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${
              storeTab === "meta" ? "lg:grid-cols-4" : "lg:grid-cols-3"
            }`}
          >
            {storeTab === "meta" ? (
              <>
                <StatCard
                  label="Facebook leads"
                  value={facebookCount}
                  icon={<IoLogoFacebook />}
                />
                <StatCard
                  label="Instagram leads"
                  value={instagramCount}
                  icon={<RiInstagramFill />}
                />
                <StatCard
                  label="WhatsApp leads"
                  value={whatsappCount}
                  icon={<IoLogoWhatsapp />}
                />
                <StatCard
                  label="Unprocessed (Meta)"
                  value={newMetaLeadsCount}
                  icon={<TbDeviceAnalytics />}
                />
              </>
            ) : (
              <>
                <StatCard
                  label="Incoming"
                  value={incomingWebsiteCount}
                  icon={<RxOpenInNewWindow />}
                />
                <StatCard
                  label="Processed"
                  value={processedWebsiteLeads.length}
                  icon={<MdFileDownloadDone />}
                />
                <StatCard
                  label="Total leads (Website)"
                  value={incomingWebsiteCount + processedWebsiteLeads.length}
                  icon={<TbDeviceAnalytics />}
                />
              </>
            )}
          </section>

          {/* Tabs + pipeline in ONE card */}
          <section className={`${ui.card} flex flex-col gap-3 p-3`}>
            <div className="inline-flex w-fit overflow-hidden rounded-lg border border-gray-300 text-sm font-medium">
              {[
                ["meta", "Meta Leads"],
                ["website", "Website Leads"],
              ].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setStoreTab(k)}
                  className={`px-4 py-2 transition-colors ${
                    storeTab === k
                      ? "bg-[#1D2826] text-white"
                      : "bg-white text-gray-500 hover:bg-green-50"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <div className="flex overflow-x-auto pb-1 pr-2">
              {(storeTab === "meta"
                ? PIPELINE_STAGES
                : WEBSITE_PIPELINE_STAGES
              ).map((stage) => (
                <PipelineTab
                  key={stage.name}
                  stage={stage}
                  isActive={
                    (storeTab === "meta"
                      ? activeStatus
                      : websiteActiveStatus) === stage.status
                  }
                  onClick={() =>
                    (storeTab === "meta"
                      ? setActiveStatus
                      : setWebsiteActiveStatus)(stage.status)
                  }
                />
              ))}
            </div>
          </section>

          {/* Bulk action bar */}
          {selectedRows.length > 0 && (
            <div className="sticky top-2 z-20 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-2.5 shadow-md">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600">
                  {selectedRows.length} selected
                </span>
                <button
                  onClick={() => setSelectedRows([])}
                  className="cursor-pointer text-xs text-gray-400 underline hover:text-gray-700"
                >
                  Clear
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {storeTab === "meta" && (
                  <button
                    onClick={handleLeadPDF}
                    className={`${ui.btn} border border-blue-400 bg-blue-50 text-blue-600 hover:bg-blue-100`}
                  >
                    <FiDownload />
                    <span>Pre-Booking PDF ({selectedRows.length})</span>
                  </button>
                )}
                {storeTab === "website" && (
                  <button
                    onClick={handleWebsiteLeadPDF}
                    className={`${ui.btn} border border-blue-400 bg-blue-50 text-blue-600 hover:bg-blue-100`}
                  >
                    <FiDownload />
                    <span>Pre-Booking PDF ({selectedRows.length})</span>
                  </button>
                )}
                {((storeTab === "meta" && !isIncomingView) ||
                  (storeTab === "website" &&
                    !isWebsiteIncomingView &&
                    selectedProcessedWebsiteCount > 0)) && (
                  <button
                    onClick={() => setShowConvertModal(true)}
                    className={`${ui.btn} ${ui.btnPrimary}`}
                  >
                    Convert to booking
                  </button>
                )}
                {storeTab === "website" &&
                  selectedUnprocessedWebsiteCount > 0 && (
                    <button
                      onClick={() => setShowBulkProcessModal(true)}
                      className={`${ui.btn} bg-yellow-200 text-yellow-800 hover:bg-yellow-300`}
                    >
                      Process selected leads
                    </button>
                  )}
              </div>
            </div>
          )}

          {/* Tables */}
          {storeTab === "meta" && (
            <MetaLeadsTable
              leads={currentLeads}
              loading={loading}
              isIncomingView={isIncomingView}
              isAllLeadsView={isAllLeadsView}
              selectedRows={selectedRows}
              onSelectAll={handleSelectAll}
              onRowCheck={handleRowCheckboxChange}
              updatingId={updatingId}
              teamMembers={teamMembers}
              teamLoading={teamLoading}
              sendingMailId={sendingMailId}
              isLeadMailReady={isLeadMailReady}
              patchLead={patchLead}
              handleAssigneeChange={handleAssigneeChange}
              addRemark={addRemark}
              openLeadPanel={openLeadPanel}
              openMailConfirm={openMailConfirm}
              setDeleteTarget={setDeleteTarget}
              handleWhatsAppClick={handleWhatsAppClick}
              onMetaProcessed={handleMetaLeadProcessed}
              pagination={paginationProps}
            />
          )}

          {storeTab === "website" && (
            <WebsiteLeadsTable
              leads={currentLeads}
              loading={
                isWebsiteIncomingView ? websiteLoading : processedWebsiteLoading
              }
              error={websiteError}
              isWebsiteIncomingView={isWebsiteIncomingView}
              selectedRows={selectedRows}
              onSelectAll={handleSelectAll}
              onRowCheck={handleRowCheckboxChange}
              updatingId={updatingId}
              teamMembers={teamMembers}
              teamLoading={teamLoading}
              sendingMailId={sendingMailId}
              isLeadMailReady={isLeadMailReady}
              patchWebsiteLead={patchWebsiteLead}
              addWebsiteRemark={addWebsiteRemark}
              openLeadPanel={openLeadPanel}
              openMailConfirm={openMailConfirm}
              setDeleteTarget={setDeleteTarget}
              handleWhatsAppClick={handleWhatsAppClick}
              onWebsiteProcessed={handleWebsiteLeadProcessed}
              pagination={paginationProps}
            />
          )}
        </div>
      </main>

      <LeadSidePanel
        isOpen={isLeadPanelOpen}
        mode={leadPanelMode}
        formData={leadFormData}
        setFormData={setLeadFormData}
        onClose={closeLeadPanel}
        onSave={handleSaveLead}
        onSwitchToEdit={() => setLeadPanelMode("edit")}
        properties={properties}
        editingLead={editingLead}
        teamMembers={teamMembers}
      />

      {waPhoneTarget && (
        <WhatsAppPhoneModal
          leadName={
            waPhoneTarget.source === "website"
              ? waPhoneTarget.lead.customer_name
              : waPhoneTarget.lead.name
          }
          onSubmit={handleWaPhoneSubmit}
          onCancel={() => setWaPhoneTarget(null)}
        />
      )}
      {mailConfirmLead && (
        <SendLeadMailModal
          lead={mailConfirmLead}
          onConfirm={handleSendLeadMail}
          onCancel={() => setMailConfirmLead(null)}
          sending={sendingMailId === mailConfirmLead.id}
        />
      )}
      {followUpLead && (
        <FollowUpModal
          lead={followUpLead}
          onClose={() => setFollowUpLead(null)}
        />
      )}
      {showCreate && (
        <CreateLeadModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
          teamMembers={teamMembers}
        />
      )}

      {/* Convert confirm */}
      {showConvertModal && (
        <ModalShell
          title="Convert to booking"
          footer={
            <>
              <button
                onClick={() => setShowConvertModal(false)}
                disabled={converting}
                className={`${ui.btn} ${ui.btnGhost}`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowConvertModal(false);
                  storeTab === "meta"
                    ? await handleConvertToBooking()
                    : await handleWebsiteConvertToBooking();
                }}
                disabled={converting}
                className={`${ui.btn} ${ui.btnPrimary}`}
              >
                {converting ? "Converting…" : "Yes, convert"}
              </button>
            </>
          }
        >
          <p className="mb-3">
            Convert{" "}
            <b className="text-gray-800">
              {selectedRows.length} lead{selectedRows.length > 1 ? "s" : ""}
            </b>{" "}
            to booking{selectedRows.length > 1 ? "s" : ""}?
          </p>
          <p className="mb-4 text-xs text-amber-600">
            {storeTab === "website"
              ? "These website leads will be marked as converted and inserted into bookings."
              : "The leads will be removed from the leads table and inserted into bookings."}
          </p>
          <div className="max-h-40 overflow-y-auto rounded-lg bg-gray-50 px-3 py-2">
            {storeTab === "meta"
              ? leads
                  .filter((l) => selectedRows.includes(l.id))
                  .map((l) => (
                    <div
                      key={l.id}
                      className="flex items-center justify-between border-b border-gray-100 py-1.5 last:border-0"
                    >
                      <span className="text-xs font-semibold text-gray-700">
                        {l.name || "—"}
                      </span>
                      <span className="text-[11px] text-gray-400">
                        {channelToSource(l.channel)}
                      </span>
                    </div>
                  ))
              : processedWebsiteLeads
                  .filter((l) => selectedRows.includes(l.id))
                  .map((l) => (
                    <div
                      key={l.id}
                      className="flex items-center justify-between border-b border-gray-100 py-1.5 last:border-0"
                    >
                      <span className="text-xs font-semibold text-gray-700">
                        {l.customer_name || "—"}
                      </span>
                      <span className="text-[11px] text-gray-400">
                        {l.property || "Website"}
                      </span>
                    </div>
                  ))}
          </div>
        </ModalShell>
      )}

      {/* Convert result */}
      {convertResult && (
        <ModalShell
          title=""
          footer={
            <button
              onClick={() => setConvertResult(null)}
              className={`${ui.btn} ${ui.btnPrimary} w-full`}
            >
              Done
            </button>
          }
        >
          {convertResult.errors.length === 0 ? (
            <>
              <div className="mb-4 text-center">
                <div className="mb-2 text-4xl">✅</div>
                <div className="text-base font-bold text-gray-800">
                  {convertResult.bookingids.length} Booking
                  {convertResult.bookingids.length > 1 ? "s" : ""} created
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Successfully converted to bookings
                </p>
              </div>
              <div className="rounded-lg bg-green-50 px-3 py-2">
                {convertResult.bookingids.map((bid, i) => (
                  <div
                    key={i}
                    className="py-0.5 text-xs font-semibold text-green-700"
                  >
                    Booking ID: <span className="font-bold">{bid}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="mb-4 text-center">
                <div className="mb-2 text-4xl">⚠️</div>
                <div className="text-base font-bold text-gray-800">
                  Partial conversion
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  {convertResult.bookingids.length} succeeded ·{" "}
                  {convertResult.errors.length} failed
                </p>
              </div>
              {convertResult.bookingids.length > 0 && (
                <div className="mb-3 rounded-lg bg-green-50 px-3 py-2">
                  <p className="mb-1 text-[10px] font-bold uppercase text-green-600">
                    Created
                  </p>
                  {convertResult.bookingids.map((bid, i) => (
                    <div key={i} className="text-xs text-green-700">
                      Booking ID: <b>{bid}</b>
                    </div>
                  ))}
                </div>
              )}
              <div className="rounded-lg bg-red-50 px-3 py-2">
                <p className="mb-1 text-[10px] font-bold uppercase text-red-500">
                  Failed
                </p>
                {convertResult.errors.map((e, i) => (
                  <div key={i} className="text-xs text-red-600">
                    <b>{e.name || e.id}</b>: {e.error}
                  </div>
                ))}
              </div>
            </>
          )}
        </ModalShell>
      )}

      {/* Bulk process confirm */}
      {showBulkProcessModal && (
        <ModalShell
          title="Process leads"
          footer={
            <>
              <button
                onClick={() => setShowBulkProcessModal(false)}
                disabled={bulkProcessing}
                className={`${ui.btn} ${ui.btnGhost}`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowBulkProcessModal(false);
                  await handleBulkProcessWebsiteLeads();
                }}
                disabled={bulkProcessing}
                className={`${ui.btn} bg-green-600 text-white hover:bg-green-700`}
              >
                {bulkProcessing ? "Processing…" : "Yes, process"}
              </button>
            </>
          }
        >
          <p className="mb-3">
            Do you want to process{" "}
            <b className="text-gray-800">
              {selectedUnprocessedWebsiteCount} lead
              {selectedUnprocessedWebsiteCount > 1 ? "s" : ""}
            </b>
            ?
          </p>
          <p className="mb-4 text-xs text-green-600">
            The selected leads will be saved to the processed leads table and
            become manageable in the pipeline.
          </p>
          <div className="max-h-40 overflow-y-auto rounded-lg bg-gray-50 px-3 py-2">
            {currentLeads
              .filter((l) => l._isIncoming && selectedRows.includes(l.leadid))
              .map((l) => (
                <div
                  key={l.leadid}
                  className="flex items-center justify-between border-b border-gray-100 py-1.5 last:border-0"
                >
                  <span className="text-xs font-semibold text-gray-700">
                    {l.customername || "—"}
                  </span>
                  <span className="font-mono text-[11px] text-gray-400">
                    {l.leadid}
                  </span>
                </div>
              ))}
          </div>
        </ModalShell>
      )}

      {/* Bulk process result */}
      {bulkProcessResult && (
        <ModalShell
          title=""
          footer={
            <button
              onClick={() => setBulkProcessResult(null)}
              className={`${ui.btn} ${ui.btnPrimary} w-full`}
            >
              Done
            </button>
          }
        >
          {bulkProcessResult.errors.length === 0 ? (
            <>
              <div className="mb-4 text-center">
                <div className="mb-2 text-4xl">✅</div>
                <div className="text-base font-bold text-gray-800">
                  {bulkProcessResult.processed.length} Lead
                  {bulkProcessResult.processed.length > 1 ? "s" : ""} processed
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Now visible in the pipeline tabs
                </p>
              </div>
              <div className="rounded-lg bg-indigo-50 px-3 py-2">
                {bulkProcessResult.processed.map((name, i) => (
                  <div
                    key={i}
                    className="py-0.5 text-xs font-semibold text-indigo-700"
                  >
                    ✓ {name}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="mb-4 text-center">
                <div className="mb-2 text-4xl">⚠️</div>
                <div className="text-base font-bold text-gray-800">
                  Partial processing
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  {bulkProcessResult.processed.length} succeeded ·{" "}
                  {bulkProcessResult.errors.length} failed
                </p>
              </div>
              {bulkProcessResult.processed.length > 0 && (
                <div className="mb-3 rounded-lg bg-indigo-50 px-3 py-2">
                  <p className="mb-1 text-[10px] font-bold uppercase text-indigo-600">
                    Processed
                  </p>
                  {bulkProcessResult.processed.map((name, i) => (
                    <div key={i} className="text-xs text-indigo-700">
                      ✓ {name}
                    </div>
                  ))}
                </div>
              )}
              <div className="rounded-lg bg-red-50 px-3 py-2">
                <p className="mb-1 text-[10px] font-bold uppercase text-red-500">
                  Failed
                </p>
                {bulkProcessResult.errors.map((e, i) => (
                  <div key={i} className="text-xs text-red-600">
                    <b>{e.name}</b>: {e.error}
                  </div>
                ))}
              </div>
            </>
          )}
        </ModalShell>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />
      {deleteTarget && (
        <DeleteLeadModal
          leadName={deleteTarget.name || "this lead"}
          onConfirm={handleDeleteLead}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
};

export default InquireLeads;

//working code for inquire leads
// import React, { useState, useEffect, useRef, useCallback } from "react";
// import Menubar from "../Menubar/Menubar";
// import { Outlet } from "react-router-dom";
// import "../Dashboard/Dashboard.css";
// import { Tooltip } from "antd";
// import {
//   MdKeyboardDoubleArrowLeft,
//   MdKeyboardDoubleArrowRight,
//   MdFileDownloadDone,
//   MdDelete,
//   MdCancel,
// } from "react-icons/md";
// import { IoLogoFacebook, IoLogoWhatsapp } from "react-icons/io";
// import { RiInstagramFill, RiWhatsappFill } from "react-icons/ri";
// import { TbDeviceAnalytics, TbRefresh } from "react-icons/tb";
// import { AiFillInstagram } from "react-icons/ai";
// import {
//   FaFacebookSquare,
//   FaPhone,
//   FaCalendarAlt,
//   FaEdit,
//   FaEye,
// } from "react-icons/fa";
// import { IoMail, IoNotificationsOutline } from "react-icons/io5";
// import { IoIosTime, IoIosMail } from "react-icons/io";
// import { RxOpenInNewWindow } from "react-icons/rx";
// import Loader from "../Loader/Loader";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import { format } from "date-fns";
// import { FiDownload } from "react-icons/fi";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";

// const API = `${process.env.REACT_APP_BACKEND_URL || "https://dash.zorbastays.com"}/web-backend`;

// /* ═════════════════════════════════════════════════════════════════════════
//    UI TOKENS + SHARED PRESENTATIONAL COMPONENTS
//    ═════════════════════════════════════════════════════════════════════════ */
// const ui = {
//   card: "bg-white rounded-xl border border-gray-200 shadow-sm",
//   th: "px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap",
//   td: "px-4 py-3 align-middle",
//   input:
//     "w-full h-10 px-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-[#1D2826] focus:ring-1 focus:ring-[#1D2826]/20 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed",
//   textarea:
//     "w-full px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg resize-none focus:outline-none focus:border-[#1D2826] focus:ring-1 focus:ring-[#1D2826]/20 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed",
//   btn: "h-9 px-4 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5 whitespace-nowrap",
//   btnPrimary: "bg-[#1D2826] text-white hover:bg-[#2d3d3a]",
//   btnGhost: "bg-gray-100 text-gray-600 hover:bg-gray-200",
//   btnDanger: "bg-red-500 text-white hover:bg-red-600",
//   btnWhatsApp: "bg-[#25d366] text-white hover:bg-green-600",
//   sectionTitle:
//     "sm:col-span-2 text-xs font-bold uppercase tracking-wide text-gray-400",
// };

// /* Replaces the "relative top-2 ml-2 bg-white" floating-label hack */
// function Field({ label, className = "", children }) {
//   return (
//     <div className={`flex min-w-0 flex-col gap-1.5 ${className}`}>
//       <span className="text-xs font-medium text-gray-500">{label}</span>
//       {children}
//     </div>
//   );
// }

// /* One modal shell for every dialog → same padding, radius, mobile width */
// function ModalShell({ title, children, footer, maxWidth = "max-w-md" }) {
//   return (
//     <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
//       <div
//         className={`flex max-h-[90vh] w-full ${maxWidth} flex-col rounded-2xl bg-white shadow-2xl`}
//       >
//         <div className="px-6 pt-5 pb-3 text-base font-bold text-gray-800">
//           {title}
//         </div>
//         <div className="overflow-y-auto px-6 pb-5 text-sm text-gray-600">
//           {children}
//         </div>
//         {footer && (
//           <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4">
//             {footer}
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// function StatCard({ label, value, icon }) {
//   return (
//     <div
//       className={`${ui.card} flex items-center justify-between gap-4 px-4 py-3`}
//     >
//       <div>
//         <p className="text-xs font-medium text-gray-500">{label}</p>
//         <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
//       </div>
//       <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#3C4C4E] text-xl text-white">
//         {icon}
//       </div>
//     </div>
//   );
// }

// const ICON_TONES = {
//   sky: "border-sky-300 hover:text-sky-500",
//   yellow: "border-yellow-300 hover:text-yellow-600",
//   purple: "border-purple-300 hover:text-purple-500",
//   red: "border-red-300 hover:text-red-500",
//   green: "border-green-300 hover:text-green-600",
// };
// function IconButton({ tip, tone = "sky", disabled, onClick, children }) {
//   return (
//     <Tooltip title={tip} placement="top">
//       <button
//         type="button"
//         onClick={onClick}
//         disabled={disabled}
//         className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-gray-400 transition-all hover:scale-110 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100 ${
//           disabled ? "border-gray-200" : ICON_TONES[tone]
//         }`}
//       >
//         {children}
//       </button>
//     </Tooltip>
//   );
// }

// function Pagination({
//   total,
//   from,
//   to,
//   currentPage,
//   setCurrentPage,
//   totalPages,
//   pages,
// }) {
//   const nav =
//     "flex h-8 min-w-8 items-center justify-center px-2 text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent";
//   return (
//     <nav className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 bg-gray-50 px-4 py-2 md:flex-row">
//       <span className="text-xs text-gray-500">
//         Showing{" "}
//         <b className="text-gray-700">
//           {total ? from : 0}–{to}
//         </b>{" "}
//         of <b className="text-gray-700">{total}</b>
//       </span>
//       <ul className="flex items-center gap-1">
//         <li>
//           <button
//             className={`${nav} rounded`}
//             disabled={currentPage === 1}
//             onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
//           >
//             <MdKeyboardDoubleArrowLeft />
//           </button>
//         </li>
//         {pages.map((p, i) =>
//           p === "..." ? (
//             <li key={i} className="px-1 text-gray-400">
//               …
//             </li>
//           ) : (
//             <li key={i}>
//               <button
//                 onClick={() => setCurrentPage(p)}
//                 className={`${nav} rounded ${
//                   currentPage === p
//                     ? "bg-[#1D2826] text-white hover:bg-[#1D2826]"
//                     : ""
//                 }`}
//               >
//                 {p}
//               </button>
//             </li>
//           ),
//         )}
//         <li>
//           <button
//             className={`${nav} rounded`}
//             disabled={currentPage === totalPages || !totalPages}
//             onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
//           >
//             <MdKeyboardDoubleArrowRight />
//           </button>
//         </li>
//       </ul>
//     </nav>
//   );
// }

// function PipelineTab({ stage, isActive, onClick }) {
//   return (
//     <button
//       onClick={onClick}
//       className={`relative h-9 min-w-[96px] flex-1 shrink-0 whitespace-nowrap px-4 text-[11px] leading-tight transition-all ${stage.color} ${
//         isActive ? "font-bold" : "font-medium opacity-70 hover:opacity-100"
//       }`}
//       style={{
//         clipPath:
//           "polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)",
//         marginRight: "-6px",
//       }}
//     >
//       {stage.name}
//       {isActive && (
//         <span className="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-current" />
//       )}
//     </button>
//   );
// }

// /* ═════════════════════════════════════════════════════════════════════════
//    DOMAIN HELPERS (unchanged logic)
//    ═════════════════════════════════════════════════════════════════════════ */
// const calcNights = (checkin, checkout) => {
//   if (!checkin || !checkout) return 0;
//   const ci = new Date(checkin);
//   const co = new Date(checkout);
//   if (isNaN(ci) || isNaN(co)) return 0;
//   return Math.max(0, Math.round((co - ci) / (1000 * 60 * 60 * 24)));
// };

// const emptyPropertyDetail = () => ({
//   destination: "",
//   guests: "",
//   bedrooms: "",
//   inclusions: "",
//   checkin: "",
//   checkout: "",
//   checkin_time: "12:00 PM",
//   checkout_time: "11:00 AM",
//   checkinHour: "12",
//   checkinMinute: "00",
//   checkinAmPm: "PM",
//   checkoutHour: "11",
//   checkoutMinute: "00",
//   checkoutAmPm: "AM",
//   proprice: "",
//   secdep: "",
//   advpay: "",
//   balpay: "",
//   totpay: "",
// });

// const recalcPropertyPricing = (detail) => {
//   const nights = calcNights(detail.checkin, detail.checkout);
//   const proprice = parseFloat(detail.proprice) || 0;
//   return { ...detail, totpay: (nights * proprice).toFixed(2) };
// };

// const getSelectedProperties = (formData) =>
//   (formData.propertyname || "")
//     .split(",")
//     .map((s) => s.trim())
//     .filter(Boolean);

// // Whitespace/case-insensitive key so " Villa  Coco " and "Villa Coco" are the same property
// const normKey = (s) =>
//   String(s || "")
//     .replace(/\s+/g, " ")
//     .trim()
//     .toLowerCase();

// // How many meaningful fields a property-detail entry has (used to pick the "real" duplicate)
// const countFilled = (d) =>
//   [
//     "destination",
//     "checkin",
//     "checkout",
//     "proprice",
//     "secdep",
//     "advpay",
//     "balpay",
//     "totpay",
//     "inclusions",
//   ].filter((f) => d?.[f] !== "" && d?.[f] != null).length;

// // Rebuilds propertydetails so it only contains the properties listed in `names`,
// // one entry each (stale / whitespace-variant duplicates are dropped).
// const reconcilePropertyDetails = (details, names) => {
//   if (!names.length) return details;
//   const out = {};
//   names.forEach((w) => {
//     const matches = Object.entries(details)
//       .filter(([k]) => normKey(k) === normKey(w))
//       .map(([, v]) => v);
//     if (matches.length)
//       out[w] = matches.reduce((best, cur) =>
//         countFilled(cur) > countFilled(best) ? cur : best,
//       );
//   });
//   return Object.keys(out).length ? out : details;
// };

// const STATUSES = [
//   "New Enquiry",
//   "Call Not Picked",
//   "Contacted",
//   "Qualified",
//   "Plan & Quote Sent",
//   "In Pipeline",
//   "Negotiating",
//   "Awaiting Payment",
//   "Lost & Closed",
// ];
// const STATUS_COLORS = {
//   "New Enquiry": "#6366f1",
//   "Call Not Picked": "#f59e0b",
//   Contacted: "#3b82f6",
//   Qualified: "#8b5cf6",
//   "Plan & Quote Sent": "#06b6d4",
//   "In Pipeline": "#10b981",
//   Negotiating: "#f97316",
//   "Awaiting Payment": "#ec4899",
//   Booked: "#22c55e",
//   "Lost & Closed": "#ef4444",
// };
// const PIPELINE_STAGES = [
//   {
//     name: "Incoming",
//     color: "bg-emerald-100 text-emerald-600",
//     status: "Incoming Leads",
//   },
//   {
//     name: "Processed",
//     color: "bg-yellow-100 text-yellow-600",
//     status: "All Processed Leads",
//   },
//   {
//     name: "New Enquiry",
//     color: "bg-purple-100 text-purple-500",
//     status: "New Enquiry",
//   },
//   {
//     name: "Call Not Picked",
//     color: "bg-sky-100 text-sky-600",
//     status: "Call Not Picked",
//   },
//   {
//     name: "Contacted",
//     color: "bg-pink-100 text-pink-600",
//     status: "Contacted",
//   },
//   {
//     name: "Qualified",
//     color: "bg-teal-100 text-teal-700",
//     status: "Qualified",
//   },
//   {
//     name: "Plan/Quote Sent",
//     color: "bg-red-100 text-red-500",
//     status: "Plan & Quote Sent",
//   },
//   {
//     name: "In Pipeline",
//     color: "bg-blue-100 text-blue-600",
//     status: "In Pipeline",
//   },
//   {
//     name: "Negotiating",
//     color: "bg-lime-100 text-lime-700",
//     status: "Negotiating",
//   },
//   {
//     name: "Awaiting Payment",
//     color: "bg-green-100 text-green-700",
//     status: "Awaiting Payment",
//   },
//   {
//     name: "Lost & Closed",
//     color: "bg-slate-200 text-slate-700",
//     status: "Lost & Closed",
//   },
// ];
// const WEBSITE_STATUSES = [
//   "New Lead",
//   "Contacted",
//   "Qualified",
//   "Plan & Quote Sent",
//   "In Pipeline",
//   "Negotiating",
//   "Awaiting Payment",
//   "Lost & Closed",
// ];
// const WEBSITE_STATUS_COLORS = {
//   "New Lead": "#6366f1",
//   Contacted: "#3b82f6",
//   Qualified: "#8b5cf6",
//   "Plan & Quote Sent": "#06b6d4",
//   "In Pipeline": "#10b981",
//   Negotiating: "#f97316",
//   "Awaiting Payment": "#ec4899",
//   "Lost & Closed": "#ef4444",
// };
// const WEBSITE_PIPELINE_STAGES = [
//   { name: "All", color: "bg-orange-100 text-orange-600", status: "All Leads" },
//   {
//     name: "Incoming",
//     color: "bg-emerald-100 text-emerald-600",
//     status: "Incoming Leads",
//   },
//   {
//     name: "Processed",
//     color: "bg-yellow-100 text-yellow-600",
//     status: "All Processed Leads",
//   },
//   {
//     name: "New Lead",
//     color: "bg-purple-100 text-purple-500",
//     status: "New Lead",
//   },
//   {
//     name: "Contacted",
//     color: "bg-pink-100 text-pink-600",
//     status: "Contacted",
//   },
//   {
//     name: "Qualified",
//     color: "bg-teal-100 text-teal-700",
//     status: "Qualified",
//   },
//   {
//     name: "Plan/Quote Sent",
//     color: "bg-red-100 text-red-500",
//     status: "Plan & Quote Sent",
//   },
//   {
//     name: "In Pipeline",
//     color: "bg-blue-100 text-blue-600",
//     status: "In Pipeline",
//   },
//   {
//     name: "Negotiating",
//     color: "bg-lime-100 text-lime-700",
//     status: "Negotiating",
//   },
//   {
//     name: "Awaiting Payment",
//     color: "bg-green-100 text-green-700",
//     status: "Awaiting Payment",
//   },
//   {
//     name: "Lost & Closed",
//     color: "bg-slate-200 text-slate-700",
//     status: "Lost & Closed",
//   },
// ];
// const CHANNELS = {
//   "Instagram DM": {
//     color: "#e1306c",
//     bg: "#fce4ec",
//     icon: <AiFillInstagram />,
//   },
//   "Facebook Lead": {
//     color: "#1877f2",
//     bg: "#e3f2fd",
//     icon: <FaFacebookSquare />,
//   },
//   WhatsApp: { color: "#25d366", bg: "#e8f5e9", icon: <RiWhatsappFill /> },
// };
// const TAG_COLORS = { Hot: "#ef4444", Warm: "#f97316", Cold: "#64748b" };

// function timeAgo(dateStr) {
//   if (!dateStr) return "—";
//   const diff = (Date.now() - new Date(dateStr)) / 1000;
//   if (diff < 60) return "Just now";
//   if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
//   if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
//   return `${Math.floor(diff / 86400)}d ago`;
// }
// function channelToSource(channel) {
//   return (
//     {
//       "Facebook Lead": "Facebook",
//       "Instagram DM": "Instagram",
//       WhatsApp: "WhatsApp",
//     }[channel] ||
//     channel ||
//     "Lead"
//   );
// }

// // ─── WhatsApp follow-up helpers ───────────────────────────────────────────────
// // Digits only; strips leading 0s / 00; 10-digit numbers are assumed Indian (+91)
// const normalizePhone = (raw) => {
//   let d = String(raw || "")
//     .replace(/\D/g, "")
//     .replace(/^0+/, "");
//   if (d.length === 10) d = "91" + d;
//   return d;
// };

// const isValidPhone = (raw) => {
//   const d = normalizePhone(raw);
//   // Indian: 91 + 10 digits, national number must start with 6-9
//   if (d.startsWith("91")) return d.length === 12 && /^[6-9]/.test(d[2]);
//   // Other countries: E.164 allows up to 15 digits
//   return d.length >= 11 && d.length <= 15;
// };
// const fmtWaDate = (val) => {
//   if (!val) return "";
//   let d;
//   if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
//     const [y, m, day] = val.split("-").map(Number);
//     d = new Date(y, m - 1, day);
//   } else d = new Date(val);
//   return isNaN(d) ? "" : format(d, "dd MMM yyyy");
// };

// const fmtINR = (v) => `₹${(parseFloat(v) || 0).toLocaleString("en-IN")}`;

// const buildWhatsAppMessage = (
//   customerName,
//   details,
//   propertiesList,
//   brochureMap,
// ) => {
//   const names = Object.keys(details);
//   const first =
//     String(customerName || "")
//       .trim()
//       .split(/\s+/)[0] || "there";

//   const optionBlocks = names.map((name, i) => {
//     const d = details[name] || {};
//     const meta = (propertiesList || []).find(
//       (p) => p.propertyname?.trim().normalize() === name.trim().normalize(),
//     );

//     if (!meta) {
//       console.warn(
//         `[WhatsApp] No property match found in properties list for "${name}"`,
//       );
//     }

//     const nights = calcNights(d.checkin, d.checkout);
//     const ci = fmtWaDate(d.checkin) || "—";
//     const co = fmtWaDate(d.checkout) || "—";
//     const maxGuests = meta?.maxadult ?? d.guests ?? "—";
//     const pdfLink = brochureMap?.[name] || "";

//     const lines = [
//       `*OPTION ${i + 1} — ${name}*`,
//       `📍 ${d.destination || "—"} · ${d.bedrooms || "—"} Bedrooms · Up to ${maxGuests} Guests`,
//       ``,
//       `*${ci} – ${co}* · ${nights} Night${nights === 1 ? "" : "s"}`,
//       `*${fmtINR(d.proprice)} / night*`,
//       `*Total Stay: ${fmtINR(d.totpay)}*`,
//       ``,
//       `Advance: ${fmtINR(d.advpay)}`,
//       `Balance: ${fmtINR(d.balpay)}`,
//       `Security Deposit: ${fmtINR(d.secdep)}`,
//       ``,
//       `✨ *Inclusions:* ${d.inclusions || "—"}`,
//     ];

//     if (pdfLink) {
//       lines.push(``, `📄 *View Property Brochure →*`, pdfLink);
//     } else {
//       console.warn(`[WhatsApp] No brochure URL resolved for "${name}"`);
//     }

//     return lines.join("\n");
//   });

//   return [
//     `Hello ${first}, 👋`,
//     ``,
//     `Greetings from *Zorba Stays*!`,
//     ``,
//     `Based on your requirements, we've shortlisted these stays for you:`,
//     ``,
//     optionBlocks.join("\n\n──────────────\n\n"),
//     ``,
//     `Please have a look at the options and brochures, and let us know which stay you'd like to explore further. We're happy to assist with availability, booking and any questions you may have. 😊`,
//     ``,
//     `Warm regards,`,
//     `*Team Zorba Stays*`,
//   ].join("\n");
// };

// // Fetches propertybrochure for each property from /admin/properties/:id,
// // since the trimmed /properties list doesn't carry that field.
// const fetchPropertyBrochures = async (names, propertiesList) => {
//   const brochureMap = {};
//   await Promise.all(
//     names.map(async (name) => {
//       const meta = (propertiesList || []).find(
//         (p) => p.propertyname?.trim().normalize() === name.trim().normalize(),
//       );
//       if (!meta) return;

//       if (meta.propertybrochure) {
//         brochureMap[name] = meta.propertybrochure;
//         return;
//       }
//       if (!meta.id) return;

//       try {
//         const res = await fetch(`${API}/admin/properties/${meta.id}`);
//         if (!res.ok) return;
//         const full = await res.json();
//         if (full.propertybrochure) brochureMap[name] = full.propertybrochure;
//       } catch (e) {
//         console.warn(
//           `[WhatsApp] Failed to fetch brochure for "${name}":`,
//           e.message,
//         );
//       }
//     }),
//   );
//   return brochureMap;
// };

// /* ═════════════════════════════════════════════════════════════════════════
//    MODALS
//    ═════════════════════════════════════════════════════════════════════════ */
// function ConfirmProcessModal({ onConfirm, onCancel }) {
//   return (
//     <ModalShell
//       title="Process lead?"
//       footer={
//         <>
//           <button onClick={onCancel} className={`${ui.btn} ${ui.btnGhost}`}>
//             Cancel
//           </button>
//           <button onClick={onConfirm} className={`${ui.btn} ${ui.btnPrimary}`}>
//             Yes, process
//           </button>
//         </>
//       }
//     >
//       This will save the lead to the database. Do you want to continue?
//     </ModalShell>
//   );
// }

// function SendLeadMailModal({ lead, onConfirm, onCancel, sending }) {
//   const leadName =
//     `${lead.firstname || lead.name || ""} ${lead.lastname || ""}`.trim() ||
//     lead.customer_name ||
//     "this lead";
//   return (
//     <ModalShell
//       title="Send pre-booking mail?"
//       footer={
//         <>
//           <button
//             onClick={onCancel}
//             disabled={sending}
//             className={`${ui.btn} ${ui.btnGhost}`}
//           >
//             Cancel
//           </button>
//           <button
//             onClick={onConfirm}
//             disabled={sending}
//             className={`${ui.btn} ${ui.btnPrimary}`}
//           >
//             {sending ? (
//               <>
//                 <svg
//                   className="h-4 w-4 animate-spin text-white"
//                   xmlns="http://www.w3.org/2000/svg"
//                   fill="none"
//                   viewBox="0 0 24 24"
//                 >
//                   <circle
//                     className="opacity-25"
//                     cx="12"
//                     cy="12"
//                     r="10"
//                     stroke="currentColor"
//                     strokeWidth="4"
//                   />
//                   <path
//                     className="opacity-75"
//                     fill="currentColor"
//                     d="M4 12a8 8 0 018-8v8z"
//                   />
//                 </svg>
//                 Sending…
//               </>
//             ) : (
//               "Yes, send"
//             )}
//           </button>
//         </>
//       }
//     >
//       Do you want to send the "Pre Booking" mail to{" "}
//       <b className="text-gray-800">{leadName}</b>?
//     </ModalShell>
//   );
// }

// function DeleteLeadModal({ leadName, onConfirm, onCancel, deleting }) {
//   return (
//     <ModalShell
//       title="Delete lead?"
//       footer={
//         <>
//           <button
//             onClick={onCancel}
//             disabled={deleting}
//             className={`${ui.btn} ${ui.btnGhost}`}
//           >
//             Cancel
//           </button>
//           <button
//             onClick={onConfirm}
//             disabled={deleting}
//             className={`${ui.btn} ${ui.btnDanger}`}
//           >
//             {deleting ? "Deleting…" : "Yes, delete"}
//           </button>
//         </>
//       }
//     >
//       <p>
//         Are you sure you want to delete{" "}
//         <b className="text-gray-800">{leadName}</b>?
//       </p>
//       <p className="mt-1 text-xs text-red-500">This action cannot be undone.</p>
//     </ModalShell>
//   );
// }

// function WhatsAppPhoneModal({ leadName, onSubmit, onCancel }) {
//   const [phone, setPhone] = useState("");
//   const [err, setErr] = useState("");

//   const submit = () => {
//     if (!isValidPhone(phone)) {
//       setErr("Enter a valid phone number (with country code if not Indian)");
//       return;
//     }
//     onSubmit(phone.trim());
//   };

//   return (
//     <ModalShell
//       title="Phone number missing"
//       footer={
//         <>
//           <button onClick={onCancel} className={`${ui.btn} ${ui.btnGhost}`}>
//             Cancel
//           </button>
//           <button onClick={submit} className={`${ui.btn} ${ui.btnWhatsApp}`}>
//             Save & send
//           </button>
//         </>
//       }
//     >
//       <p className="mb-3">
//         Enter the number for <b className="text-gray-800">{leadName}</b> to send
//         the property details on WhatsApp.
//       </p>
//       <input
//         autoFocus
//         type="tel"
//         value={phone}
//         onChange={(e) => {
//           setPhone(e.target.value);
//           setErr("");
//         }}
//         onKeyDown={(e) => e.key === "Enter" && submit()}
//         placeholder="e.g. 9876543210"
//         className={ui.input}
//       />
//       {err && <p className="mt-1.5 text-xs text-red-500">{err}</p>}
//     </ModalShell>
//   );
// }

// // ─── Process buttons ──────────────────────────────────────────────────────────
// const processBtnCls = (processing) =>
//   `h-8 px-3 rounded-md text-white text-xs font-bold whitespace-nowrap transition-all ${
//     processing
//       ? "bg-gray-400 cursor-not-allowed"
//       : "bg-[#aeb3a4] hover:bg-[#797d72] cursor-pointer"
//   }`;

// function ProcessMetaLeadButton({ lead, onProcessed }) {
//   const [processing, setProcessing] = useState(false);
//   const [done, setDone] = useState(false);
//   const [showConfirm, setShowConfirm] = useState(false);

//   const handleProcess = async () => {
//     setProcessing(true);
//     try {
//       const meta_lead_id = lead.id.startsWith("meta_")
//         ? lead.id.replace("meta_", "")
//         : lead.id;
//       const res = await fetch(`${API}/leads/process-meta`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           meta_lead_id,
//           name: lead.name,
//           phone: lead.phone || "",
//           email: lead.email || "",
//           channel: lead.channel || "Facebook Lead",
//           enquiry: lead.enquiry || "",
//           form_id: lead.form_id || "",
//           page_id: lead.page_id || "",
//           field_data: lead.field_data || [],
//           raw_payload: lead,
//         }),
//       });
//       if (!res.ok) throw new Error("Failed " + res.status);
//       const { lead: savedLead, already_existed } = await res.json();
//       setDone(true);
//       onProcessed(savedLead, already_existed);
//     } catch (e) {
//       alert("Error processing lead: " + e.message);
//     } finally {
//       setProcessing(false);
//     }
//   };

//   if (done)
//     return (
//       <span className="text-xs font-bold text-green-600">✓ Saved to DB</span>
//     );

//   return (
//     <>
//       {showConfirm && (
//         <ConfirmProcessModal
//           onConfirm={() => {
//             setShowConfirm(false);
//             handleProcess();
//           }}
//           onCancel={() => setShowConfirm(false)}
//         />
//       )}
//       <button
//         onClick={() => setShowConfirm(true)}
//         disabled={processing}
//         className={processBtnCls(processing)}
//       >
//         {processing ? "Saving…" : "Process lead"}
//       </button>
//     </>
//   );
// }

// function ProcessWebsiteLeadButton({ lead, onProcessed }) {
//   const [processing, setProcessing] = useState(false);
//   const [done, setDone] = useState(false);
//   const [showConfirm, setShowConfirm] = useState(false);

//   const handleProcess = async () => {
//     setProcessing(true);
//     try {
//       const res = await fetch(`${API}/website-leads/process`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ leadid: lead.leadid }),
//       });
//       if (!res.ok) throw new Error("Failed " + res.status);
//       const { lead: savedLead, already_existed } = await res.json();
//       setDone(true);
//       onProcessed(savedLead, already_existed);
//     } catch (e) {
//       alert("Error processing lead: " + e.message);
//     } finally {
//       setProcessing(false);
//     }
//   };

//   if (done)
//     return (
//       <span className="text-xs font-bold text-green-600">✓ Saved to DB</span>
//     );

//   return (
//     <>
//       {showConfirm && (
//         <ConfirmProcessModal
//           onConfirm={() => {
//             setShowConfirm(false);
//             handleProcess();
//           }}
//           onCancel={() => setShowConfirm(false)}
//         />
//       )}
//       <button
//         onClick={() => setShowConfirm(true)}
//         disabled={processing}
//         className={processBtnCls(processing)}
//       >
//         {processing ? "Saving…" : "Process lead"}
//       </button>
//     </>
//   );
// }

// // ─── Follow Up Modal ──────────────────────────────────────────────────────────
// function FollowUpModal({ lead, onClose }) {
//   const [type, setType] = useState("whatsapp");
//   const [msg, setMsg] = useState("");
//   const [sent, setSent] = useState(false);
//   const send = () => {
//     setSent(true);
//     setTimeout(onClose, 1600);
//   };
//   if (sent)
//     return (
//       <ModalShell title="">
//         <div className="py-4 text-center">
//           <div className="text-5xl">✅</div>
//           <div className="mt-3 text-base font-bold text-gray-800">
//             Follow-up sent!
//           </div>
//         </div>
//       </ModalShell>
//     );
//   return (
//     <ModalShell
//       title="Send follow-up"
//       footer={
//         <>
//           <button onClick={onClose} className={`${ui.btn} ${ui.btnGhost}`}>
//             Cancel
//           </button>
//           <button
//             onClick={send}
//             disabled={!msg}
//             className={`${ui.btn} ${ui.btnWhatsApp}`}
//           >
//             Send
//           </button>
//         </>
//       }
//     >
//       <p className="mb-4 text-xs text-gray-400">
//         To: <b className="text-gray-700">{lead.name || lead.customername}</b>
//         {lead.phone ? ` · ${lead.phone}` : ""}
//       </p>
//       <div className="mb-4 grid grid-cols-2 gap-2">
//         {["whatsapp", "email"].map((t) => (
//           <button
//             key={t}
//             onClick={() => setType(t)}
//             className={`h-10 rounded-lg border-2 text-sm font-semibold transition-all ${
//               type === t
//                 ? "border-indigo-500 bg-indigo-50 text-indigo-600"
//                 : "border-gray-200 bg-white text-gray-500"
//             }`}
//           >
//             {t === "whatsapp" ? "📱 WhatsApp" : "✉️ Email"}
//           </button>
//         ))}
//       </div>
//       <textarea
//         value={msg}
//         onChange={(e) => setMsg(e.target.value)}
//         rows={4}
//         placeholder={`Write your ${type} message…`}
//         className={ui.textarea}
//       />
//     </ModalShell>
//   );
// }

// // ─── Create Lead Modal ────────────────────────────────────────────────────────
// function CreateLeadModal({ onClose, onCreated, teamMembers = [] }) {
//   const [form, setForm] = useState({
//     name: "",
//     phone: "",
//     email: "",
//     channel: "Instagram DM",
//     enquiry: "",
//     tag: "Warm",
//     assignee: "Unassigned",
//   });
//   const [saving, setSaving] = useState(false);
//   const [err, setErr] = useState("");
//   const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
//   const handleSubmit = async () => {
//     if (!form.name.trim()) {
//       setErr("Name is required");
//       return;
//     }
//     setSaving(true);
//     setErr("");
//     try {
//       const res = await fetch(`${API}/leads`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(form),
//       });
//       if (!res.ok) throw new Error("Server error " + res.status);
//       onCreated(await res.json());
//       onClose();
//     } catch (e) {
//       setErr(e.message);
//     } finally {
//       setSaving(false);
//     }
//   };
//   return (
//     <ModalShell
//       title="➕ Add new lead"
//       maxWidth="max-w-lg"
//       footer={
//         <>
//           <button onClick={onClose} className={`${ui.btn} ${ui.btnGhost}`}>
//             Cancel
//           </button>
//           <button
//             onClick={handleSubmit}
//             disabled={saving}
//             className={`${ui.btn} ${ui.btnPrimary}`}
//           >
//             {saving ? "Saving…" : "Create lead"}
//           </button>
//         </>
//       }
//     >
//       <div className="space-y-3">
//         <Field label="Full name *">
//           <input
//             className={ui.input}
//             value={form.name}
//             onChange={(e) => set("name", e.target.value)}
//           />
//         </Field>
//         <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
//           <Field label="Phone number">
//             <input
//               className={ui.input}
//               value={form.phone}
//               onChange={(e) => set("phone", e.target.value)}
//             />
//           </Field>
//           <Field label="Email">
//             <input
//               className={ui.input}
//               type="email"
//               value={form.email}
//               onChange={(e) => set("email", e.target.value)}
//             />
//           </Field>
//         </div>
//         <Field label="Channel">
//           <select
//             className={ui.input}
//             value={form.channel}
//             onChange={(e) => set("channel", e.target.value)}
//           >
//             {Object.keys(CHANNELS).map((c) => (
//               <option key={c}>{c}</option>
//             ))}
//           </select>
//         </Field>
//         <Field label="Enquiry / message">
//           <textarea
//             className={ui.textarea}
//             rows={3}
//             value={form.enquiry}
//             onChange={(e) => set("enquiry", e.target.value)}
//           />
//         </Field>
//         <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
//           <Field label="Tag">
//             <select
//               className={ui.input}
//               value={form.tag}
//               onChange={(e) => set("tag", e.target.value)}
//             >
//               {["Hot", "Warm", "Cold"].map((t) => (
//                 <option key={t}>{t}</option>
//               ))}
//             </select>
//           </Field>
//           <Field label="Assigned to">
//             <select
//               className={ui.input}
//               value={form.assignee}
//               onChange={(e) => set("assignee", e.target.value)}
//             >
//               {(teamMembers.length ? teamMembers : ["Unassigned"]).map((t) => (
//                 <option key={t}>{t}</option>
//               ))}
//             </select>
//           </Field>
//         </div>
//         {err && <div className="text-xs text-red-500">⚠ {err}</div>}
//       </div>
//     </ModalShell>
//   );
// }

// /* ═════════════════════════════════════════════════════════════════════════
//    LEAD SIDE PANEL
//    ═════════════════════════════════════════════════════════════════════════ */
// function TimeSelects({ prefix, detail, onChange, disabled }) {
//   const cls = `${ui.input} px-2`;
//   return (
//     <div className="grid grid-cols-3 gap-2">
//       <select
//         name={`${prefix}Hour`}
//         value={detail[`${prefix}Hour`]}
//         onChange={onChange}
//         disabled={disabled}
//         className={cls}
//       >
//         {Array.from({ length: 12 }, (_, i) =>
//           String(i + 1).padStart(2, "0"),
//         ).map((h) => (
//           <option key={h} value={h}>
//             {h}
//           </option>
//         ))}
//       </select>
//       <select
//         name={`${prefix}Minute`}
//         value={detail[`${prefix}Minute`]}
//         onChange={onChange}
//         disabled={disabled}
//         className={cls}
//       >
//         {["00", "15", "30", "45"].map((m) => (
//           <option key={m} value={m}>
//             {m}
//           </option>
//         ))}
//       </select>
//       <select
//         name={`${prefix}AmPm`}
//         value={detail[`${prefix}AmPm`]}
//         onChange={onChange}
//         disabled={disabled}
//         className={cls}
//       >
//         <option value="AM">AM</option>
//         <option value="PM">PM</option>
//       </select>
//     </div>
//   );
// }

// function LeadSidePanel({
//   isOpen,
//   mode,
//   formData,
//   setFormData,
//   onClose,
//   onSave,
//   onSwitchToEdit,
//   properties,
//   editingLead,
//   teamMembers = [],
// }) {
//   const isViewOnly = mode === "view";

//   // Simple text/select fields
//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => {
//       const updated = { ...prev, [name]: value };
//       if (name === "country") {
//         updated.state = "";
//         updated.city = "";
//       } else if (name === "state") {
//         updated.city = "";
//       }
//       return updated;
//     });
//   };

//   // ── Property multi-select state ────────────────────────────────────
//   const [isPropOpen, setIsPropOpen] = useState(false);
//   const [propSearch, setPropSearch] = useState("");
//   const [manualMode, setManualMode] = useState(false);
//   const [manualProperty, setManualProperty] = useState("");
//   const [worldData, setWorldData] = useState([]);
//   const dropdownRef = useRef(null);

//   // { [propertyName]: { db: [...], airbnb: [...] } }
//   const [blockedDatesMap, setBlockedDatesMap] = useState({});

//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
//         setIsPropOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   useEffect(() => {
//     fetch("/world.json")
//       .then((res) => res.json())
//       .then((data) => setWorldData(data))
//       .catch((err) => console.error("Error loading world.json:", err));
//   }, []);

//   useEffect(() => {
//     if (!isOpen) {
//       setIsPropOpen(false);
//       setManualMode(false);
//       setPropSearch("");
//       setManualProperty("");
//     }
//   }, [isOpen]);

//   // Find selected country/state/city objects
//   const selectedCountry = worldData.find((c) => c.name === formData.country);
//   const states = selectedCountry?.states || [];
//   const selectedState = states.find((s) => s.name === formData.state);
//   const cities = selectedState?.cities || [];

//   const selectedProperties = getSelectedProperties(formData);

//   // Fetch blocked dates for any newly-selected property we haven't fetched yet
//   useEffect(() => {
//     selectedProperties.forEach((prop) => {
//       if (blockedDatesMap[prop] !== undefined) return;
//       fetch(`${API}/blocked-dates/${encodeURIComponent(prop)}`)
//         .then((res) => res.json())
//         .then((data) => {
//           setBlockedDatesMap((prev) => ({
//             ...prev,
//             [prop]: {
//               db: data.success ? data.dbblockeddates || [] : [],
//               airbnb: data.success ? data.airbnbblockeddates || [] : [],
//             },
//           }));
//         })
//         .catch(() => {
//           setBlockedDatesMap((prev) => ({
//             ...prev,
//             [prop]: { db: [], airbnb: [] },
//           }));
//         });
//     });
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [formData.propertyname]);

//   const sortedProperties = [...(properties || [])].sort((a, b) =>
//     a.propertyname.localeCompare(b.propertyname),
//   );
//   const filteredProperties = sortedProperties.filter((p) =>
//     p.propertyname.toLowerCase().includes(propSearch.toLowerCase()),
//   );

//   const toggleProperty = (propertyname, matched) => {
//     setFormData((prev) => {
//       const current = getSelectedProperties(prev);
//       const details = { ...(prev.propertyDetails || {}) };
//       let next;

//       if (current.includes(propertyname)) {
//         next = current.filter((p) => p !== propertyname);
//         delete details[propertyname];
//       } else {
//         next = [...current, propertyname];
//         details[propertyname] = {
//           ...emptyPropertyDetail(),
//           bedrooms: matched?.maxbed ?? "",
//           guests: matched?.maxadult ?? "",
//         };
//       }

//       return {
//         ...prev,
//         propertyname: next.join(", "),
//         propertyDetails: details,
//       };
//     });
//   };

//   const removeProperty = (propertyname) => toggleProperty(propertyname, null);

//   const handleManualAdd = () => {
//     if (!manualProperty.trim()) return;
//     toggleProperty(manualProperty.trim(), null);
//     setManualMode(false);
//     setManualProperty("");
//   };

//   // ── Per-property field updates ─────────────────────────────────────
//   const updatePropertyDetail = (propName, patch) => {
//     setFormData((prev) => ({
//       ...prev,
//       propertyDetails: {
//         ...prev.propertyDetails,
//         [propName]: recalcPropertyPricing({
//           ...prev.propertyDetails[propName],
//           ...patch,
//         }),
//       },
//     }));
//   };

//   const handlePropertyText = (propName, field) => (e) =>
//     updatePropertyDetail(propName, { [field]: e.target.value });

//   const handlePropertyNumeric = (propName, field) => (e) => {
//     const v = e.target.value;
//     if (v === "" || /^[0-9]+$/.test(v))
//       updatePropertyDetail(propName, { [field]: v });
//   };

//   const handlePropertyPrice = (propName, field) => (e) => {
//     const v = e.target.value;
//     if (/^\d*\.?\d*$/.test(v)) updatePropertyDetail(propName, { [field]: v });
//   };

//   const handlePropertyDate = (propName, which) => (date) => {
//     const val = date ? format(date, "yyyy-MM-dd") : "";
//     if (which === "checkin") {
//       updatePropertyDetail(propName, { checkin: val, checkout: "" });
//     } else {
//       updatePropertyDetail(propName, { checkout: val });
//     }
//   };

//   const handlePropertyTime = (propName, isCheckin) => (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => {
//       const detail = { ...prev.propertyDetails[propName], [name]: value };
//       const hourKey = isCheckin ? "checkinHour" : "checkoutHour";
//       const minuteKey = isCheckin ? "checkinMinute" : "checkoutMinute";
//       const ampmKey = isCheckin ? "checkinAmPm" : "checkoutAmPm";
//       const targetKey = isCheckin ? "checkin_time" : "checkout_time";
//       detail[targetKey] =
//         `${detail[hourKey]}:${detail[minuteKey]} ${detail[ampmKey]}`;
//       return {
//         ...prev,
//         propertyDetails: { ...prev.propertyDetails, [propName]: detail },
//       };
//     });
//   };

//   const parseDateValue = (val) => {
//     if (!val) return null;
//     if (val instanceof Date) return val;
//     if (typeof val === "string" && val.includes("T")) return new Date(val);
//     if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
//       const [y, m, d] = val.split("-").map(Number);
//       return new Date(y, m - 1, d);
//     }
//     return new Date(val);
//   };

//   const isDateBlockedFor = (propName) => (date) => {
//     const str = format(date, "yyyy-MM-dd");
//     const b = blockedDatesMap[propName];
//     return !!b && (b.db.includes(str) || b.airbnb.includes(str));
//   };
//   const getDayClassFor = (propName) => (date) => {
//     const str = format(date, "yyyy-MM-dd");
//     const b = blockedDatesMap[propName];
//     if (!b) return undefined;
//     if (b.airbnb.includes(str)) return "airbnb-blocked-day";
//     if (b.db.includes(str)) return "zorba-blocked-day";
//     return undefined;
//   };
//   const hasBlockedInRangeFor = (propName, checkin) => (checkoutDate) => {
//     if (!checkin) return false;
//     const b = blockedDatesMap[propName];
//     if (!b) return false;
//     const start = new Date(checkin);
//     start.setDate(start.getDate() + 1);
//     let current = new Date(start);
//     const end = new Date(checkoutDate);
//     while (current < end) {
//       const str = format(current, "yyyy-MM-dd");
//       if (b.db.includes(str) || b.airbnb.includes(str)) return true;
//       current.setDate(current.getDate() + 1);
//     }
//     return false;
//   };

//   const text = (name, extra = {}) => ({
//     name,
//     value: formData[name] || "",
//     onChange: handleChange,
//     readOnly: isViewOnly,
//     disabled: isViewOnly,
//     className: ui.input,
//     ...extra,
//   });

//   return (
//     <>
//       {/* Raises the datepicker popper above the panel */}
//       <style>{`.react-datepicker-popper{z-index:80 !important}`}</style>

//       {isOpen && (
//         <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
//       )}

//       <aside
//         className={`fixed right-0 top-0 z-50 flex h-full w-full flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out sm:w-[560px] md:w-[680px] ${
//           isOpen ? "translate-x-0" : "translate-x-full"
//         }`}
//       >
//         {/* Header */}
//         <header className="flex shrink-0 items-center justify-between border-b px-6 py-4">
//           <h2 className="text-lg font-semibold text-[#1D2826]">
//             {mode === "edit" ? "Edit lead" : "View lead"}
//           </h2>
//           <div className="flex items-center gap-3">
//             {mode === "view" && (
//               <button
//                 onClick={onSwitchToEdit}
//                 className={`${ui.btn} h-8 bg-green-900 px-3 text-xs text-white hover:opacity-90`}
//               >
//                 <FaEdit /> Edit lead
//               </button>
//             )}
//             <button
//               onClick={onClose}
//               className="text-xl text-gray-500 hover:text-black"
//               aria-label="Close"
//             >
//               <MdCancel />
//             </button>
//           </div>
//         </header>

//         {/* Scrollable body */}
//         <div className="flex-1 overflow-y-auto px-6 py-5">
//           <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
//             {/* ── Customer ─────────────────────────────── */}
//             <h3 className={ui.sectionTitle}>Customer</h3>
//             <Field label="First name">
//               <input {...text("firstname")} />
//             </Field>
//             <Field label="Last name">
//               <input {...text("lastname")} />
//             </Field>
//             <Field label="Phone">
//               <input {...text("phone")} />
//             </Field>
//             <Field label="Email">
//               <input {...text("email", { type: "email" })} />
//             </Field>
//             <Field label="Country">
//               <select
//                 name="country"
//                 value={formData.country || ""}
//                 onChange={handleChange}
//                 disabled={isViewOnly}
//                 className={ui.input}
//               >
//                 <option value="">Select your country</option>
//                 {worldData.map((country) => (
//                   <option key={country.id} value={country.name}>
//                     {country.name}
//                   </option>
//                 ))}
//               </select>
//             </Field>
//             <Field label="State">
//               <select
//                 name="state"
//                 value={formData.state || ""}
//                 onChange={handleChange}
//                 disabled={isViewOnly || !formData.country}
//                 className={ui.input}
//               >
//                 <option value="">Select your state</option>
//                 {states.map((state) => (
//                   <option key={state.id} value={state.name}>
//                     {state.name}
//                   </option>
//                 ))}
//               </select>
//             </Field>
//             <Field label="City">
//               <select
//                 name="city"
//                 value={formData.city || ""}
//                 onChange={handleChange}
//                 disabled={isViewOnly || !formData.state}
//                 className={ui.input}
//               >
//                 <option value="">Select your city</option>
//                 {cities.map((city) => (
//                   <option key={city.id} value={city.name}>
//                     {city.name}
//                   </option>
//                 ))}
//               </select>
//             </Field>

//             {/* ── Lead ─────────────────────────────────── */}
//             <h3 className={`${ui.sectionTitle} mt-2`}>Lead</h3>
//             <Field label="Channel">
//               <select
//                 name="channel"
//                 value={formData.channel || ""}
//                 onChange={handleChange}
//                 disabled={isViewOnly}
//                 className={ui.input}
//               >
//                 {["Instagram DM", "Facebook Lead", "WhatsApp"].map((c) => (
//                   <option key={c} value={c}>
//                     {c}
//                   </option>
//                 ))}
//               </select>
//             </Field>
//             <Field label="Status">
//               <select
//                 name="status"
//                 value={formData.status || ""}
//                 onChange={handleChange}
//                 disabled={isViewOnly}
//                 className={ui.input}
//               >
//                 {STATUSES.map((s) => (
//                   <option key={s} value={s}>
//                     {s}
//                   </option>
//                 ))}
//               </select>
//             </Field>
//             <Field label="Assigned to">
//               <select
//                 name="assignee"
//                 value={formData.assignee || "Unassigned"}
//                 onChange={handleChange}
//                 disabled={isViewOnly}
//                 className={ui.input}
//               >
//                 {(teamMembers.length ? teamMembers : ["Unassigned"]).map(
//                   (t) => (
//                     <option key={t} value={t}>
//                       {t}
//                     </option>
//                   ),
//                 )}
//               </select>
//             </Field>
//             <Field label="Tag">
//               <select
//                 name="tag"
//                 value={formData.tag || "Warm"}
//                 onChange={handleChange}
//                 disabled={isViewOnly}
//                 className={ui.input}
//               >
//                 {["Hot", "Warm", "Cold"].map((t) => (
//                   <option key={t} value={t}>
//                     {t}
//                   </option>
//                 ))}
//               </select>
//             </Field>

//             {/* ── Properties ───────────────────────────── */}
//             <h3 className={`${ui.sectionTitle} mt-2`}>Properties</h3>
//             <div
//               className="relative flex flex-col gap-1.5 sm:col-span-2"
//               ref={dropdownRef}
//             >
//               <span className="text-xs font-medium text-gray-500">
//                 Selected properties
//               </span>

//               <div
//                 onClick={() => !isViewOnly && setIsPropOpen((p) => !p)}
//                 className={`flex min-h-[40px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-gray-300 p-1.5 text-sm ${
//                   isViewOnly
//                     ? "cursor-not-allowed bg-gray-50"
//                     : "cursor-pointer bg-white"
//                 }`}
//               >
//                 {selectedProperties.length === 0 && (
//                   <span className="px-1.5 text-gray-400">
//                     Select one or more properties
//                   </span>
//                 )}
//                 {selectedProperties.map((p) => (
//                   <span
//                     key={p}
//                     className="inline-flex items-center gap-1.5 rounded-full bg-[#DAE0CE] px-2.5 py-1 text-xs font-semibold text-[#1D2826]"
//                   >
//                     {p}
//                     {!isViewOnly && (
//                       <span
//                         onClick={(e) => {
//                           e.stopPropagation();
//                           removeProperty(p);
//                         }}
//                         className="cursor-pointer text-[#1D2826]/60 hover:text-red-500"
//                       >
//                         ✕
//                       </span>
//                     )}
//                   </span>
//                 ))}
//               </div>

//               {isPropOpen && !isViewOnly && (
//                 <div className="absolute left-0 top-full z-20 mt-1 max-h-80 w-full overflow-y-auto rounded-lg border bg-white shadow-lg">
//                   <div className="flex items-center justify-between border-b bg-gray-50 px-3 py-2">
//                     <span className="text-sm font-semibold text-gray-600">
//                       Select properties
//                     </span>
//                     <button
//                       type="button"
//                       onClick={() => setManualMode((p) => !p)}
//                       className="text-xs font-medium text-blue-600 hover:underline"
//                     >
//                       {manualMode ? "← Back to list" : "+ Add manually"}
//                     </button>
//                   </div>

//                   <div className="border-b p-2">
//                     {manualMode ? (
//                       <div className="flex items-center gap-2">
//                         <input
//                           type="text"
//                           placeholder="Enter property name"
//                           className={`${ui.input} h-9 flex-1`}
//                           value={manualProperty}
//                           onChange={(e) => setManualProperty(e.target.value)}
//                           onKeyDown={(e) =>
//                             e.key === "Enter" && handleManualAdd()
//                           }
//                         />
//                         <button
//                           type="button"
//                           disabled={!manualProperty.trim()}
//                           onClick={handleManualAdd}
//                           className={`${ui.btn} h-9 ${ui.btnPrimary}`}
//                         >
//                           + Add
//                         </button>
//                       </div>
//                     ) : (
//                       <input
//                         type="text"
//                         placeholder="Search a property"
//                         className={`${ui.input} h-9`}
//                         value={propSearch}
//                         onChange={(e) => setPropSearch(e.target.value)}
//                       />
//                     )}
//                   </div>

//                   {!manualMode && (
//                     <ul className="max-h-56 overflow-y-auto">
//                       {filteredProperties.length > 0 ? (
//                         filteredProperties.map((p) => {
//                           const checked = selectedProperties.includes(
//                             p.propertyname,
//                           );
//                           return (
//                             <li
//                               key={p.id}
//                               onClick={() => toggleProperty(p.propertyname, p)}
//                               className="flex cursor-pointer items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50"
//                             >
//                               <input
//                                 type="checkbox"
//                                 readOnly
//                                 checked={checked}
//                                 className="pointer-events-none"
//                               />
//                               {p.propertyname}
//                             </li>
//                           );
//                         })
//                       ) : (
//                         <li className="px-4 py-3 text-sm text-gray-400">
//                           No results found
//                         </li>
//                       )}
//                     </ul>
//                   )}
//                 </div>
//               )}
//             </div>

//             {/* ── One card per selected property ───────── */}
//             {selectedProperties.map((propName) => {
//               const detail =
//                 formData.propertyDetails?.[propName] || emptyPropertyDetail();
//               const nights = calcNights(detail.checkin, detail.checkout);

//               return (
//                 <div
//                   key={propName}
//                   className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 sm:col-span-2"
//                 >
//                   <h4 className="mb-4 text-sm font-bold text-[#1D2826]">
//                     🏡 {propName}
//                   </h4>

//                   <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
//                     <Field label="Destination">
//                       <input
//                         value={detail.destination}
//                         onChange={handlePropertyText(propName, "destination")}
//                         readOnly={isViewOnly}
//                         disabled={isViewOnly}
//                         className={ui.input}
//                       />
//                     </Field>
//                     <Field label="Number of guests">
//                       <input
//                         inputMode="numeric"
//                         value={detail.guests}
//                         onChange={handlePropertyNumeric(propName, "guests")}
//                         readOnly={isViewOnly}
//                         disabled={isViewOnly}
//                         className={ui.input}
//                       />
//                     </Field>

//                     <Field label="Check-in date">
//                       <DatePicker
//                         wrapperClassName="w-full"
//                         selected={parseDateValue(detail.checkin)}
//                         onChange={handlePropertyDate(propName, "checkin")}
//                         dateFormat="dd-MM-yyyy"
//                         minDate={new Date()}
//                         filterDate={(d) => !isDateBlockedFor(propName)(d)}
//                         dayClassName={getDayClassFor(propName)}
//                         disabled={isViewOnly}
//                         className={ui.input}
//                       />
//                       <TimeSelects
//                         prefix="checkin"
//                         detail={detail}
//                         onChange={handlePropertyTime(propName, true)}
//                         disabled={isViewOnly}
//                       />
//                     </Field>

//                     <Field label="Check-out date">
//                       <DatePicker
//                         wrapperClassName="w-full"
//                         selected={parseDateValue(detail.checkout)}
//                         onChange={handlePropertyDate(propName, "checkout")}
//                         dateFormat="dd-MM-yyyy"
//                         filterDate={(d) =>
//                           !hasBlockedInRangeFor(propName, detail.checkin)(d)
//                         }
//                         disabled={!detail.checkin || isViewOnly}
//                         minDate={
//                           detail.checkin
//                             ? (() => {
//                                 const d = parseDateValue(detail.checkin);
//                                 d.setDate(d.getDate() + 1);
//                                 return d;
//                               })()
//                             : null
//                         }
//                         placeholderText={
//                           !detail.checkin
//                             ? "Select check-in first"
//                             : "dd-mm-yyyy"
//                         }
//                         className={ui.input}
//                       />
//                       <TimeSelects
//                         prefix="checkout"
//                         detail={detail}
//                         onChange={handlePropertyTime(propName, false)}
//                         disabled={isViewOnly}
//                       />
//                     </Field>

//                     <Field label="Bedrooms">
//                       <input
//                         inputMode="numeric"
//                         value={detail.bedrooms}
//                         onChange={handlePropertyNumeric(propName, "bedrooms")}
//                         readOnly={isViewOnly}
//                         disabled={isViewOnly}
//                         className={ui.input}
//                       />
//                     </Field>
//                     <Field label="Property price (per night)">
//                       <input
//                         value={detail.proprice}
//                         onChange={handlePropertyPrice(propName, "proprice")}
//                         readOnly={isViewOnly}
//                         disabled={isViewOnly}
//                         className={ui.input}
//                       />
//                     </Field>
//                     <Field label="Number of nights">
//                       <input
//                         value={nights}
//                         readOnly
//                         disabled
//                         className={ui.input}
//                       />
//                     </Field>
//                     <Field label="Security deposit">
//                       <input
//                         value={detail.secdep}
//                         onChange={handlePropertyPrice(propName, "secdep")}
//                         readOnly={isViewOnly}
//                         disabled={isViewOnly}
//                         className={ui.input}
//                       />
//                     </Field>
//                     <Field label="Advance payment">
//                       <input
//                         value={detail.advpay}
//                         onChange={handlePropertyPrice(propName, "advpay")}
//                         readOnly={isViewOnly}
//                         disabled={isViewOnly}
//                         className={ui.input}
//                       />
//                     </Field>
//                     <Field label="Balance payment">
//                       <input
//                         value={detail.balpay}
//                         onChange={handlePropertyPrice(propName, "balpay")}
//                         readOnly={isViewOnly}
//                         disabled={isViewOnly}
//                         className={ui.input}
//                       />
//                     </Field>
//                     <Field label="Total price">
//                       <input
//                         value={detail.totpay || "0.00"}
//                         readOnly
//                         disabled
//                         className={`${ui.input} font-semibold`}
//                       />
//                     </Field>
//                     <Field label="Inclusions" className="sm:col-span-2">
//                       <textarea
//                         value={detail.inclusions}
//                         onChange={handlePropertyText(propName, "inclusions")}
//                         readOnly={isViewOnly}
//                         disabled={isViewOnly}
//                         rows={2}
//                         className={ui.textarea}
//                       />
//                     </Field>
//                   </div>
//                 </div>
//               );
//             })}

//             {/* ── Notes ────────────────────────────────── */}
//             <h3 className={`${ui.sectionTitle} mt-2`}>Notes</h3>
//             <Field label="Enquiry" className="sm:col-span-2">
//               <textarea
//                 name="enquiry"
//                 value={formData.enquiry || ""}
//                 onChange={handleChange}
//                 readOnly={isViewOnly}
//                 disabled={isViewOnly}
//                 rows={3}
//                 className={ui.textarea}
//               />
//             </Field>
//             <Field label="Remark" className="sm:col-span-2">
//               <textarea
//                 name="remark"
//                 value={formData.remark || ""}
//                 onChange={handleChange}
//                 readOnly={isViewOnly}
//                 disabled={isViewOnly}
//                 rows={2}
//                 className={ui.textarea}
//               />
//             </Field>
//           </div>
//         </div>

//         {/* Fixed footer */}
//         {mode === "edit" && (
//           <footer className="grid shrink-0 grid-cols-2 gap-3 border-t bg-white px-6 py-4">
//             <button onClick={onClose} className={`${ui.btn} ${ui.btnGhost}`}>
//               Cancel
//             </button>
//             <button onClick={onSave} className={`${ui.btn} ${ui.btnPrimary}`}>
//               Save
//             </button>
//           </footer>
//         )}
//       </aside>
//     </>
//   );
// }

// /* ═════════════════════════════════════════════════════════════════════════
//    TABLES
//    ═════════════════════════════════════════════════════════════════════════ */
// /* ── Small shared pieces ─────────────────────────────────────────────────── */
// const Dash = () => <span className="text-xs text-gray-300">—</span>;

// const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : "—");

// function TableShell({ head, children, footer }) {
//   return (
//     <section className={`${ui.card} overflow-hidden`}>
//       <div className="overflow-x-auto">
//         <table className="w-full text-sm text-gray-600">
//           <thead className="border-b border-gray-200 bg-gray-50">
//             <tr>{head}</tr>
//           </thead>
//           <tbody>{children}</tbody>
//         </table>
//       </div>
//       {footer}
//     </section>
//   );
// }

// function EmptyRow({ colSpan, children, tone = "text-gray-400" }) {
//   return (
//     <tr>
//       <td
//         colSpan={colSpan}
//         className={`px-4 py-14 text-center text-sm ${tone}`}
//       >
//         {children}
//       </td>
//     </tr>
//   );
// }

// function ContactLines({ phone, email, time }) {
//   return (
//     <div className="flex flex-col gap-1 text-xs">
//       {phone && (
//         <span className="inline-flex items-center gap-1.5 text-gray-600">
//           <FaPhone className="shrink-0 text-gray-400" /> {phone}
//         </span>
//       )}
//       {email && (
//         <span className="inline-flex items-center gap-1.5 break-all text-gray-500">
//           <IoMail className="shrink-0 text-gray-400" /> {email}
//         </span>
//       )}
//       {time && (
//         <span className="inline-flex items-center gap-1.5 text-gray-400">
//           <IoIosTime className="shrink-0" /> {time}
//         </span>
//       )}
//     </div>
//   );
// }
// const SCORE_TONES = {
//   "Ready to book": "bg-red-100 text-red-600",
//   Interested: "bg-orange-100 text-orange-600",
//   "Just browsing": "bg-slate-100 text-slate-500",
//   Booked: "bg-green-100 text-green-700",
//   Lost: "bg-gray-200 text-gray-500",
// };
// function ScoreBadge({ s }) {
//   if (!s) return null;
//   const why = s.reasons
//     .map((r) => `${r.points > 0 ? "+" : ""}${r.points} ${r.text}`)
//     .join("\n");
//   return (
//     <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{why}</div>}>
//       <Badge className={SCORE_TONES[s.label]}>
//         {s.label} · {s.score}
//       </Badge>
//     </Tooltip>
//   );
// }
// function Badge({ children, className = "", style }) {
//   return (
//     <span
//       style={style}
//       className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${className}`}
//     >
//       {children}
//     </span>
//   );
// }

// function StatusSelect({ value, options, colors, fallback = "", onChange }) {
//   const c = colors[value] || "#94a3b8";
//   return (
//     <select
//       value={value || fallback}
//       onChange={(e) => onChange(e.target.value)}
//       className="!h-8 !py-0 !leading-none min-w-[150px] cursor-pointer rounded-md border !px-2 text-[11px] font-bold outline-none"
//       style={{ borderColor: c, background: c + "18", color: c }}
//     >
//       {options.map((s) => (
//         <option key={s} value={s}>
//           {s}
//         </option>
//       ))}
//     </select>
//   );
// }

// function AssigneeSelect({ value, teamMembers, teamLoading, onChange }) {
//   return (
//     <select
//       value={value || "Unassigned"}
//       onChange={(e) => onChange(e.target.value)}
//       disabled={teamLoading}
//       className="!h-8 !py-0 !leading-none min-w-[130px] cursor-pointer rounded-md border border-gray-300 bg-white !px-2 text-xs outline-none focus:border-[#1D2826] disabled:bg-gray-50"
//     >
//       {teamLoading ? (
//         <option>Loading...</option>
//       ) : (
//         teamMembers.map((t) => (
//           <option key={t} value={t}>
//             {t}
//           </option>
//         ))
//       )}
//     </select>
//   );
// }

// function RemarkCell({ remark, onEdit }) {
//   return remark ? (
//     <button
//       type="button"
//       onClick={onEdit}
//       title="Click to edit"
//       className="block max-w-[180px] rounded-md bg-gray-50 px-2 py-1 text-left text-xs leading-snug text-gray-600 hover:bg-gray-100"
//     >
//       {remark}
//     </button>
//   ) : (
//     <button
//       type="button"
//       onClick={onEdit}
//       className="whitespace-nowrap rounded-md border border-dashed border-indigo-300 px-2 py-1 text-[11px] font-medium text-indigo-500 hover:bg-indigo-50"
//     >
//       + Add remark
//     </button>
//   );
// }

// function LeadActions({
//   lead,
//   source,
//   name,
//   sendingMailId,
//   mailReady,
//   onView,
//   onEdit,
//   onMail,
//   onDelete,
// }) {
//   const sending = sendingMailId === lead.id;
//   return (
//     <div className="flex items-center gap-2">
//       <IconButton tip="View lead" onClick={onView}>
//         <FaEye size={14} />
//       </IconButton>
//       <IconButton tip="Edit lead" tone="yellow" onClick={onEdit}>
//         <FaEdit size={14} />
//       </IconButton>
//       <IconButton
//         tip={
//           sending
//             ? "Sending…"
//             : mailReady
//               ? "Send email"
//               : "Add property, check-in & check-out first"
//         }
//         tone="purple"
//         disabled={sending || !mailReady}
//         onClick={onMail}
//       >
//         <IoIosMail size={16} className={sending ? "animate-pulse" : ""} />
//       </IconButton>
//       {lead.status === "Lost & Closed" && (
//         <IconButton
//           tip="Delete lead"
//           tone="red"
//           onClick={() => onDelete({ id: lead.id, name, type: source })}
//         >
//           <MdDelete size={14} />
//         </IconButton>
//       )}
//     </div>
//   );
// }

// function WhatsAppButton({ onClick }) {
//   return (
//     <IconButton tip="Send property details on WhatsApp" onClick={onClick}>
//       <RiWhatsappFill size={16} className="text-[#25d366]" />
//     </IconButton>
//   );
// }

// function EnquiryText({ text }) {
//   if (!text) return <Dash />;
//   return (
//     <div className="space-y-2 text-xs leading-relaxed text-gray-600">
//       {text.split("\n").map((line, i) => {
//         const clean = line.replace(/^•\s*/, "").trim();
//         if (!clean) return null;
//         const [label, ...rest] = clean.split(":");
//         const value = rest.join(":").trim();
//         return (
//           <div key={i}>
//             <div className="text-gray-500">
//               {label}
//               {value ? ":" : ""}
//             </div>
//             {value && (
//               <div className="font-semibold text-gray-800">{value}</div>
//             )}
//           </div>
//         );
//       })}
//     </div>
//   );
// }

// const rowCls = "border-b border-gray-100 transition-colors hover:bg-gray-50";

// /* ── META TABLE ──────────────────────────────────────────────────────────── */
// function MetaLeadsTable({
//   leads, // currentLeads
//   loading,
//   isIncomingView,
//   isAllLeadsView,
//   selectedRows,
//   onSelectAll,
//   onRowCheck, // handleRowCheckboxChange
//   updatingId,
//   teamMembers,
//   teamLoading,
//   sendingMailId,
//   isLeadMailReady,
//   patchLead,
//   handleAssigneeChange,
//   addRemark,
//   openLeadPanel,
//   openMailConfirm,
//   setDeleteTarget,
//   handleWhatsAppClick,
//   onMetaProcessed, // handleMetaLeadProcessed
//   pagination, // props for <Pagination />
// }) {
//   const colSpan = isIncomingView ? 5 : 9;
//   const allChecked = leads.length > 0 && selectedRows.length === leads.length;

//   const head = (
//     <>
//       <th className={`${ui.th} w-10`}>
//         <input type="checkbox" checked={allChecked} onChange={onSelectAll} />
//       </th>
//       <th className={ui.th}>Lead details</th>
//       <th className={ui.th}>Enquiry</th>
//       {isIncomingView ? (
//         <>
//           <th className={ui.th}>Meta fields</th>
//           <th className={ui.th}>Action</th>
//         </>
//       ) : (
//         <>
//           <th className={ui.th}>Status</th>

//           <th className={ui.th}>Assigned to</th>
//           <th className={ui.th}>Remark</th>
//           <th className={ui.th}>Date</th>
//           <th className={ui.th}>Actions</th>
//           <th className={`${ui.th} text-center`}>Follow up</th>
//         </>
//       )}
//     </>
//   );

//   return (
//     <TableShell head={head} footer={<Pagination {...pagination} />}>
//       {loading ? (
//         <tr>
//           <td colSpan={colSpan}>
//             <Loader label="Loading..." fullScreen={false} className="py-6" />
//           </td>
//         </tr>
//       ) : leads.length === 0 ? (
//         <EmptyRow colSpan={colSpan}>
//           {isIncomingView
//             ? "No unprocessed Meta leads. They'll appear here automatically via Meta webhooks."
//             : "No leads found for this filter."}
//         </EmptyRow>
//       ) : (
//         leads.map((lead) => {
//           const ch = CHANNELS[lead.channel] || {
//             color: "#888",
//             bg: "#f1f5f9",
//             icon: null,
//           };
//           const isMetaLead = String(lead.id).startsWith("meta_");
//           const showProcessButton = isAllLeadsView && isMetaLead;

//           return (
//             <tr
//               key={lead.id}
//               className={rowCls}
//               style={{ opacity: updatingId === lead.id ? 0.55 : 1 }}
//             >
//               <td className={ui.td}>
//                 <input
//                   type="checkbox"
//                   checked={selectedRows.includes(lead.id)}
//                   onChange={() =>
//                     onRowCheck(lead.id, isMetaLead && isIncomingView)
//                   }
//                 />
//               </td>

//               {/* Lead details */}
//               <td className={`${ui.td} min-w-[230px] align-top`}>
//                 <div className="mb-1.5 font-semibold text-gray-900">
//                   {lead.name || "—"} <ScoreBadge s={lead.lead_score} />
//                 </div>
//                 <div className="mb-2 flex flex-wrap items-center gap-1.5">
//                   <Badge style={{ background: ch.bg, color: ch.color }}>
//                     {ch.icon} {lead.channel}
//                   </Badge>
//                   {lead.tag && (
//                     <Badge
//                       style={{
//                         background: (TAG_COLORS[lead.tag] || "#999") + "18",
//                         color: TAG_COLORS[lead.tag] || "#999",
//                       }}
//                     >
//                       {lead.tag}
//                     </Badge>
//                   )}
//                   {showProcessButton && (
//                     <Badge className="bg-amber-100 text-amber-700">
//                       Unprocessed
//                     </Badge>
//                   )}
//                 </div>

//                 <ContactLines
//                   phone={lead.phone}
//                   email={lead.email}
//                   time={timeAgo(lead.created_at)}
//                 />
//               </td>

//               {/* Enquiry */}
//               <td className={`${ui.td} min-w-[200px] max-w-[280px] align-top`}>
//                 <EnquiryText text={lead.enquiry} />
//               </td>

//               {isIncomingView ? (
//                 <>
//                   <td
//                     className={`${ui.td} min-w-[200px] max-w-[260px] align-top`}
//                   >
//                     {(lead.field_data || []).length > 0 ? (
//                       <div className="flex flex-col gap-1">
//                         {lead.field_data.map((f, i) => (
//                           <div key={i} className="text-[11px] leading-snug">
//                             <span className="text-gray-400">
//                               {f.name.replace(/_/g, " ")}:{" "}
//                             </span>
//                             <span className="font-semibold text-gray-700">
//                               {f.values?.[0] || "—"}
//                             </span>
//                           </div>
//                         ))}
//                       </div>
//                     ) : (
//                       <Dash />
//                     )}
//                   </td>
//                   <td className={ui.td}>
//                     <ProcessMetaLeadButton
//                       lead={lead}
//                       onProcessed={onMetaProcessed}
//                     />
//                   </td>
//                 </>
//               ) : (
//                 <>
//                   {/* Status */}
//                   <td className={ui.td}>
//                     {showProcessButton ? (
//                       <ProcessMetaLeadButton
//                         lead={lead}
//                         onProcessed={onMetaProcessed}
//                       />
//                     ) : (
//                       <StatusSelect
//                         value={lead.status}
//                         options={STATUSES}
//                         colors={STATUS_COLORS}
//                         onChange={(v) => patchLead(lead.id, "status", v)}
//                       />
//                     )}
//                   </td>

//                   {/* Assignee */}
//                   <td className={ui.td}>
//                     {showProcessButton ? (
//                       <Dash />
//                     ) : (
//                       <AssigneeSelect
//                         value={lead.assignee}
//                         teamMembers={teamMembers}
//                         teamLoading={teamLoading}
//                         onChange={(v) => handleAssigneeChange(lead.id, v)}
//                       />
//                     )}
//                   </td>

//                   {/* Remark */}
//                   <td className={ui.td}>
//                     {showProcessButton ? (
//                       <Dash />
//                     ) : (
//                       <RemarkCell
//                         remark={lead.remark}
//                         onEdit={() => addRemark(lead)}
//                       />
//                     )}
//                   </td>

//                   {/* Date */}
//                   <td className={`${ui.td} whitespace-nowrap text-xs`}>
//                     {showProcessButton ? (
//                       <Dash />
//                     ) : (
//                       <Tooltip
//                         title={
//                           lead.created_at
//                             ? new Date(lead.created_at).toLocaleString()
//                             : "—"
//                         }
//                         placement="left"
//                       >
//                         <span>{fmtDate(lead.created_at)}</span>
//                       </Tooltip>
//                     )}
//                   </td>

//                   {/* Actions */}
//                   <td className={ui.td}>
//                     {!showProcessButton && (
//                       <LeadActions
//                         lead={lead}
//                         source="meta"
//                         name={lead.name}
//                         sendingMailId={sendingMailId}
//                         mailReady={isLeadMailReady(lead)}
//                         onView={() => openLeadPanel(lead, "view")}
//                         onEdit={() => openLeadPanel(lead, "edit")}
//                         onMail={() => openMailConfirm(lead, "meta")}
//                         onDelete={setDeleteTarget}
//                       />
//                     )}
//                   </td>

//                   {/* Follow up */}
//                   <td className={`${ui.td} text-center`}>
//                     {showProcessButton ? (
//                       <Dash />
//                     ) : (
//                       <div className="flex justify-center">
//                         <WhatsAppButton
//                           onClick={() => handleWhatsAppClick(lead, "meta")}
//                         />
//                       </div>
//                     )}
//                   </td>
//                 </>
//               )}
//             </tr>
//           );
//         })
//       )}
//     </TableShell>
//   );
// }

// /* ── WEBSITE TABLE ───────────────────────────────────────────────────────── */
// function WebsiteLeadsTable({
//   leads, // currentLeads
//   loading, // isWebsiteIncomingView ? websiteLoading : processedWebsiteLoading
//   error, // websiteError
//   isWebsiteIncomingView,
//   selectedRows,
//   onSelectAll,
//   onRowCheck,
//   updatingId,
//   teamMembers,
//   teamLoading,
//   sendingMailId,
//   isLeadMailReady,
//   patchWebsiteLead,
//   addWebsiteRemark,
//   openLeadPanel,
//   openMailConfirm,
//   setDeleteTarget,
//   handleWhatsAppClick,
//   onWebsiteProcessed, // handleWebsiteLeadProcessed
//   pagination,
// }) {
//   const colSpan = isWebsiteIncomingView ? 7 : 11;
//   const allChecked = leads.length > 0 && selectedRows.length === leads.length;

//   const head = (
//     <>
//       <th className={`${ui.th} w-10`}>
//         <input type="checkbox" checked={allChecked} onChange={onSelectAll} />
//       </th>
//       <th className={ui.th}>Lead ID</th>
//       <th className={ui.th}>Customer</th>
//       <th className={ui.th}>Property / destination</th>
//       <th className={ui.th}>Check-in → Check-out</th>
//       <th className={ui.th}>Location</th>
//       {isWebsiteIncomingView ? (
//         <th className={ui.th}>Action</th>
//       ) : (
//         <>
//           <th className={ui.th}>Status</th>
//           <th className={ui.th}>Assigned to</th>
//           <th className={ui.th}>Remark</th>
//           <th className={ui.th}>Actions</th>
//           <th className={`${ui.th} text-center`}>Follow up</th>
//         </>
//       )}
//     </>
//   );

//   return (
//     <TableShell head={head} footer={<Pagination {...pagination} />}>
//       {loading ? (
//         <tr>
//           <td colSpan={colSpan}>
//             <Loader label="Loading..." fullScreen={false} className="py-6" />
//           </td>
//         </tr>
//       ) : error && isWebsiteIncomingView ? (
//         <EmptyRow colSpan={colSpan} tone="text-red-400">
//           {error}
//         </EmptyRow>
//       ) : leads.length === 0 ? (
//         <EmptyRow colSpan={colSpan}>
//           {isWebsiteIncomingView
//             ? "No new website leads. All incoming leads have been processed."
//             : "No processed website leads found for this stage."}
//         </EmptyRow>
//       ) : (
//         leads.map((lead) => {
//           const raw = lead._isIncoming === true || isWebsiteIncomingView;
//           const rowId = raw ? lead.leadid : lead.id;
//           const name = raw
//             ? lead.customername ||
//               [lead.firstname, lead.lastname].filter(Boolean).join(" ") ||
//               "—"
//             : lead.customer_name || "—";
//           const checkin = raw ? lead.checkin : lead.check_in;
//           const checkout = raw ? lead.checkout : lead.check_out;
//           const location = [lead.city, lead.state, lead.country]
//             .filter(Boolean)
//             .join(", ");

//           return (
//             <tr
//               key={rowId}
//               className={rowCls}
//               style={{ opacity: updatingId === lead.id ? 0.55 : 1 }}
//             >
//               <td className={ui.td}>
//                 <input
//                   type="checkbox"
//                   checked={selectedRows.includes(rowId)}
//                   onChange={() => onRowCheck(rowId, raw)}
//                 />
//               </td>

//               {/* Lead ID */}
//               <td className={`${ui.td} align-top`}>
//                 <div className="flex flex-col items-start gap-1">
//                   <span className="whitespace-nowrap font-mono text-xs text-indigo-600">
//                     {raw ? lead.leadid : `WL-${lead.original_lead_id}`}
//                   </span>
//                   {raw && !isWebsiteIncomingView && (
//                     <Badge className="bg-amber-100 text-amber-700">
//                       Unprocessed
//                     </Badge>
//                   )}
//                 </div>
//               </td>

//               {/* Customer */}
//               <td className={`${ui.td} min-w-[200px] align-top`}>
//                 <div className="mb-1.5 font-semibold text-gray-900">{name} {" "}  <ScoreBadge s={lead.lead_score} /></div>
//                 <ContactLines phone={lead.phone} email={lead.email} />
//                 {!raw && lead.converted === 1 && (
//                   <Badge className="mt-2 bg-green-100 text-green-700">
//                     ✓ Converted
//                   </Badge>
//                 )}
//               </td>

//               {/* Property */}
//               <td className={`${ui.td} min-w-[180px] align-top`}>
//                 <div className="text-xs font-semibold text-gray-700">
//                   {lead.property || "—"}
//                 </div>
//                 <div className="mt-0.5 text-xs text-gray-400">
//                   {lead.destination || "—"}
//                 </div>
//               </td>

//               {/* Dates */}
//               <td
//                 className={`${ui.td} whitespace-nowrap text-xs text-gray-600`}
//               >
//                 <span className="inline-flex items-center gap-1.5">
//                   <FaCalendarAlt className="text-gray-400" />
//                   {fmtDate(checkin)} → {fmtDate(checkout)}
//                 </span>
//               </td>

//               {/* Location */}
//               <td className={`${ui.td} min-w-[140px] text-xs text-gray-500`}>
//                 {location || "—"}
//               </td>

//               {isWebsiteIncomingView ? (
//                 <td className={ui.td}>
//                   <ProcessWebsiteLeadButton
//                     lead={lead}
//                     onProcessed={onWebsiteProcessed}
//                   />
//                 </td>
//               ) : (
//                 <>
//                   {/* Status */}
//                   <td className={ui.td}>
//                     {raw ? (
//                       <ProcessWebsiteLeadButton
//                         lead={lead}
//                         onProcessed={onWebsiteProcessed}
//                       />
//                     ) : (
//                       <StatusSelect
//                         value={lead.status}
//                         fallback="New Lead"
//                         options={WEBSITE_STATUSES}
//                         colors={WEBSITE_STATUS_COLORS}
//                         onChange={(v) => patchWebsiteLead(lead.id, "status", v)}
//                       />
//                     )}
//                   </td>

//                   {/* Assignee */}
//                   <td className={ui.td}>
//                     {raw ? (
//                       <Dash />
//                     ) : (
//                       <AssigneeSelect
//                         value={lead.assignee}
//                         teamMembers={teamMembers}
//                         teamLoading={teamLoading}
//                         onChange={(v) =>
//                           patchWebsiteLead(lead.id, "assignee", v)
//                         }
//                       />
//                     )}
//                   </td>

//                   {/* Remark */}
//                   <td className={ui.td}>
//                     {raw ? (
//                       <Dash />
//                     ) : (
//                       <RemarkCell
//                         remark={lead.remark}
//                         onEdit={() => addWebsiteRemark(lead)}
//                       />
//                     )}
//                   </td>

//                   {/* Actions */}
//                   <td className={ui.td}>
//                     {raw ? (
//                       <Dash />
//                     ) : (
//                       <LeadActions
//                         lead={lead}
//                         source="website"
//                         name={lead.customer_name}
//                         sendingMailId={sendingMailId}
//                         mailReady={isLeadMailReady(lead)}
//                         onView={() => openLeadPanel(lead, "view", "website")}
//                         onEdit={() => openLeadPanel(lead, "edit", "website")}
//                         onMail={() => openMailConfirm(lead, "website")}
//                         onDelete={setDeleteTarget}
//                       />
//                     )}
//                   </td>

//                   {/* Follow up */}
//                   <td className={`${ui.td} text-center`}>
//                     {raw ? (
//                       <Dash />
//                     ) : (
//                       <div className="flex justify-center">
//                         <WhatsAppButton
//                           onClick={() => handleWhatsAppClick(lead, "website")}
//                         />
//                       </div>
//                     )}
//                   </td>
//                 </>
//               )}
//             </tr>
//           );
//         })
//       )}
//     </TableShell>
//   );
// }

// /* ═════════════════════════════════════════════════════════════════════════
//    MAIN COMPONENT  (state + handlers: unchanged)
//    ═════════════════════════════════════════════════════════════════════════ */
// const InquireLeads = () => {
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
//   const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
//     const s = localStorage.getItem("menubarOpen");
//     return s !== null ? JSON.parse(s) : window.innerWidth >= 1024;
//   });
//   const [storeTab, setStoreTab] = useState("meta");
//   const [mailConfirmLead, setMailConfirmLead] = useState(null);
//   const [leads, setLeads] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [leadSource, setLeadSource] = useState("meta");
//   const [mailConfirmSource, setMailConfirmSource] = useState("meta");
//   const [teamMembers, setTeamMembers] = useState([]);
//   const [teamLoading, setTeamLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [sendingMailId, setSendingMailId] = useState(null);
//   const [processedMetaIds, setProcessedMetaIds] = useState(new Set());
//   const [activeStatus, setActiveStatus] = useState("Incoming Leads");
//   const [websiteLeads, setWebsiteLeads] = useState([]);
//   const [websiteLoading, setWebsiteLoading] = useState(false);
//   const [websiteError, setWebsiteError] = useState(null);
//   const [processedWebsiteIds, setProcessedWebsiteIds] = useState(new Set());
//   const [processedWebsiteLeads, setProcessedWebsiteLeads] = useState([]);
//   const [processedWebsiteLoading, setProcessedWebsiteLoading] = useState(false);
//   const [websiteActiveStatus, setWebsiteActiveStatus] = useState("All Leads");
//   const [activeChannel, setActiveChannel] = useState("All");
//   const [activeTag] = useState("All");
//   const [searchInput, setSearchInput] = useState("");
//   const [search, setSearch] = useState("");
//   const [followUpLead, setFollowUpLead] = useState(null);
//   const [showCreate, setShowCreate] = useState(false);
//   const [updatingId, setUpdatingId] = useState(null);
//   const [selectedRows, setSelectedRows] = useState([]);
//   const [showConvertModal, setShowConvertModal] = useState(false);
//   const [converting, setConverting] = useState(false);
//   const [convertResult, setConvertResult] = useState(null);
//   const [showBulkProcessModal, setShowBulkProcessModal] = useState(false);
//   const [bulkProcessing, setBulkProcessing] = useState(false);
//   const [bulkProcessResult, setBulkProcessResult] = useState(null);
//   const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, type: "meta"|"website" }
//   const [deleting, setDeleting] = useState(false);
//   const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
//   const userId = currentUser.id;
//   const [properties, setProperties] = useState([]);
//   const [notifCount, setNotifCount] = useState(0);
//   const [notifBreakdown, setNotifBreakdown] = useState({ meta: 0, website: 0 });
//   const [showNotifDropdown, setShowNotifDropdown] = useState(false);
//   const rowsPerPage = 20;
//   const [currentPage, setCurrentPage] = useState(1);
//   const [showPopup_filter, setShowPopup_filter] = useState(false);
//   const filterRef = useRef(null);
//   const [isLeadPanelOpen, setIsLeadPanelOpen] = useState(false);
//   const [leadPanelMode, setLeadPanelMode] = useState("view"); // "view" | "edit"
//   const [editingLead, setEditingLead] = useState(null);
//   const [leadFormData, setLeadFormData] = useState({});
//   const [websiteFilter, setWebsiteFilter] = useState("All"); // "All" | "Processed" | "Unprocessed"
//   const [dateFrom, setDateFrom] = useState("");
//   const [dateTo, setDateTo] = useState("");
//   const [waPhoneTarget, setWaPhoneTarget] = useState(null); // { lead, source }

//   useEffect(() => {
//     fetchTeamMembers();
//   }, []);
//   const fetchTeamMembers = async () => {
//     try {
//       setTeamLoading(true);
//       const response = await fetch(
//         "https://dash.zorbastays.com/web-backend/users",
//       );
//       const data = await response.json();

//       // Extract names from the API response
//       const names = data.map((user) => user.name);
//       // Add "Unassigned" at the beginning
//       setTeamMembers(["Unassigned", ...names]);
//     } catch (error) {
//       console.error("Error fetching team members:", error);
//       // Fallback to static list if API fails
//       setTeamMembers([
//         "Unassigned",
//         "Parth",
//         "Altaf",
//         "Riya",
//         "Dev",
//         "Sneha",
//         "Maharshi",
//       ]);
//     } finally {
//       setTeamLoading(false);
//     }
//   };
//   useEffect(() => {
//     const t = setTimeout(() => setSearch(searchInput), 400);
//     return () => clearTimeout(t);
//   }, [searchInput]);
//   useEffect(() => {
//     setCurrentPage(1);
//     setSelectedRows([]);
//     setWebsiteFilter("All");
//   }, [
//     search,
//     activeStatus,
//     activeChannel,
//     activeTag,
//     websiteActiveStatus,
//     storeTab,
//   ]);
//   useEffect(() => {
//     function handleClickOutside(e) {
//       if (filterRef.current && !filterRef.current.contains(e.target))
//         setShowPopup_filter(false);
//     }
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);
//   useEffect(() => {
//     const handleResize = () => {
//       const mobile = window.innerWidth < 1024;
//       setIsMobile(mobile);
//       if (mobile) setIsSidebarOpen(false);
//     };
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const fetchLeads = useCallback(async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       // Just fetch all, filter client-side
//       const res = await fetch(`${API}/leads?limit=200`);
//       if (!res.ok) throw new Error(`API error ${res.status}`);
//       const data = await res.json();
//       const allLeads = data.leads || [];
//       setProcessedMetaIds(
//         new Set(
//           allLeads
//             .filter((l) => !String(l.id).startsWith("meta_") && l.meta_lead_id)
//             .map((l) => l.meta_lead_id),
//         ),
//       );
//       setLeads(allLeads);
//     } catch (e) {
//       setError(e.message);
//     } finally {
//       setLoading(false);
//     }
//   }, []); // no longer depends on filter states

//   useEffect(() => {
//     fetchLeads();
//   }, [fetchLeads]);
//   useEffect(() => {
//     const fetchProperties = async () => {
//       try {
//         const res = await fetch(`${API}/properties`);
//         const data = await res.json();
//         setProperties(data);
//       } catch (err) {
//         console.error("Error fetching properties:", err);
//       }
//     };
//     fetchProperties();
//   }, []);
//   const fetchWebsiteLeads = useCallback(async () => {
//     setWebsiteLoading(true);
//     setWebsiteError(null);
//     try {
//       const res = await fetch(`${API}/website-leads`);
//       if (!res.ok) throw new Error(`API error ${res.status}`);
//       setWebsiteLeads((await res.json()).data || []);
//     } catch (e) {
//       setWebsiteError(e.message);
//     } finally {
//       setWebsiteLoading(false);
//     }
//   }, []);

//   const fetchProcessedWebsiteLeads = useCallback(async () => {
//     setProcessedWebsiteLoading(true);
//     try {
//       const url =
//         websiteActiveStatus !== "Incoming Leads" &&
//         websiteActiveStatus !== "All Processed Leads" &&
//         websiteActiveStatus !== "All Leads"
//           ? `${API}/processed-website-leads?status=${encodeURIComponent(websiteActiveStatus)}`
//           : `${API}/processed-website-leads`;
//       const res = await fetch(url);
//       if (!res.ok) throw new Error(`API error ${res.status}`);
//       const rows = (await res.json()).data || [];
//       setProcessedWebsiteLeads(rows);
//       setProcessedWebsiteIds(
//         new Set(rows.map((r) => String(r.original_lead_id))),
//       );
//     } catch (e) {
//       console.error("fetchProcessedWebsiteLeads:", e.message);
//     } finally {
//       setProcessedWebsiteLoading(false);
//     }
//   }, [websiteActiveStatus]);

//   useEffect(() => {
//     if (storeTab === "website") {
//       fetchWebsiteLeads();
//       fetchProcessedWebsiteLeads();
//     }
//   }, [storeTab, fetchWebsiteLeads, fetchProcessedWebsiteLeads]);

//   // ── Notifications ───────────────────────────────────────────────────────────
//   const [, setCurrentCounts] = useState({ meta: 0, website: 0 });
//   const hydrateTimes = (d) => {
//     const ci = parseTimeParts(d.checkin_time, "12", "PM");
//     const co = parseTimeParts(d.checkout_time, "11", "AM");
//     return {
//       ...d,
//       checkinHour: ci.hour,
//       checkinMinute: ci.minute,
//       checkinAmPm: ci.ampm,
//       checkoutHour: co.hour,
//       checkoutMinute: co.minute,
//       checkoutAmPm: co.ampm,
//       checkin_time: `${ci.hour}:${ci.minute} ${ci.ampm}`,
//       checkout_time: `${co.hour}:${co.minute} ${co.ampm}`,
//     };
//   };
//   const fetchNotifications = useCallback(async () => {
//     if (!userId) return;
//     try {
//       const res = await fetch(`${API}/lead-notifications?userId=${userId}`);
//       const data = await res.json();
//       setNotifCount(data.total || 0);
//       setNotifBreakdown({ meta: data.meta || 0, website: data.website || 0 });
//       setCurrentCounts({
//         meta: data.currentMeta || 0,
//         website: data.currentWebsite || 0,
//       });
//     } catch (e) {
//       console.error("Notification fetch failed:", e.message);
//     }
//   }, [userId]);

//   const markSeen = useCallback(async () => {
//     if (!userId) return;
//     try {
//       await fetch(`${API}/lead-notifications/mark-seen`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ userId }), // just userId, nothing else
//       });
//       setNotifCount(0);
//       setNotifBreakdown({ meta: 0, website: 0 });
//       setShowNotifDropdown(false);
//     } catch (e) {
//       console.error("markSeen failed:", e.message);
//     }
//   }, [userId]);
//   // Poll every 60 seconds
//   useEffect(() => {
//     fetchNotifications();
//     const interval = setInterval(fetchNotifications, 60_000);
//     return () => clearInterval(interval);
//   }, [fetchNotifications]);

//   const notifRef = useRef(null);

//   useEffect(() => {
//     function handleClickOutside(e) {
//       if (notifRef.current && !notifRef.current.contains(e.target))
//         setShowNotifDropdown(false);
//     }
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   // ── Helpers ───────────────────────────────────────────────────────────────────
//   const matchesSearch = (lead, term) => {
//     if (!term) return true;
//     const t = term.toLowerCase();
//     return (
//       (lead.name || lead.customername || lead.customer_name || "")
//         .toLowerCase()
//         .includes(t) ||
//       (lead.phone || "").toLowerCase().includes(t) ||
//       (lead.email || "").toLowerCase().includes(t)
//     );
//   };

//   const matchesDateRange = (dateStr) => {
//     if (!dateFrom && !dateTo) return true;
//     if (!dateStr) return false;
//     const d = new Date(dateStr);
//     if (dateFrom && d < new Date(dateFrom)) return false;
//     if (dateTo && d > new Date(dateTo + "T23:59:59")) return false;
//     return true;
//   };
//   const parseTimeParts = (timeStr, fallbackHour, fallbackAmPm) => {
//     if (!timeStr)
//       return { hour: fallbackHour, minute: "00", ampm: fallbackAmPm };
//     const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
//     if (!match) return { hour: fallbackHour, minute: "00", ampm: fallbackAmPm };
//     return {
//       hour: match[1].padStart(2, "0"),
//       minute: match[2],
//       ampm: match[3].toUpperCase(),
//     };
//   };
//   const buildInitialPropertyDetails = (lead, source) => {
//     const existing = lead.propertydetails;

//     if (existing && Object.keys(existing).length > 0) {
//       // Clean any legacy comma-joined keys on read
//       const cleaned = {};
//       Object.entries(existing).forEach(([k, v]) => {
//         if (k.includes(",")) {
//           k.split(",").forEach((name) => {
//             const trimmed = name.trim();
//             if (trimmed) cleaned[trimmed] = hydrateTimes({ ...v });
//           });
//         } else {
//           cleaned[k] = hydrateTimes(v);
//         }
//       });
//       const wanted = String(
//         (source === "website" ? lead.property : lead.propertyname) || "",
//       )
//         .split(",")
//         .map((s) => s.trim())
//         .filter(Boolean);
//       return reconcilePropertyDetails(cleaned, wanted);
//     }

//     // Fallback: reconstruct from flat columns, splitting comma-joined propertyname
//     const raw = source === "website" ? lead.property : lead.propertyname;
//     if (!raw) return {};

//     const names = String(raw)
//       .split(",")
//       .map((s) => s.trim())
//       .filter(Boolean);

//     const ci = parseTimeParts(lead.checkin_time, "12", "PM");
//     const co = parseTimeParts(lead.checkout_time, "11", "AM");

//     const details = {};
//     names.forEach((name) => {
//       details[name] = {
//         destination: lead.destination || "",
//         guests: lead.guests || "",
//         bedrooms: lead.bedrooms || "",
//         inclusions: lead.inclusions || "",
//         checkin:
//           source === "website" ? lead.check_in || "" : lead.checkin || "",
//         checkout:
//           source === "website" ? lead.check_out || "" : lead.checkout || "",
//         checkin_time: lead.checkin_time || `${ci.hour}:${ci.minute} ${ci.ampm}`,
//         checkout_time:
//           lead.checkout_time || `${co.hour}:${co.minute} ${co.ampm}`,
//         checkinHour: ci.hour,
//         checkinMinute: ci.minute,
//         checkinAmPm: ci.ampm,
//         checkoutHour: co.hour,
//         checkoutMinute: co.minute,
//         checkoutAmPm: co.ampm,
//         proprice: lead.proprice || "",
//         secdep: lead.secdep || "",
//         advpay: lead.advpay || "",
//         balpay: lead.balpay || "",
//         totpay: lead.totpay || "",
//       };
//     });
//     return details;
//   };

//   // ── visibleLeads (Meta) ───────────────────────────────────────────────────────
//   const visibleLeads = (() => {
//     let base;
//     if (activeStatus === "All Leads") base = leads;
//     else if (activeStatus === "Incoming Leads")
//       base = leads.filter(
//         (l) =>
//           String(l.id).startsWith("meta_") &&
//           !processedMetaIds.has(String(l.id).replace("meta_", "")),
//       );
//     else if (activeStatus === "All Processed Leads")
//       base = leads.filter((l) => !String(l.id).startsWith("meta_"));
//     else
//       base = leads.filter(
//         (l) => !String(l.id).startsWith("meta_") && l.status === activeStatus,
//       );

//     return base
//       .filter((l) => matchesSearch(l, search))
//       .filter((l) => activeChannel === "All" || l.channel === activeChannel)
//       .filter((l) => matchesDateRange(l.created_at));
//   })();

//   // ── visibleWebsiteLeads ───────────────────────────────────────────────────────
//   const visibleWebsiteLeads = (() => {
//     let base;
//     if (websiteActiveStatus === "All Leads") {
//       const incoming = websiteLeads
//         .filter(
//           (l) => !processedWebsiteIds.has(String(l.leadid).replace("WL-", "")),
//         )
//         .map((l) => ({ ...l, _isIncoming: true }));
//       base = [...incoming, ...processedWebsiteLeads];
//     } else if (websiteActiveStatus === "Incoming Leads") {
//       base = websiteLeads
//         .filter(
//           (l) => !processedWebsiteIds.has(String(l.leadid).replace("WL-", "")),
//         )
//         .map((l) => ({ ...l, _isIncoming: true }));
//     } else if (websiteActiveStatus === "All Processed Leads") {
//       base = processedWebsiteLeads;
//     } else {
//       base = processedWebsiteLeads.filter(
//         (l) => l.status === websiteActiveStatus,
//       );
//     }

//     // Apply websiteFilter (Processed / Unprocessed) — only meaningful in All Leads
//     if (websiteActiveStatus === "All Leads") {
//       if (websiteFilter === "Unprocessed")
//         base = base.filter((l) => l._isIncoming);
//       else if (websiteFilter === "Processed")
//         base = base.filter((l) => !l._isIncoming);
//     }

//     return base.filter((l) => {
//       const nameMatch = matchesSearch(l, search);
//       const dateField = l._isIncoming ? l.createdat : l.created_at;
//       return nameMatch && matchesDateRange(dateField);
//     });
//   })();

//   const patchLead = async (id, field, value) => {
//     setUpdatingId(id);
//     setLeads((prev) =>
//       prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
//     );
//     try {
//       const res = await fetch(`${API}/leads/${id}`, {
//         method: "PATCH",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ [field]: value }),
//       });
//       if (!res.ok) throw new Error("patch failed");
//     } catch {
//       fetchLeads();
//     } finally {
//       setUpdatingId(null);
//     }
//   };
//   const handleAssigneeChange = async (id, value) => {
//     setUpdatingId(id);
//     setLeads((prev) =>
//       prev.map((l) => (l.id === id ? { ...l, assignee: value } : l)),
//     );
//     try {
//       const res = await fetch(`${API}/leads/${id}`, {
//         method: "PATCH",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ assignee: value }),
//       });
//       if (!res.ok) throw new Error("patch failed");
//     } catch {
//       fetchLeads();
//     } finally {
//       setUpdatingId(null);
//     }
//   };
//   const openLeadPanel = async (lead, mode, source = "meta") => {
//     setLeadSource(source);
//     setEditingLead(lead);
//     setIsLeadPanelOpen(true);
//     setLeadPanelMode(mode);

//     // Show the panel immediately with whatever we have
//     const ci = parseTimeParts(lead.checkin_time, "12", "PM");
//     const co = parseTimeParts(lead.checkout_time, "11", "AM");
//     setLeadFormData({
//       ...lead,
//       checkinHour: ci.hour,
//       checkinMinute: ci.minute,
//       checkinAmPm: ci.ampm,
//       checkoutHour: co.hour,
//       checkoutMinute: co.minute,
//       checkoutAmPm: co.ampm,
//       propertyname:
//         source === "website" ? lead.property || "" : lead.propertyname || "",
//       propertyDetails: buildInitialPropertyDetails(lead, source),
//     });

//     // ── Re-fetch the fresh row so propertydetails reflects the DB ──
//     try {
//       const url =
//         source === "website"
//           ? `${API}/processed-website-leads/${lead.id}`
//           : `${API}/leads/${lead.id}`;
//       const res = await fetch(url);
//       if (!res.ok) return;
//       const fresh = await res.json();

//       const fci = parseTimeParts(fresh.checkin_time, "12", "PM");
//       const fco = parseTimeParts(fresh.checkout_time, "11", "AM");

//       const baseForm = {
//         ...fresh,
//         checkinHour: fci.hour,
//         checkinMinute: fci.minute,
//         checkinAmPm: fci.ampm,
//         checkoutHour: fco.hour,
//         checkoutMinute: fco.minute,
//         checkoutAmPm: fco.ampm,
//         propertyname:
//           source === "website"
//             ? fresh.property || ""
//             : fresh.propertyname || "",
//         propertyDetails: buildInitialPropertyDetails(fresh, source),
//       };

//       if (source === "website") {
//         const [firstname = "", ...rest] = (fresh.customer_name || "")
//           .trim()
//           .split(/\s+/);
//         baseForm.firstname = fresh.firstname || firstname;
//         baseForm.lastname = fresh.lastname || rest.join(" ");
//       } else {
//         const [firstname = "", ...rest] = (fresh.name || "")
//           .trim()
//           .split(/\s+/);
//         baseForm.firstname = fresh.firstname || firstname;
//         baseForm.lastname = fresh.lastname || rest.join(" ");
//       }

//       setLeadFormData(baseForm);
//       setEditingLead(fresh);
//     } catch (err) {
//       console.warn("Could not refresh lead, using list-row data:", err.message);
//     }
//   };
//   const closeLeadPanel = () => {
//     setIsLeadPanelOpen(false);
//     setEditingLead(null);
//     setLeadFormData({});
//     setLeadSource("meta");
//   };

//   const handleSaveLead = async () => {
//     if (!editingLead) return;
//     setUpdatingId(editingLead.id);
//     try {
//       let endpoint, payload;

//       const {
//         checkinHour,
//         checkinMinute,
//         checkinAmPm,
//         checkoutHour,
//         checkoutMinute,
//         checkoutAmPm,
//         propertyDetails,
//         ...cleanFormData
//       } = leadFormData;

//       // Strip scratch fields from each per-property detail
//       const cleanedDetails = {};
//       const selectedNames = getSelectedProperties(leadFormData);
//       Object.entries(propertyDetails || {}).forEach(([name, d]) => {
//         if (selectedNames.length && !selectedNames.includes(name)) return;

//         const {
//           checkinHour,
//           checkinMinute,
//           checkinAmPm,
//           checkoutHour,
//           checkoutMinute,
//           checkoutAmPm,
//           ...rest
//         } = d;

//         // 👇 derive the combined strings from the scratch fields (fall back to
//         //    the empty-property defaults if a field is missing). This guarantees
//         //    the DB always receives a valid "HH:MM AM/PM" string even when the
//         //    user accepted the defaults and never touched the time pickers.
//         const safeCheckinHour = checkinHour || "12";
//         const safeCheckinMinute = checkinMinute || "00";
//         const safeCheckinAmPm = checkinAmPm || "PM";
//         const safeCheckoutHour = checkoutHour || "11";
//         const safeCheckoutMinute = checkoutMinute || "00";
//         const safeCheckoutAmPm = checkoutAmPm || "AM";

//         cleanedDetails[name] = {
//           ...rest,
//           checkin_time: `${safeCheckinHour}:${safeCheckinMinute} ${safeCheckinAmPm}`,
//           checkout_time: `${safeCheckoutHour}:${safeCheckoutMinute} ${safeCheckoutAmPm}`,
//         };
//       });

//       if (leadSource === "website") {
//         endpoint = `${API}/processed-website-leads/${editingLead.id}`;
//         const { propertyname, checkin, checkout, ...rest } = cleanFormData;
//         payload = {
//           ...rest,
//           property: propertyname,
//           propertydetails: cleanedDetails,
//         };
//       } else {
//         endpoint = `${API}/leads/${editingLead.id}`;
//         payload = { ...cleanFormData, propertydetails: cleanedDetails };
//       }

//       const res = await fetch(endpoint, {
//         method: "PATCH",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//       });
//       if (!res.ok) throw new Error("Update failed");

//       if (leadSource === "website") {
//         setProcessedWebsiteLeads((prev) =>
//           prev.map((l) => (l.id === editingLead.id ? { ...l, ...payload } : l)),
//         );
//       } else {
//         setLeads((prev) =>
//           prev.map((l) =>
//             l.id === editingLead.id
//               ? { ...l, ...leadFormData, propertydetails: cleanedDetails }
//               : l,
//           ),
//         );
//       }
//       toast.success("Lead updated successfully");
//       closeLeadPanel();
//     } catch (e) {
//       toast.error("Error: " + e.message);
//     } finally {
//       setUpdatingId(null);
//     }
//   };
//   const patchWebsiteLead = async (id, field, value) => {
//     setUpdatingId(id);
//     setProcessedWebsiteLeads((prev) =>
//       prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
//     );
//     try {
//       const res = await fetch(`${API}/processed-website-leads/${id}`, {
//         method: "PATCH",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ [field]: value }),
//       });
//       if (!res.ok) throw new Error("patch failed");
//     } catch {
//       fetchProcessedWebsiteLeads();
//     } finally {
//       setUpdatingId(null);
//     }
//   };
//   const handleDeleteLead = async () => {
//     if (!deleteTarget) return;
//     setDeleting(true);
//     try {
//       const url =
//         deleteTarget.type === "meta"
//           ? `${API}/leads/${deleteTarget.id}`
//           : `${API}/processed-website-leads/${deleteTarget.id}`;
//       const res = await fetch(url, { method: "DELETE" });
//       if (!res.ok) throw new Error("Delete failed " + res.status);

//       if (deleteTarget.type === "meta") {
//         setLeads((prev) => prev.filter((l) => l.id !== deleteTarget.id));
//       } else {
//         setProcessedWebsiteLeads((prev) =>
//           prev.filter((l) => l.id !== deleteTarget.id),
//         );
//       }
//       toast.success("Lead deleted successfully");
//     } catch (e) {
//       toast.error("Error: " + e.message);
//     } finally {
//       setDeleting(false);
//       setDeleteTarget(null);
//     }
//   };

//   const addRemark = (lead) => {
//     const r = prompt("Remark:", lead.remark || "");
//     if (r !== null) patchLead(lead.id, "remark", r);
//   };
//   const addWebsiteRemark = (lead) => {
//     const r = prompt("Remark:", lead.remark || "");
//     if (r !== null) patchWebsiteLead(lead.id, "remark", r);
//   };
//   const sendWhatsAppFollowUp = (lead, source) => {
//     const details = buildInitialPropertyDetails(lead, source);
//     if (Object.keys(details).length === 0) {
//       toast.warning(
//         "Add property details to this lead before sending on WhatsApp",
//       );
//       return;
//     }

//     // Open the tab NOW, synchronously, inside the click handler.
//     // No "noopener" here — we need the reference back to navigate it below.
//     const win = window.open("", "_blank");

//     (async () => {
//       try {
//         const names = Object.keys(details);
//         const brochureMap = await fetchPropertyBrochures(names, properties);

//         const customerName =
//           source === "website"
//             ? lead.customer_name
//             : lead.name || `${lead.firstname || ""} ${lead.lastname || ""}`;
//         const text = buildWhatsAppMessage(
//           customerName,
//           details,
//           properties,
//           brochureMap,
//         );
//         const url = `https://api.whatsapp.com/send/?phone=${normalizePhone(lead.phone)}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;

//         if (win && !win.closed) {
//           win.location.href = url;
//         } else {
//           toast.info(
//             "Pop-up blocked. Allow pop-ups for this site and try again.",
//           );
//         }
//       } catch (e) {
//         console.error("[WhatsApp] Failed to build/send message:", e);
//         if (win && !win.closed) win.close(); // don't leave a dead blank tab behind
//         toast.error("Couldn't prepare the WhatsApp message: " + e.message);
//       }
//     })();
//   };

//   const handleWhatsAppClick = (lead, source) => {
//     if (!isValidPhone(lead.phone)) {
//       setWaPhoneTarget({ lead, source }); // ask for the number first
//       return;
//     }
//     sendWhatsAppFollowUp(lead, source);
//   };

//   const handleWaPhoneSubmit = (phone) => {
//     const { lead, source } = waPhoneTarget;
//     // Save the number so we never ask again (optimistic update + PATCH)
//     if (source === "website") patchWebsiteLead(lead.id, "phone", phone);
//     else patchLead(lead.id, "phone", phone);
//     sendWhatsAppFollowUp({ ...lead, phone }, source);
//     setWaPhoneTarget(null);
//   };
//   const handleCreated = (lead) => setLeads((prev) => [lead, ...prev]);
//   const handleMetaLeadProcessed = (savedLead) => {
//     setProcessedMetaIds((prev) => {
//       const n = new Set(prev);
//       n.add(savedLead.meta_lead_id);
//       return n;
//     });
//     setLeads((prev) =>
//       prev.some((l) => l.id === savedLead.id) ? prev : [savedLead, ...prev],
//     );
//   };
//   const handleWebsiteLeadProcessed = (savedLead) => {
//     setProcessedWebsiteIds((prev) => {
//       const n = new Set(prev);
//       n.add(String(savedLead.original_lead_id));
//       return n;
//     });
//     setProcessedWebsiteLeads((prev) =>
//       prev.some((l) => l.id === savedLead.id) ? prev : [savedLead, ...prev],
//     );
//   };

//   const handleConvertToBooking = async () => {
//     const leadsToConvert = leads.filter((l) => selectedRows.includes(l.id));
//     if (!leadsToConvert.length) return;
//     setConverting(true);
//     setConvertResult(null);
//     const results = { bookingids: [], errors: [] };
//     for (const lead of leadsToConvert) {
//       try {
//         const nameParts = (lead.name || "").trim().split(/\s+/);
//         const payload = {
//           firstname: nameParts[0] || "Unknown",
//           lastname: nameParts.slice(1).join(" ") || "",
//           email: lead.email || "",
//           phone: lead.phone || "",
//           tripexecutive:
//             lead.assignee && lead.assignee !== "Unassigned"
//               ? lead.assignee
//               : "",
//           bookingstatus: "Pending",
//           paymentstatus: "Pending",
//           source: channelToSource(lead.channel),
//         };
//         const res = await fetch(`${API}/createBooking`, {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//         });
//         if (!res.ok) {
//           const e = await res.json().catch(() => ({}));
//           throw new Error(e.message || `HTTP ${res.status}`);
//         }
//         const data = await res.json();
//         results.bookingids.push(data.bookingid || "—");
//         const delRes = await fetch(`${API}/leads/${lead.id}`, {
//           method: "DELETE",
//         });
//         if (!delRes.ok)
//           console.warn(
//             `Booking created but lead delete failed for id ${lead.id}`,
//           );
//       } catch (e) {
//         results.errors.push({ name: lead.name, error: e.message });
//       }
//     }
//     const failedNames = new Set(results.errors.map((e) => e.name));
//     setLeads((prev) =>
//       prev.filter(
//         (l) =>
//           !leadsToConvert
//             .filter((lc) => !failedNames.has(lc.name))
//             .map((lc) => lc.id)
//             .includes(l.id),
//       ),
//     );
//     setSelectedRows([]);
//     setConverting(false);
//     setConvertResult(results);
//   };

//   const handleWebsiteConvertToBooking = async () => {
//     if (!selectedRows.length) return;
//     setConverting(true);
//     setConvertResult(null);
//     try {
//       const res = await fetch(`${API}/website-leads/convert-booking`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ ids: selectedRows }),
//       });
//       if (!res.ok) throw new Error(`HTTP ${res.status}`);
//       const data = await res.json();
//       const results = {
//         bookingids: data.bookingids || [],
//         errors: data.errors || [],
//       };
//       const failedIds = new Set((data.errors || []).map((e) => e.id));
//       setProcessedWebsiteLeads((prev) =>
//         prev.filter(
//           (l) =>
//             !selectedRows.filter((id) => !failedIds.has(id)).includes(l.id),
//         ),
//       );
//       setSelectedRows([]);
//       setConvertResult(results);
//     } catch (e) {
//       setConvertResult({
//         bookingids: [],
//         errors: [{ id: "all", error: e.message }],
//       });
//     } finally {
//       setConverting(false);
//     }
//   };

//   const handleBulkProcessWebsiteLeads = async () => {
//     const toProcess = currentLeads.filter(
//       (l) => l._isIncoming && selectedRows.includes(l.leadid),
//     );
//     if (!toProcess.length) return;
//     setBulkProcessing(true);
//     setBulkProcessResult(null);
//     const results = { processed: [], errors: [] };
//     for (const lead of toProcess) {
//       try {
//         const res = await fetch(`${API}/website-leads/process`, {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify({ leadid: lead.leadid }),
//         });
//         if (!res.ok) throw new Error(`HTTP ${res.status}`);
//         const { lead: savedLead } = await res.json();
//         results.processed.push(lead.customername || lead.leadid);
//         handleWebsiteLeadProcessed(savedLead);
//       } catch (e) {
//         results.errors.push({
//           name: lead.customername || lead.leadid,
//           error: e.message,
//         });
//       }
//     }
//     setSelectedRows([]);
//     setBulkProcessing(false);
//     setBulkProcessResult(results);
//   };

//   // ── Smart checkbox handler with toast guard ────────────────────────────────
//   // For website "All Leads" view: prevent mixing processed and unprocessed selections
//   const handleRowCheckboxChange = (id, isUnprocessed = false) => {
//     if (storeTab === "website" && websiteActiveStatus === "All Leads") {
//       const hasProcessed = selectedRows.some(
//         (sid) => !currentLeads.find((l) => l._isIncoming && l.leadid === sid),
//       );
//       const hasUnprocessed = selectedRows.some((sid) =>
//         currentLeads.find((l) => l._isIncoming && l.leadid === sid),
//       );

//       if (isUnprocessed && hasProcessed) {
//         toast.info("Cannot select processed and unprocessed leads at once");
//         return;
//       }
//       if (!isUnprocessed && hasUnprocessed) {
//         toast.info("Cannot select processed and unprocessed leads at once");
//         return;
//       }
//     }
//     setSelectedRows((prev) =>
//       prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
//     );
//   };

//   const handleSelectAll = () => {
//     if (
//       selectedRows.length === currentLeads.length &&
//       currentLeads.length > 0
//     ) {
//       setSelectedRows([]);
//       return;
//     }
//     // In All Leads website view, only select same type as first already-selected row (or all if none selected)
//     if (
//       storeTab === "website" &&
//       websiteActiveStatus === "All Leads" &&
//       selectedRows.length > 0
//     ) {
//       const hasProcessed = selectedRows.some(
//         (sid) => !currentLeads.find((l) => l._isIncoming && l.leadid === sid),
//       );
//       const hasUnprocessed = selectedRows.some((sid) =>
//         currentLeads.find((l) => l._isIncoming && l.leadid === sid),
//       );
//       if (hasUnprocessed && !hasProcessed) {
//         setSelectedRows(
//           currentLeads.filter((l) => l._isIncoming).map((l) => l.leadid),
//         );
//         return;
//       }
//       if (hasProcessed && !hasUnprocessed) {
//         setSelectedRows(
//           currentLeads.filter((l) => !l._isIncoming).map((l) => l.id),
//         );
//         return;
//       }
//     }
//     setSelectedRows(currentLeads.map((l) => l.id || l.leadid));
//   };

//   // Counts for stat cards
//   const newMetaLeadsCount = leads.filter(
//     (l) =>
//       String(l.id).startsWith("meta_") &&
//       !processedMetaIds.has(String(l.id).replace("meta_", "")),
//   ).length;
//   const facebookCount = leads.filter(
//     (l) => l.channel === "Facebook Lead",
//   ).length;
//   const instagramCount = leads.filter(
//     (l) => l.channel === "Instagram DM",
//   ).length;
//   const whatsappCount = leads.filter((l) => l.channel === "WhatsApp").length;
//   const incomingWebsiteCount = websiteLeads.filter(
//     (l) => !processedWebsiteIds.has(String(l.leadid).replace("WL-", "")),
//   ).length;

//   const activeLeadList =
//     storeTab === "meta" ? visibleLeads : visibleWebsiteLeads;
//   const totalPages = Math.ceil(activeLeadList.length / rowsPerPage);
//   const indexOfFirst = (currentPage - 1) * rowsPerPage;
//   const indexOfLast = indexOfFirst + rowsPerPage;
//   const currentLeads = activeLeadList.slice(indexOfFirst, indexOfLast);

//   const generatePageNumbers = () => {
//     const pages = [];
//     if (totalPages <= 3) {
//       for (let i = 1; i <= totalPages; i++) pages.push(i);
//     } else {
//       if (currentPage > 2) {
//         pages.push(1);
//         pages.push("...");
//       }
//       for (let i = currentPage - 1; i <= currentPage + 1; i++) {
//         if (i > 0 && i <= totalPages) pages.push(i);
//       }
//       if (currentPage < totalPages - 1) {
//         pages.push("...");
//         pages.push(totalPages);
//       }
//     }
//     return pages;
//   };

//   const isIncomingView = activeStatus === "Incoming Leads";
//   const isAllLeadsView = activeStatus === "All Leads";
//   const isWebsiteIncomingView = websiteActiveStatus === "Incoming Leads";

//   const selectedUnprocessedWebsiteCount = currentLeads.filter(
//     (l) =>
//       selectedRows.includes(l.leadid) &&
//       (l._isIncoming || isWebsiteIncomingView),
//   ).length;
//   const selectedProcessedWebsiteCount = selectedRows.filter(
//     (id) => !currentLeads.find((l) => l._isIncoming && l.leadid === id),
//   ).length;

//   const paginationProps = {
//     total: activeLeadList.length,
//     from: indexOfFirst + 1,
//     to: Math.min(indexOfLast, activeLeadList.length),
//     currentPage,
//     setCurrentPage,
//     totalPages,
//     pages: generatePageNumbers(),
//   };

//   if (error && storeTab === "meta")
//     return (
//       <div className="flex">
//         <Menubar
//           isOpen={isSidebarOpen}
//           setIsOpen={setIsSidebarOpen}
//           isMobile={isMobile}
//         />
//         <div className="flex h-64 flex-1 items-center justify-center p-6">
//           <div className="text-lg text-red-500">
//             {error}
//             <button
//               onClick={fetchLeads}
//               className="ml-3 text-sm text-indigo-500 underline"
//             >
//               Retry
//             </button>
//           </div>
//         </div>
//       </div>
//     );

//   //META PreBooking Mail
//   const openMailConfirm = (lead, source = "meta") => {
//     if (!lead.email) {
//       toast.warning("This lead has no email address on file");
//       return;
//     }

//     const details = buildInitialPropertyDetails(lead, source);
//     const names = Object.keys(details);

//     const missing = [];

//     if (names.length > 0) {
//       // Multi-property lead — check each property has required fields
//       if (names.length === 0) missing.push("Property");
//       names.forEach((name) => {
//         const pd = details[name] || {};
//         if (!pd.checkin) missing.push(`${name}: Check-in`);
//         if (!pd.checkout) missing.push(`${name}: Check-out`);
//       });
//     } else {
//       // Legacy single-property lead
//       if (!(lead.propertyname || lead.property)) missing.push("Property");
//       if (!(lead.checkin || lead.check_in)) missing.push("Check-in date");
//       if (!(lead.checkout || lead.check_out)) missing.push("Check-out date");
//     }

//     if (missing.length) {
//       toast.warning(`Add ${missing.join(", ")} before sending this email`);
//       return;
//     }

//     setMailConfirmSource(source);
//     setMailConfirmLead(lead);
//   };

//   const handleSendLeadMail = async () => {
//     const lead = mailConfirmLead;
//     if (!lead) return;

//     setSendingMailId(lead.id);
//     try {
//       const endpoint =
//         mailConfirmSource === "website"
//           ? `${API}/processed-website-leads/${lead.id}/send-mail`
//           : `${API}/leads/${lead.id}/send-mail`;
//       const res = await fetch(endpoint, { method: "POST" });
//       const data = await res.json().catch(() => ({}));
//       if (!res.ok || !data.success) {
//         throw new Error(data.message || `HTTP ${res.status}`);
//       }
//       toast.success("Email sent successfully");
//       setMailConfirmLead(null);
//     } catch (e) {
//       toast.error("Error sending email: " + e.message);
//       setMailConfirmLead(null);
//     } finally {
//       setSendingMailId(null);
//     }
//   };
//   const handleLeadPDF = () => {
//     const selectedLeads = leads.filter((l) => selectedRows.includes(l.id));
//     if (selectedLeads.length === 0) return;

//     if (selectedLeads.length > 1) {
//       toast.info(
//         "If your browser blocks some tabs, allow pop-ups for this site.",
//       );
//     }

//     selectedLeads.forEach((l, i) => {
//       const url = `/preleadpdf/${l.id}?autoprint=1`;
//       setTimeout(() => {
//         const win = window.open(url, "_blank");
//         if (!win)
//           toast.info(`Pop-up blocked for lead ${l.id}. Please allow pop-ups.`);
//       }, i * 150);
//     });
//   };
//   const handleWebsiteLeadPDF = () => {
//     const selectedLeads = processedWebsiteLeads.filter((l) =>
//       selectedRows.includes(l.id),
//     );
//     if (selectedLeads.length === 0) {
//       toast.info("Select processed leads to download a Pre-Booking PDF");
//       return;
//     }

//     if (selectedLeads.length > 1) {
//       toast.info(
//         "If your browser blocks some tabs, allow pop-ups for this site.",
//       );
//     }

//     selectedLeads.forEach((l, i) => {
//       const url = `/prewebsiteleadpdf/${l.id}?autoprint=1`;
//       setTimeout(() => {
//         const win = window.open(url, "_blank");
//         if (!win)
//           toast.info(`Pop-up blocked for lead ${l.id}. Please allow pop-ups.`);
//       }, i * 150);
//     });
//   };
//   const isLeadMailReady = (lead) => {
//     if (!lead.email) return false;

//     const details = buildInitialPropertyDetails(
//       lead,
//       lead.original_lead_id !== undefined || lead.customer_name !== undefined
//         ? "website"
//         : "meta",
//     );
//     const names = Object.keys(details);

//     // Multi-property: every selected property must have check-in and check-out
//     if (names.length > 0) {
//       return names.every((name) => {
//         const pd = details[name] || {};
//         return !!pd.checkin && !!pd.checkout;
//       });
//     }

//     // Legacy single-property: flat columns
//     return (
//       !!(lead.propertyname || lead.property) &&
//       !!(lead.checkin || lead.check_in) &&
//       !!(lead.checkout || lead.check_out)
//     );
//   };

//   return (
//     <div className="flex min-h-screen bg-[#F8F9FA]">
//       <Menubar
//         isOpen={isSidebarOpen}
//         setIsOpen={setIsSidebarOpen}
//         isMobile={isMobile}
//       />

//       <main
//         className={`min-w-0 flex-1 p-4 transition-all duration-500 sm:p-6 ${
//           isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
//         }`}
//       >
//         <div className="mx-auto flex max-w-[1600px] flex-col gap-5">
//           <Outlet />

//           {/* Header */}
//           <header className="flex flex-wrap items-center justify-between gap-3 pl-12 lg:pl-0">
//             <h1 className="text-xl font-semibold text-gray-800">
//               Inquire Leads
//             </h1>

//             <div className="flex items-center gap-2">
//               {/* Search */}
//               <div className="group mr-2 flex h-8 w-8 items-center overflow-hidden rounded-full p-2 duration-300 hover:w-[120px] hover:border hover:border-[#1D2826] hover:shadow-[2px_2px_20px_rgba(0,0,0,0.08)] sm:hover:w-[250px]">
//                 <div className="flex items-center justify-center fill-[#1D2826]">
//                   <svg
//                     xmlns="http://www.w3.org/2000/svg"
//                     viewBox="0 0 24 24"
//                     width="18"
//                     height="18"
//                   >
//                     <path d="M18.9,16.776A10.539,10.539,0,1,0,16.776,18.9l5.1,5.1L24,21.88ZM10.5,18A7.5,7.5,0,1,1,18,10.5,7.507,7.507,0,0,1,10.5,18Z" />
//                   </svg>
//                 </div>
//                 <input
//                   type="text"
//                   placeholder="Search name, phone…"
//                   value={searchInput}
//                   onChange={(e) => setSearchInput(e.target.value)}
//                   className="w-full border-none bg-transparent px-2 text-sm font-normal outline-none focus:ring-0"
//                 />
//               </div>

//               {/* Filter */}
//               <div className="relative inline-block" ref={filterRef}>
//                 <div
//                   onClick={() => setShowPopup_filter((p) => !p)}
//                   className={`group flex cursor-pointer select-none scale-[0.75] flex-col items-center gap-4 rounded-full border-2 px-2 py-2.5 transition-all duration-300 ${
//                     showPopup_filter
//                       ? "border-[#1D2826]"
//                       : "border-transparent hover:border-[#1D2826]"
//                   }`}
//                 >
//                   {[
//                     ["100%", "0%"],
//                     ["0%", "70%"],
//                     ["100%", "0%"],
//                   ].map(([a, b], i) => (
//                     <div
//                       key={i}
//                       className={`relative h-0.5 w-6 rounded-full bg-[#1D2826] ${i === 1 ? "-my-2.5" : ""}`}
//                     >
//                       <div
//                         className="absolute top-1/2 h-2 w-2 -translate-y-1/2 rounded-full border-2 border-black bg-white transition-all duration-300"
//                         style={{
//                           left: showPopup_filter ? a : b,
//                           transform: `translate(${showPopup_filter ? (i === 1 ? "0" : "-100%") : i === 1 ? "0" : "-0%"},-50%)`,
//                         }}
//                       />
//                     </div>
//                   ))}
//                 </div>

//                 {showPopup_filter && (
//                   <div className="absolute right-0 top-full z-30 mt-1 w-[260px] space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-xl">
//                     {storeTab === "meta" && (
//                       <div className="flex flex-col gap-2">
//                         <p className="text-xs font-semibold uppercase text-gray-500">
//                           Channel
//                         </p>
//                         <div className="flex flex-wrap gap-1">
//                           {[
//                             "All",
//                             "Instagram DM",
//                             "Facebook Lead",
//                             "WhatsApp",
//                           ].map((ch) => (
//                             <button
//                               key={ch}
//                               onClick={() => setActiveChannel(ch)}
//                               className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
//                                 activeChannel === ch
//                                   ? "bg-[#1D2826] text-white"
//                                   : "bg-gray-100 hover:bg-[#DAE0CE]"
//                               }`}
//                             >
//                               {ch}
//                             </button>
//                           ))}
//                         </div>
//                       </div>
//                     )}

//                     {storeTab === "website" && (
//                       <div className="flex flex-col gap-2">
//                         <p className="text-xs font-semibold uppercase text-gray-500">
//                           Lead type
//                         </p>
//                         <div className="flex flex-wrap gap-1">
//                           {["All", "Processed", "Unprocessed"].map((f) => (
//                             <button
//                               key={f}
//                               onClick={() => setWebsiteFilter(f)}
//                               className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
//                                 websiteFilter === f
//                                   ? "bg-[#1D2826] text-white"
//                                   : "bg-gray-100 hover:bg-[#DAE0CE]"
//                               }`}
//                             >
//                               {f}
//                             </button>
//                           ))}
//                         </div>
//                       </div>
//                     )}

//                     <div className="flex flex-col gap-2">
//                       <p className="text-xs font-semibold uppercase text-gray-500">
//                         Date range
//                       </p>
//                       <div className="flex justify-between gap-2">
//                         {[
//                           { label: "Today", days: 0 },
//                           { label: "Week", days: 7 },
//                           { label: "Month", days: 30 },
//                           { label: "Year", days: 365 },
//                         ].map(({ label, days }) => (
//                           <button
//                             key={label}
//                             onClick={() => {
//                               const from = new Date();
//                               from.setDate(from.getDate() - days);
//                               setDateFrom(
//                                 days === 0
//                                   ? new Date().toISOString().split("T")[0]
//                                   : from.toISOString().split("T")[0],
//                               );
//                               setDateTo(new Date().toISOString().split("T")[0]);
//                             }}
//                             className="flex-1 rounded bg-gray-100 px-2 py-1 text-xs font-medium hover:bg-[#DAE0CE] active:bg-[#DAE0CE]"
//                           >
//                             {label}
//                           </button>
//                         ))}
//                       </div>
//                       <div className="flex items-center gap-2">
//                         <input
//                           type="date"
//                           value={dateFrom}
//                           onChange={(e) => setDateFrom(e.target.value)}
//                           className="w-full rounded border px-2 py-1 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-green-600"
//                         />
//                         <span className="text-sm text-gray-500">-</span>
//                         <input
//                           type="date"
//                           value={dateTo}
//                           onChange={(e) => setDateTo(e.target.value)}
//                           className="w-full rounded border px-2 py-1 text-sm focus:border-black focus:outline-none focus:ring-1 focus:ring-green-600"
//                         />
//                       </div>
//                       {(dateFrom || dateTo) && (
//                         <button
//                           onClick={() => {
//                             setDateFrom("");
//                             setDateTo("");
//                           }}
//                           className="text-left text-xs text-gray-400 underline hover:text-gray-700"
//                         >
//                           Clear dates
//                         </button>
//                       )}
//                     </div>
//                   </div>
//                 )}
//               </div>

//               {/* Notification bell */}
//               <div className="relative" ref={notifRef}>
//                 <button
//                   onClick={() => setShowNotifDropdown((p) => !p)}
//                   title="New leads"
//                   className="relative cursor-pointer rounded-lg border-none p-2 text-xl"
//                 >
//                   <IoNotificationsOutline />
//                   {notifCount > 0 && (
//                     <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white">
//                       {notifCount > 99 ? "99+" : notifCount}
//                     </span>
//                   )}
//                 </button>

//                 {showNotifDropdown && (
//                   <div className="absolute right-0 top-full z-30 mt-1 w-56 rounded-xl border border-gray-200 bg-white p-4 text-xs shadow-xl">
//                     <p className="mb-2 text-sm font-semibold text-gray-700">
//                       {notifCount > 0
//                         ? `${notifCount} new lead${notifCount > 1 ? "s" : ""}`
//                         : "All caught up!"}
//                     </p>
//                     {notifCount > 0 && (
//                       <>
//                         <div className="flex justify-between border-b border-gray-100 py-1 text-gray-500">
//                           <span>Meta leads</span>
//                           <span className="font-bold text-gray-800">
//                             {notifBreakdown.meta}
//                           </span>
//                         </div>
//                         <div className="mb-3 flex justify-between py-1 text-gray-500">
//                           <span>Website leads</span>
//                           <span className="font-bold text-gray-800">
//                             {notifBreakdown.website}
//                           </span>
//                         </div>
//                         <button
//                           onClick={markSeen}
//                           className="w-full cursor-pointer rounded-lg bg-[#1D2826] py-1.5 text-xs font-semibold text-white"
//                         >
//                           Mark all seen
//                         </button>
//                       </>
//                     )}
//                   </div>
//                 )}
//               </div>

//               <button
//                 onClick={
//                   storeTab === "meta"
//                     ? fetchLeads
//                     : () => {
//                         fetchWebsiteLeads();
//                         fetchProcessedWebsiteLeads();
//                       }
//                 }
//                 title="Refresh"
//                 className="cursor-pointer rounded-lg border-none p-2 text-xl"
//               >
//                 <TbRefresh />
//               </button>

//               {storeTab === "meta" && (
//                 <button
//                   onClick={() => setShowCreate(true)}
//                   className={`${ui.btn} ${ui.btnPrimary} hover:scale-95`}
//                 >
//                   + New lead
//                 </button>
//               )}
//             </div>
//           </header>

//           {/* Stats */}
//           <section
//             className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${
//               storeTab === "meta" ? "lg:grid-cols-4" : "lg:grid-cols-3"
//             }`}
//           >
//             {storeTab === "meta" ? (
//               <>
//                 <StatCard
//                   label="Facebook leads"
//                   value={facebookCount}
//                   icon={<IoLogoFacebook />}
//                 />
//                 <StatCard
//                   label="Instagram leads"
//                   value={instagramCount}
//                   icon={<RiInstagramFill />}
//                 />
//                 <StatCard
//                   label="WhatsApp leads"
//                   value={whatsappCount}
//                   icon={<IoLogoWhatsapp />}
//                 />
//                 <StatCard
//                   label="Unprocessed (Meta)"
//                   value={newMetaLeadsCount}
//                   icon={<TbDeviceAnalytics />}
//                 />
//               </>
//             ) : (
//               <>
//                 <StatCard
//                   label="Incoming"
//                   value={incomingWebsiteCount}
//                   icon={<RxOpenInNewWindow />}
//                 />
//                 <StatCard
//                   label="Processed"
//                   value={processedWebsiteLeads.length}
//                   icon={<MdFileDownloadDone />}
//                 />
//                 <StatCard
//                   label="Total leads (Website)"
//                   value={incomingWebsiteCount + processedWebsiteLeads.length}
//                   icon={<TbDeviceAnalytics />}
//                 />
//               </>
//             )}
//           </section>

//           {/* Tabs + pipeline in ONE card */}
//           <section className={`${ui.card} flex flex-col gap-3 p-3`}>
//             <div className="inline-flex w-fit overflow-hidden rounded-lg border border-gray-300 text-sm font-medium">
//               {[
//                 ["meta", "Meta Leads"],
//                 ["website", "Website Leads"],
//               ].map(([k, l]) => (
//                 <button
//                   key={k}
//                   onClick={() => setStoreTab(k)}
//                   className={`px-4 py-2 transition-colors ${
//                     storeTab === k
//                       ? "bg-[#1D2826] text-white"
//                       : "bg-white text-gray-500 hover:bg-green-50"
//                   }`}
//                 >
//                   {l}
//                 </button>
//               ))}
//             </div>
//             <div className="flex overflow-x-auto pb-1 pr-2">
//               {(storeTab === "meta"
//                 ? PIPELINE_STAGES
//                 : WEBSITE_PIPELINE_STAGES
//               ).map((stage) => (
//                 <PipelineTab
//                   key={stage.name}
//                   stage={stage}
//                   isActive={
//                     (storeTab === "meta"
//                       ? activeStatus
//                       : websiteActiveStatus) === stage.status
//                   }
//                   onClick={() =>
//                     (storeTab === "meta"
//                       ? setActiveStatus
//                       : setWebsiteActiveStatus)(stage.status)
//                   }
//                 />
//               ))}
//             </div>
//           </section>

//           {/* Bulk action bar */}
//           {selectedRows.length > 0 && (
//             <div className="sticky top-2 z-20 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-2.5 shadow-md">
//               <div className="flex items-center gap-3">
//                 <span className="text-sm font-medium text-gray-600">
//                   {selectedRows.length} selected
//                 </span>
//                 <button
//                   onClick={() => setSelectedRows([])}
//                   className="cursor-pointer text-xs text-gray-400 underline hover:text-gray-700"
//                 >
//                   Clear
//                 </button>
//               </div>
//               <div className="flex flex-wrap items-center gap-2">
//                 {storeTab === "meta" && (
//                   <button
//                     onClick={handleLeadPDF}
//                     className={`${ui.btn} border border-blue-400 bg-blue-50 text-blue-600 hover:bg-blue-100`}
//                   >
//                     <FiDownload />
//                     <span>Pre-Booking PDF ({selectedRows.length})</span>
//                   </button>
//                 )}
//                 {storeTab === "website" && (
//                   <button
//                     onClick={handleWebsiteLeadPDF}
//                     className={`${ui.btn} border border-blue-400 bg-blue-50 text-blue-600 hover:bg-blue-100`}
//                   >
//                     <FiDownload />
//                     <span>Pre-Booking PDF ({selectedRows.length})</span>
//                   </button>
//                 )}
//                 {((storeTab === "meta" && !isIncomingView) ||
//                   (storeTab === "website" &&
//                     !isWebsiteIncomingView &&
//                     selectedProcessedWebsiteCount > 0)) && (
//                   <button
//                     onClick={() => setShowConvertModal(true)}
//                     className={`${ui.btn} ${ui.btnPrimary}`}
//                   >
//                     Convert to booking
//                   </button>
//                 )}
//                 {storeTab === "website" &&
//                   selectedUnprocessedWebsiteCount > 0 && (
//                     <button
//                       onClick={() => setShowBulkProcessModal(true)}
//                       className={`${ui.btn} bg-yellow-200 text-yellow-800 hover:bg-yellow-300`}
//                     >
//                       Process selected leads
//                     </button>
//                   )}
//               </div>
//             </div>
//           )}

//           {/* Tables */}
//           {storeTab === "meta" && (
//             <MetaLeadsTable
//               leads={currentLeads}
//               loading={loading}
//               isIncomingView={isIncomingView}
//               isAllLeadsView={isAllLeadsView}
//               selectedRows={selectedRows}
//               onSelectAll={handleSelectAll}
//               onRowCheck={handleRowCheckboxChange}
//               updatingId={updatingId}
//               teamMembers={teamMembers}
//               teamLoading={teamLoading}
//               sendingMailId={sendingMailId}
//               isLeadMailReady={isLeadMailReady}
//               patchLead={patchLead}
//               handleAssigneeChange={handleAssigneeChange}
//               addRemark={addRemark}
//               openLeadPanel={openLeadPanel}
//               openMailConfirm={openMailConfirm}
//               setDeleteTarget={setDeleteTarget}
//               handleWhatsAppClick={handleWhatsAppClick}
//               onMetaProcessed={handleMetaLeadProcessed}
//               pagination={paginationProps}
//             />
//           )}

//           {storeTab === "website" && (
//             <WebsiteLeadsTable
//               leads={currentLeads}
//               loading={
//                 isWebsiteIncomingView ? websiteLoading : processedWebsiteLoading
//               }
//               error={websiteError}
//               isWebsiteIncomingView={isWebsiteIncomingView}
//               selectedRows={selectedRows}
//               onSelectAll={handleSelectAll}
//               onRowCheck={handleRowCheckboxChange}
//               updatingId={updatingId}
//               teamMembers={teamMembers}
//               teamLoading={teamLoading}
//               sendingMailId={sendingMailId}
//               isLeadMailReady={isLeadMailReady}
//               patchWebsiteLead={patchWebsiteLead}
//               addWebsiteRemark={addWebsiteRemark}
//               openLeadPanel={openLeadPanel}
//               openMailConfirm={openMailConfirm}
//               setDeleteTarget={setDeleteTarget}
//               handleWhatsAppClick={handleWhatsAppClick}
//               onWebsiteProcessed={handleWebsiteLeadProcessed}
//               pagination={paginationProps}
//             />
//           )}
//         </div>
//       </main>

//       <LeadSidePanel
//         isOpen={isLeadPanelOpen}
//         mode={leadPanelMode}
//         formData={leadFormData}
//         setFormData={setLeadFormData}
//         onClose={closeLeadPanel}
//         onSave={handleSaveLead}
//         onSwitchToEdit={() => setLeadPanelMode("edit")}
//         properties={properties}
//         editingLead={editingLead}
//         teamMembers={teamMembers}
//       />

//       {waPhoneTarget && (
//         <WhatsAppPhoneModal
//           leadName={
//             waPhoneTarget.source === "website"
//               ? waPhoneTarget.lead.customer_name
//               : waPhoneTarget.lead.name
//           }
//           onSubmit={handleWaPhoneSubmit}
//           onCancel={() => setWaPhoneTarget(null)}
//         />
//       )}
//       {mailConfirmLead && (
//         <SendLeadMailModal
//           lead={mailConfirmLead}
//           onConfirm={handleSendLeadMail}
//           onCancel={() => setMailConfirmLead(null)}
//           sending={sendingMailId === mailConfirmLead.id}
//         />
//       )}
//       {followUpLead && (
//         <FollowUpModal
//           lead={followUpLead}
//           onClose={() => setFollowUpLead(null)}
//         />
//       )}
//       {showCreate && (
//         <CreateLeadModal
//           onClose={() => setShowCreate(false)}
//           onCreated={handleCreated}
//           teamMembers={teamMembers}
//         />
//       )}

//       {/* Convert confirm */}
//       {showConvertModal && (
//         <ModalShell
//           title="Convert to booking"
//           footer={
//             <>
//               <button
//                 onClick={() => setShowConvertModal(false)}
//                 disabled={converting}
//                 className={`${ui.btn} ${ui.btnGhost}`}
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={async () => {
//                   setShowConvertModal(false);
//                   storeTab === "meta"
//                     ? await handleConvertToBooking()
//                     : await handleWebsiteConvertToBooking();
//                 }}
//                 disabled={converting}
//                 className={`${ui.btn} ${ui.btnPrimary}`}
//               >
//                 {converting ? "Converting…" : "Yes, convert"}
//               </button>
//             </>
//           }
//         >
//           <p className="mb-3">
//             Convert{" "}
//             <b className="text-gray-800">
//               {selectedRows.length} lead{selectedRows.length > 1 ? "s" : ""}
//             </b>{" "}
//             to booking{selectedRows.length > 1 ? "s" : ""}?
//           </p>
//           <p className="mb-4 text-xs text-amber-600">
//             {storeTab === "website"
//               ? "These website leads will be marked as converted and inserted into bookings."
//               : "The leads will be removed from the leads table and inserted into bookings."}
//           </p>
//           <div className="max-h-40 overflow-y-auto rounded-lg bg-gray-50 px-3 py-2">
//             {storeTab === "meta"
//               ? leads
//                   .filter((l) => selectedRows.includes(l.id))
//                   .map((l) => (
//                     <div
//                       key={l.id}
//                       className="flex items-center justify-between border-b border-gray-100 py-1.5 last:border-0"
//                     >
//                       <span className="text-xs font-semibold text-gray-700">
//                         {l.name || "—"}
//                       </span>
//                       <span className="text-[11px] text-gray-400">
//                         {channelToSource(l.channel)}
//                       </span>
//                     </div>
//                   ))
//               : processedWebsiteLeads
//                   .filter((l) => selectedRows.includes(l.id))
//                   .map((l) => (
//                     <div
//                       key={l.id}
//                       className="flex items-center justify-between border-b border-gray-100 py-1.5 last:border-0"
//                     >
//                       <span className="text-xs font-semibold text-gray-700">
//                         {l.customer_name || "—"}
//                       </span>
//                       <span className="text-[11px] text-gray-400">
//                         {l.property || "Website"}
//                       </span>
//                     </div>
//                   ))}
//           </div>
//         </ModalShell>
//       )}

//       {/* Convert result */}
//       {convertResult && (
//         <ModalShell
//           title=""
//           footer={
//             <button
//               onClick={() => setConvertResult(null)}
//               className={`${ui.btn} ${ui.btnPrimary} w-full`}
//             >
//               Done
//             </button>
//           }
//         >
//           {convertResult.errors.length === 0 ? (
//             <>
//               <div className="mb-4 text-center">
//                 <div className="mb-2 text-4xl">✅</div>
//                 <div className="text-base font-bold text-gray-800">
//                   {convertResult.bookingids.length} Booking
//                   {convertResult.bookingids.length > 1 ? "s" : ""} created
//                 </div>
//                 <p className="mt-1 text-xs text-gray-400">
//                   Successfully converted to bookings
//                 </p>
//               </div>
//               <div className="rounded-lg bg-green-50 px-3 py-2">
//                 {convertResult.bookingids.map((bid, i) => (
//                   <div
//                     key={i}
//                     className="py-0.5 text-xs font-semibold text-green-700"
//                   >
//                     Booking ID: <span className="font-bold">{bid}</span>
//                   </div>
//                 ))}
//               </div>
//             </>
//           ) : (
//             <>
//               <div className="mb-4 text-center">
//                 <div className="mb-2 text-4xl">⚠️</div>
//                 <div className="text-base font-bold text-gray-800">
//                   Partial conversion
//                 </div>
//                 <p className="mt-1 text-xs text-gray-400">
//                   {convertResult.bookingids.length} succeeded ·{" "}
//                   {convertResult.errors.length} failed
//                 </p>
//               </div>
//               {convertResult.bookingids.length > 0 && (
//                 <div className="mb-3 rounded-lg bg-green-50 px-3 py-2">
//                   <p className="mb-1 text-[10px] font-bold uppercase text-green-600">
//                     Created
//                   </p>
//                   {convertResult.bookingids.map((bid, i) => (
//                     <div key={i} className="text-xs text-green-700">
//                       Booking ID: <b>{bid}</b>
//                     </div>
//                   ))}
//                 </div>
//               )}
//               <div className="rounded-lg bg-red-50 px-3 py-2">
//                 <p className="mb-1 text-[10px] font-bold uppercase text-red-500">
//                   Failed
//                 </p>
//                 {convertResult.errors.map((e, i) => (
//                   <div key={i} className="text-xs text-red-600">
//                     <b>{e.name || e.id}</b>: {e.error}
//                   </div>
//                 ))}
//               </div>
//             </>
//           )}
//         </ModalShell>
//       )}

//       {/* Bulk process confirm */}
//       {showBulkProcessModal && (
//         <ModalShell
//           title="Process leads"
//           footer={
//             <>
//               <button
//                 onClick={() => setShowBulkProcessModal(false)}
//                 disabled={bulkProcessing}
//                 className={`${ui.btn} ${ui.btnGhost}`}
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={async () => {
//                   setShowBulkProcessModal(false);
//                   await handleBulkProcessWebsiteLeads();
//                 }}
//                 disabled={bulkProcessing}
//                 className={`${ui.btn} bg-green-600 text-white hover:bg-green-700`}
//               >
//                 {bulkProcessing ? "Processing…" : "Yes, process"}
//               </button>
//             </>
//           }
//         >
//           <p className="mb-3">
//             Do you want to process{" "}
//             <b className="text-gray-800">
//               {selectedUnprocessedWebsiteCount} lead
//               {selectedUnprocessedWebsiteCount > 1 ? "s" : ""}
//             </b>
//             ?
//           </p>
//           <p className="mb-4 text-xs text-green-600">
//             The selected leads will be saved to the processed leads table and
//             become manageable in the pipeline.
//           </p>
//           <div className="max-h-40 overflow-y-auto rounded-lg bg-gray-50 px-3 py-2">
//             {currentLeads
//               .filter((l) => l._isIncoming && selectedRows.includes(l.leadid))
//               .map((l) => (
//                 <div
//                   key={l.leadid}
//                   className="flex items-center justify-between border-b border-gray-100 py-1.5 last:border-0"
//                 >
//                   <span className="text-xs font-semibold text-gray-700">
//                     {l.customername || "—"}
//                   </span>
//                   <span className="font-mono text-[11px] text-gray-400">
//                     {l.leadid}
//                   </span>
//                 </div>
//               ))}
//           </div>
//         </ModalShell>
//       )}

//       {/* Bulk process result */}
//       {bulkProcessResult && (
//         <ModalShell
//           title=""
//           footer={
//             <button
//               onClick={() => setBulkProcessResult(null)}
//               className={`${ui.btn} ${ui.btnPrimary} w-full`}
//             >
//               Done
//             </button>
//           }
//         >
//           {bulkProcessResult.errors.length === 0 ? (
//             <>
//               <div className="mb-4 text-center">
//                 <div className="mb-2 text-4xl">✅</div>
//                 <div className="text-base font-bold text-gray-800">
//                   {bulkProcessResult.processed.length} Lead
//                   {bulkProcessResult.processed.length > 1 ? "s" : ""} processed
//                 </div>
//                 <p className="mt-1 text-xs text-gray-400">
//                   Now visible in the pipeline tabs
//                 </p>
//               </div>
//               <div className="rounded-lg bg-indigo-50 px-3 py-2">
//                 {bulkProcessResult.processed.map((name, i) => (
//                   <div
//                     key={i}
//                     className="py-0.5 text-xs font-semibold text-indigo-700"
//                   >
//                     ✓ {name}
//                   </div>
//                 ))}
//               </div>
//             </>
//           ) : (
//             <>
//               <div className="mb-4 text-center">
//                 <div className="mb-2 text-4xl">⚠️</div>
//                 <div className="text-base font-bold text-gray-800">
//                   Partial processing
//                 </div>
//                 <p className="mt-1 text-xs text-gray-400">
//                   {bulkProcessResult.processed.length} succeeded ·{" "}
//                   {bulkProcessResult.errors.length} failed
//                 </p>
//               </div>
//               {bulkProcessResult.processed.length > 0 && (
//                 <div className="mb-3 rounded-lg bg-indigo-50 px-3 py-2">
//                   <p className="mb-1 text-[10px] font-bold uppercase text-indigo-600">
//                     Processed
//                   </p>
//                   {bulkProcessResult.processed.map((name, i) => (
//                     <div key={i} className="text-xs text-indigo-700">
//                       ✓ {name}
//                     </div>
//                   ))}
//                 </div>
//               )}
//               <div className="rounded-lg bg-red-50 px-3 py-2">
//                 <p className="mb-1 text-[10px] font-bold uppercase text-red-500">
//                   Failed
//                 </p>
//                 {bulkProcessResult.errors.map((e, i) => (
//                   <div key={i} className="text-xs text-red-600">
//                     <b>{e.name}</b>: {e.error}
//                   </div>
//                 ))}
//               </div>
//             </>
//           )}
//         </ModalShell>
//       )}

//       <ToastContainer
//         position="top-right"
//         autoClose={3000}
//         hideProgressBar={false}
//         newestOnTop
//         closeOnClick
//         pauseOnHover
//         draggable
//       />
//       {deleteTarget && (
//         <DeleteLeadModal
//           leadName={deleteTarget.name || "this lead"}
//           onConfirm={handleDeleteLead}
//           onCancel={() => setDeleteTarget(null)}
//           deleting={deleting}
//         />
//       )}
//     </div>
//   );
// };

// export default InquireLeads;
